import axios from 'axios';
import { ProcedureViewStateContext, ProcedureViewState } from './procedureViewState';
import { findPlanCard } from './lessonPlanCards';
import { planReadiness, TEACHER_REFLECTION_TITLE } from './lessonPlanReadiness';
import { LessonReviewEditContext, LessonReviewReadOnlyContext, useReviewSaveLabel, useReviewUnkept } from './lessonReviewReadOnly';
import { useSyncedState } from './useSyncedState';
import VerbaScholaProcedureSteps from './VerbaScholaProcedureSteps';
import { planProcedureSteps, procedurePeriodMinutes } from './planProcedureSteps';
import { preparationChecklistItems, togglePreparationItem, resetPreparationChecklist } from './preparationChecklist';
import { readTargetGuidance, writeTargetGuidance } from './lessonTargetGuidance';
import { pedagogyGuidanceOptions } from '../style/pedagogyGuidance';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    Box,
    Button,
    ButtonBase,
    Chip,
    Checkbox,
    FormControlLabel,
    Alert,
    Backdrop,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    IconButton,
    LinearProgress,
    Menu,
    Popover,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import React from 'react';
import { lessonAuthoringError, VerbaScholaApi } from '../api/VerbaScholaApi';
import type { IVerbaScholaTargetChanges } from '../api/VerbaScholaApi';
import { lessonImageUrl } from '../lessonImageUrl';
import VerbaScholaTeacherStudentPreview from './VerbaScholaTeacherStudentPreview';
import { touchDragSourceSx, useTouchDragAndDrop } from './touchDragAndDrop';
import { BUILDER_COPILOT_WIDTH, BUILDER_RAIL_WIDTH, BuilderPanelHideButton, BuilderPanelRail, builderPanelResize, useBuilderPanels } from './VerbaScholaTeacherBuilderFrame';
import { VerbaScholaPinyinField } from './VerbaScholaPinyinField';
import { studentAuthorableStepTypes, studentAuthorableStepTypeSet } from './studentAuthorableStepTypes';
import VerbaScholaNativeStepFields, { isSafePictureUrl } from './VerbaScholaNativeStepFields';
import { revealExerciseReviewText, reviewChineseQuotes } from './revealExerciseReviewText';
import { isEarlierImagePass } from './exerciseImageReviewVersion';
import VerbaScholaExerciseImageField, { hasPictureDescription } from './VerbaScholaExerciseImageField';
import { LanguageLevelEnum, LanguageLevelLabels } from '../api/ChatApi';
import {
    IVerbaScholaCourse,
    IVerbaScholaLesson,
    IVerbaScholaLessonExercise,
    IVerbaScholaLessonExerciseStep,
    IVerbaScholaLessonGeneratedMaterial,
    IVerbaScholaLessonGrammarTarget,
    IVerbaScholaLessonPlanSection,
    IVerbaScholaPracticeSession,
    IVerbaScholaSessionTargetCoverage,
    IVerbaScholaTargetCoverage,
    IVerbaScholaTargetCoverageRow,
    IVerbaScholaExercisePracticeTargets,
    IVerbaScholaExerciseChoice,
    IVerbaScholaSpeakingSentence,
    IVerbaScholaExerciseConversation,
    IVerbaScholaLessonVocabularyTarget,
    IVerbaScholaExercisePictureReview,
    IVerbaScholaExercisePictureReviewItem,
    IVerbaScholaStudentStep,
    IVerbaScholaPdfImport,
} from '../api/VerbaScholaApi';
import { VerbaScholaCard, VerbaScholaCardHeader, VerbaScholaEmptyState } from './VerbaScholaTeacherShell';
import PdfPagesSources, { AddedPdfPages, pdfImportPayload, pdfPagesBlock, pdfPagesSummary, withoutPdfPages, withPdfPages } from './pdfImport/PdfPagesSources';
import PdfImportChecks from './pdfImport/PdfImportChecks';
import { importBlocks, importStates, SOURCE_LINE_PREFIX } from './pdfImport/importChecks';
import type { PdfDestination, PdfPagesResult } from './pdfImport/VerbaScholaPdfPagesDialog';
import { samePoint } from './pdfImport/pdfTargets';
import { entryProblems, GrammarEntry, newEntryKey, pinyinWords, targetChanges, TargetLists, targetLists, unsavedEntries, VocabularyEntry } from './targetEntries';
import { GrammarEntryDialog, VocabularyEntryDialog } from './TargetEntryDialog';
import VerbaScholaConfirmDeleteButton from './VerbaScholaConfirmDeleteButton';
import { editablePinyin, instructionalPinyin } from './studentInstructionalPinyin';
import {
    aiButtonSx,
    BLUE,
    BLUE_TEXT,
    BORDER,
    ERROR_TEXT,
    GREEN,
    INTER,
    MUTED,
    NAVY,
    PAGE_BACKGROUND,
    POPPINS,
    primaryButtonSx,
    PROGRESS_TRACK,
    SLATE,
    SOFT_BLUE,
    SUCCESS_SOFT,
    SUCCESS_TEXT,
    SURFACE_MUTED,
    SURFACE_SUBTLE,
    SURFACE_TINT,
    WARNING_SOFT,
    WARNING_TEXT,
    YELLOW,
} from '../style/verbaScholaStyles';
import LessonText from './LessonText';
export type BuilderStep = 'description' | 'plan' | 'exercises';
type TargetMode = 'suggest' | 'provide' | 'hybrid';
type CharacterSet = 'simplified' | 'traditional';
type PedagogyApproach = 'task-based' | 'communicative' | 'presentation-practice-production' | 'comprehensible-input' | 'custom';
export type PlanPanel = 'context' | 'outcomes' | 'language' | 'materials' | 'preparation' | 'support' | 'procedure' | 'assessment';
type LessonPlanSectionType = 'context' | 'outcomes' | 'language' | 'preparation' | 'support' | 'procedure' | 'assessment';

export const BUILDER_STEPS: { value: BuilderStep; label: string }[] = [
    { value: 'description', label: 'Description' },
    { value: 'plan', label: 'Lesson plan' },
    { value: 'exercises', label: 'Exercises' },
];

// The PDF popup and its pdf.js and pdf-lib code load only when a teacher opens it.
const VerbaScholaPdfPagesDialog = React.lazy(() => import('./pdfImport/VerbaScholaPdfPagesDialog'));
const levelOptions = Object.values(LanguageLevelEnum).filter(level => level.startsWith('ACTFL_'));
const durationMinutesOptions = [30, 45, 50, 75];
const PRACTICE_SESSION_MINUTES = 15;
// 5, 10, whole 15-minute sessions up to 16 sessions (240 minutes), then hours up to 8: a pasted
// textbook unit practises online for about as long as its class periods, up to 480 minutes.
const exerciseMinutesOptions = [5, 10, ...Array.from({ length: 16 }, (_, index) => (index + 1) * PRACTICE_SESSION_MINUTES), 300, 360, 420, 480];
const exerciseMinutesLabel = (minutes: number) => minutes >= PRACTICE_SESSION_MINUTES && minutes % PRACTICE_SESSION_MINUTES === 0
    ? `${minutes / PRACTICE_SESSION_MINUTES} ${minutes === PRACTICE_SESSION_MINUTES ? 'session' : 'sessions'} (about ${minutes} mins total)`
    : `${minutes} mins total`;
// Flag an editable minute label far from the activity-content guide: over 50% apart,
// and at least 2 minutes so a label rounded up from the estimate is not flagged.
const hasTimingGap = (saved: number | null | undefined, content: number | null | undefined) =>
    saved != null && content != null && content > 0 && Math.abs(saved - content) > content * 0.5 && Math.abs(saved - content) >= 2;
const planPanelConfigs: { id: PlanPanel; label: string }[] = [
    { id: 'context', label: 'Class context' },
    { id: 'outcomes', label: 'Learning outcomes' },
    { id: 'language', label: 'Language targets' },
    { id: 'materials', label: 'Teaching materials' },
    { id: 'preparation', label: 'Preparation' },
    { id: 'support', label: 'Challenges & support' },
    { id: 'procedure', label: 'Classroom procedure' },
    { id: 'assessment', label: 'Assessment & follow-up' },
];
const storedPlanSectionOrder: Record<LessonPlanSectionType, number> = {
    context: 1,
    outcomes: 2,
    language: 3,
    preparation: 5,
    support: 6,
    procedure: 7,
    assessment: 8,
};
const stepClassNameDisplayLabels = {
    conversation_listen: 'Conversation listen',
    conversation_listen_speak: 'Conversation listen and speak',
    conversation_listen_without_character: 'Conversation listen without characters',
    conversation_read_speak: 'Conversation read and speak',
    conversation_read_with_blank_speak: 'Conversation fill blank and speak',
    conversation_speak_only: 'Conversation speak only',
    image: 'Image',
    image_select_speak: 'Image select and speak',
    introduce_word: 'Introduce word',
    learn_with_character: 'Learn with character',
    listen_speak: 'Listen and speak',
    listen_speak_no_grade: 'Listen and speak, no grade',
    match_game: 'Match game',
    match_word_picture: 'Match word and picture',
    quiz_question: 'Quiz question',
    quiz_question_audio: 'Quiz question with audio',
    quiz_question_speak: 'Quiz question and speak',
    quiz_word: 'Quiz word',
    summary: 'Speaking summary',
    select_next_dialog: 'Choose what comes next',
    speak_no_grade: 'Speak, no grade',
    speak_with_grade: 'Speak with grade',
    type_speak: 'Type and speak',
    type_speak_v2: 'Type and speak (earlier version)',
    write_open: 'Open writing',
    speak_open: 'Open speaking',
    type_speak_open: 'Write and speak',
} as const;
const stepClassNameOptions: string[] = [...studentAuthorableStepTypes];

function stepClassNameLabel(className: string): string {
    return stepClassNameDisplayLabels[className as keyof typeof stepClassNameDisplayLabels] ?? 'Activity type unavailable';
}

function planSectionType(section: IVerbaScholaLessonPlanSection): LessonPlanSectionType {
    const value = section.section_type;
    return value === 'context' || value === 'outcomes' || value === 'language' || value === 'preparation' || value === 'support' || value === 'assessment' ? value : 'procedure';
}

const pedagogyOptions = pedagogyGuidanceOptions;

function optionalText(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
}

function optionalNumber(value: string): number | null {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : Number(trimmed);
}

function titleCase(value: string | null | undefined): string {
    return (value ?? '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function splitBodyLines(value: string | null | undefined): string[] {
    return (value ?? '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);
}

function sectionForCard(sections: IVerbaScholaLessonPlanSection[], title: string, index: number): IVerbaScholaLessonPlanSection | null {
    return findPlanCard(sections, title, index);
}

const planContentCardSx = {
    p: 2,
    borderRadius: '14px',
    border: `1px solid ${BORDER}`,
    backgroundColor: SURFACE_TINT,
} as const;

export function statusChipSx(status: string) {
    const positive = status === 'published' || status === 'current' || status === 'complete' || status === 'ready';
    return {
        fontFamily: INTER,
        fontWeight: 700,
        fontSize: 11,
        textTransform: 'capitalize',
        color: positive ? SUCCESS_TEXT : WARNING_TEXT,
        backgroundColor: positive ? SUCCESS_SOFT : WARNING_SOFT,
    };
}

function sourceChip(source: string | null | undefined) {
    if (source === 'suggested') return { label: 'AI suggested', color: BLUE_TEXT, backgroundColor: SOFT_BLUE };
    if (source === 'added' || source === 'teacher') return { label: 'Teacher added', color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT };
    if (source === 'imported') return { label: 'Imported', color: SLATE, backgroundColor: SURFACE_MUTED };
    return { label: titleCase(source) || 'Required', color: NAVY, backgroundColor: SOFT_BLUE };
}

function SmallSourceChip({ source }: { source: string | null | undefined }) {
    const config = sourceChip(source);
    return <Chip label={config.label} size="small" sx={{ height: 22, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: config.color, backgroundColor: config.backgroundColor }} />;
}

function ManualAddition({ label, children }: { label: string; children: React.ReactNode }) {
    if (React.useContext(LessonReviewReadOnlyContext)) return null;
    return <Box component="details" sx={{ my: 1 }}>
        <Box component="summary" sx={{ cursor: 'pointer', color: BLUE, fontFamily: INTER, fontWeight: 600, py: 1 }}>{label}</Box>
        <Stack spacing={1.25}>{children}</Stack>
    </Box>;
}

/** `lockedSteps` maps a closed stage to the reason shown on its disabled tab. */
export function BuilderStageTabs({ activeStep, onChange, lockedSteps = {} }: { activeStep: BuilderStep; onChange: (value: BuilderStep) => void; lockedSteps?: Partial<Record<BuilderStep, string | null>> }) {
    const activeIndex = BUILDER_STEPS.findIndex(step => step.value === activeStep);
    const [mobileAnchor, setMobileAnchor] = React.useState<HTMLElement | null>(null);
    return (
        <Box sx={{ minWidth: 0 }}>
            <Box role="tablist" aria-label="Lesson builder stages" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                {BUILDER_STEPS.map((step, index) => <React.Fragment key={step.value}>
                    {index > 0 && <Box aria-hidden="true" sx={{ width: 24, height: 1, backgroundColor: index <= activeIndex ? BLUE : BORDER }} />}
                    {/* No tooltip wrapper: a tablist may contain only tabs. */}
                    <Button role="tab" aria-selected={index === activeIndex} aria-current={index === activeIndex ? 'step' : undefined}
                        disabled={index !== activeIndex && Boolean(lockedSteps[step.value])} title={(index !== activeIndex && lockedSteps[step.value]) || undefined}
                        onClick={() => onChange(step.value)} sx={{ minWidth: 0, p: 0.5, borderRadius: '10px', textTransform: 'none' }}>
                        <Box aria-hidden="true" sx={{ width: 24, height: 24, mr: 1, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: index <= activeIndex ? BLUE : '#fff', border: `1px solid ${index <= activeIndex ? BLUE : BORDER}` }}>
                            {index < activeIndex ? <CheckCircleRoundedIcon sx={{ color: '#fff', fontSize: 17 }} />
                                : <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: index <= activeIndex ? '#fff' : SLATE }}>{index + 1}</Typography>}
                        </Box>
                        <Typography sx={{ fontFamily: INTER, fontWeight: index === activeIndex ? 700 : 500, fontSize: 13, color: index === activeIndex ? NAVY : SLATE }}>{step.label}</Typography>
                    </Button>
                </React.Fragment>)}
            </Box>
            <Chip label={`${activeIndex + 1}/3 · ${BUILDER_STEPS[activeIndex].label}`} size="small" onClick={event => setMobileAnchor(event.currentTarget)}
                aria-label="Choose lesson builder stage" aria-expanded={Boolean(mobileAnchor)}
                sx={{ display: { xs: 'flex', md: 'none' }, minHeight: 44, fontFamily: INTER, fontWeight: 700, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />
            <Menu anchorEl={mobileAnchor} open={Boolean(mobileAnchor)} onClose={() => setMobileAnchor(null)}>
                {BUILDER_STEPS.map((step, index) => <MenuItem key={step.value} selected={index === activeIndex} disabled={index !== activeIndex && Boolean(lockedSteps[step.value])} onClick={() => { setMobileAnchor(null); onChange(step.value); }}>
                    {index + 1}. {step.label}{index !== activeIndex && lockedSteps[step.value] ? ' (finish the lesson plan first)' : ''}
                </MenuItem>)}
            </Menu>
        </Box>
    );
}

function exerciseStatusChipSx(status: string) {
    return status === 'draft'
        ? { ...statusChipSx(status), color: BLUE_TEXT, backgroundColor: SOFT_BLUE }
        : statusChipSx(status);
}

export function BuilderRefreshButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
    return <Tooltip title={label}><span><IconButton aria-label={label} disabled={disabled} onClick={onClick} sx={{ color: SLATE, width: 44, height: 44 }}><RefreshRoundedIcon /></IconButton></span></Tooltip>;
}

/** The form's values for a saved lesson. */
function lessonFormValues(lesson: IVerbaScholaLesson) {
    const guidance = readTargetGuidance(lesson.ai_context_prompt_text);
    const savedApproach = (lesson.pedagogy_approach as PedagogyApproach | null) ?? 'task-based';
    const savedPreset = pedagogyOptions.find(option => option.id === savedApproach);
    return {
        title: lesson.title,
        // Older lessons stored pasted source in guidance. Move it into the visible brief
        // on the next save so removing that field does not silently discard the source.
        brief: guidance.source && !(lesson.brief ?? '').includes(guidance.source)
            ? [lesson.brief, guidance.source].filter(Boolean).join('\n\n') : lesson.brief ?? '',
        description: lesson.description ?? '',
        durationMinutes: lesson.duration_minutes?.toString() ?? '50',
        exerciseDurationMinutes: lesson.exercise_duration_minutes?.toString() ?? '',
        proficiencyLevel: lesson.proficiency_level ?? LanguageLevelEnum.ActflIntermediateMid,
        characterSet: (lesson.character_set as CharacterSet | null) ?? 'simplified',
        vocabularyTargetMode: (lesson.vocabulary_target_mode as TargetMode | null) ?? 'hybrid',
        grammarTargetMode: (lesson.grammar_target_mode as TargetMode | null) ?? 'hybrid',
        // Keep existing edited guidance rather than replacing it with a preset on load.
        pedagogyApproach: (guidance.context && guidance.context !== savedPreset?.context ? 'custom' : savedApproach) as PedagogyApproach,
        aiContextPromptText: guidance.context || savedPreset?.context || '',
        vocabularyInput: guidance.vocabulary,
        grammarInput: guidance.grammar,
    };
}

export function DescriptionEditor({
    lesson,
    vocabularyTargets,
    grammarTargets,
    saving,
    onSaveLesson,
    onUpdateVocabulary,
    onDeleteVocabulary,
    onUpdateGrammar,
    onDeleteGrammar,
    onOpenPlan,
    onDirtyChange,
    onReviseTarget,
    renderTargetActions,
    renderLessonActions,
    onFormStateChange,
    planExists = true,
    onCreatePlan,
    onGeneratePlan,
    generatePlanDisabled = false,
    generatePlanReason,
    createPlanDisabled = false,
    extraWidth = 0,
    headerAction,
    reviewBar,
    reviewField,
    review,
    onReviewEdit,
}: {
    /** In an editable review (Your version), field changes edit Your version instead of the form. */
    onReviewEdit?: (fields: Partial<IVerbaScholaLesson>) => void;
    /**
     * The lesson version under proposal review, shown read-only in place of the form's values. The form keeps
     * its own values and stays mounted, with the target suggestion panels it renders.
     */
    review?: { lesson: IVerbaScholaLesson; vocabulary: IVerbaScholaLessonVocabularyTarget[]; grammar: IVerbaScholaLessonGrammarTarget[] } | null;
    /** Proposal review controls above the form. */
    reviewBar?: React.ReactNode;
    /** The lesson field under review, so a collapsed section holding it opens. */
    reviewField?: string;
    /** A control at the right of the title's row, such as the narrow-screen copilot opener. */
    headerAction?: React.ReactNode;
    lesson: IVerbaScholaLesson;
    vocabularyTargets: IVerbaScholaLessonVocabularyTarget[];
    grammarTargets: IVerbaScholaLessonGrammarTarget[];
    saving: boolean;
    /** Width a folded side panel gave back; the form widens by the same amount. */
    extraWidth?: number;
    planExists?: boolean;
    onCreatePlan?: () => void;
    /** With a saved plan: propose a revised plan for review, then open the plan page. */
    onGeneratePlan?: () => void;
    generatePlanDisabled?: boolean;
    generatePlanReason?: string | null;
    /** AI availability for the first plan; continuing without generating never depends on it. */
    createPlanDisabled?: boolean;
    renderLessonActions?: (payload: Partial<IVerbaScholaLesson>, canSave: boolean) => React.ReactNode;
    /** Reports the unsaved form so controls outside the editor, such as the copilot, can save it first. */
    onFormStateChange?: (payload: Partial<IVerbaScholaLesson>, canSave: boolean) => void;
    renderTargetActions?: (scope: 'vocabulary' | 'grammar', payload: Partial<IVerbaScholaLesson>, canSave: boolean) => React.ReactNode;
    onSaveLesson: (payload: Partial<IVerbaScholaLesson>) => Promise<void>;
    onAddVocabulary: (payload: Partial<IVerbaScholaLessonVocabularyTarget>) => Promise<void>;
    onUpdateVocabulary: (targetId: number, payload: Partial<IVerbaScholaLessonVocabularyTarget>) => Promise<void>;
    onDeleteVocabulary: (targetId: number) => Promise<void>;
    onAddGrammar: (payload: Partial<IVerbaScholaLessonGrammarTarget>) => Promise<void>;
    onUpdateGrammar: (targetId: number, payload: Partial<IVerbaScholaLessonGrammarTarget>) => Promise<void>;
    onDeleteGrammar: (targetId: number) => Promise<void>;
    onOpenPlan: () => void;
    onDirtyChange?: (dirty: boolean) => void;
    onReviseLesson?: () => void;
    onReviseTarget?: (target_type: 'vocabulary_target' | 'grammar_target', target_id: number) => void;
}) {
    const [initial] = React.useState(() => lessonFormValues(lesson));
    const [title, setTitle] = React.useState(initial.title);
    const [brief, setBrief] = React.useState(initial.brief);
    const [description, setDescription] = React.useState(initial.description);
    const [durationMinutes, setDurationMinutes] = React.useState(initial.durationMinutes);
    const [exerciseDurationMinutes, setExerciseDurationMinutes] = React.useState(initial.exerciseDurationMinutes);
    const [proficiencyLevel, setProficiencyLevel] = React.useState(initial.proficiencyLevel);
    const [characterSet, setCharacterSet] = React.useState<CharacterSet>(initial.characterSet);
    const [vocabularyTargetMode, setVocabularyTargetMode] = React.useState<TargetMode>(initial.vocabularyTargetMode);
    const [grammarTargetMode, setGrammarTargetMode] = React.useState<TargetMode>(initial.grammarTargetMode);
    const [pedagogyApproach, setPedagogyApproach] = React.useState<PedagogyApproach>(initial.pedagogyApproach);
    const [aiContextPromptText, setAiContextPromptText] = React.useState(initial.aiContextPromptText);
    const [vocabularyInput, setVocabularyInput] = React.useState(initial.vocabularyInput);
    const [grammarInput, setGrammarInput] = React.useState(initial.grammarInput);
    // The target lists as edited, saved with the lesson. A save (or another write this page made) gives the saved
    // targets again, and the lists start again from them.
    const [lists, setLists] = React.useState<TargetLists>(() => targetLists(vocabularyTargets, grammarTargets));
    const listsLoadedFrom = React.useRef({ vocabularyTargets, grammarTargets });
    React.useEffect(() => {
        const loaded = listsLoadedFrom.current;
        if (loaded.vocabularyTargets === vocabularyTargets && loaded.grammarTargets === grammarTargets) return;
        listsLoadedFrom.current = { vocabularyTargets, grammarTargets };
        setLists(targetLists(vocabularyTargets, grammarTargets));
    }, [vocabularyTargets, grammarTargets]);
    const readOnly = React.useContext(LessonReviewReadOnlyContext) || Boolean(review);
    // What the fields show: the version under review, or the teacher's own values.
    const shown = review ? lessonFormValues(review.lesson) : { title, brief, description, durationMinutes, exerciseDurationMinutes, proficiencyLevel,
        characterSet, vocabularyTargetMode, grammarTargetMode, pedagogyApproach, aiContextPromptText, vocabularyInput, grammarInput };
    const shownLesson = review?.lesson ?? lesson;
    const shownVocabulary = review?.vocabulary ?? vocabularyTargets;
    const shownGrammar = review?.grammar ?? grammarTargets;
    const reviseTarget = readOnly ? undefined : onReviseTarget;
    const editingReview = Boolean(review && onReviewEdit);
    const fieldReadOnly = readOnly && !editingReview;
    type FormValues = ReturnType<typeof lessonFormValues>;
    /** One field change: the form's own value, or, in an editable review, an edit to Your version's lesson. */
    function change(values: Partial<FormValues>, local: () => void) {
        if (!editingReview) { local(); return; }
        const next = { ...shown, ...values };
        const fields: Partial<IVerbaScholaLesson> = {};
        for (const key of Object.keys(values) as (keyof FormValues)[]) {
            if (key === 'title') fields.title = next.title;
            else if (key === 'brief') fields.brief = next.brief;
            else if (key === 'description') fields.description = next.description;
            else if (key === 'durationMinutes') fields.duration_minutes = optionalNumber(next.durationMinutes);
            else if (key === 'exerciseDurationMinutes') fields.exercise_duration_minutes = optionalNumber(next.exerciseDurationMinutes);
            else if (key === 'proficiencyLevel') fields.proficiency_level = next.proficiencyLevel;
            else if (key === 'characterSet') fields.character_set = next.characterSet;
            else if (key === 'vocabularyTargetMode') fields.vocabulary_target_mode = next.vocabularyTargetMode;
            else if (key === 'grammarTargetMode') fields.grammar_target_mode = next.grammarTargetMode;
            else if (key === 'pedagogyApproach') fields.pedagogy_approach = next.pedagogyApproach;
            else fields.ai_context_prompt_text = writeTargetGuidance(next.aiContextPromptText, next.vocabularyInput, next.grammarInput);
        }
        onReviewEdit?.(fields);
    }
    const [advancedOpen, setAdvancedOpen] = React.useState(readOnly && reviewField === 'pedagogy_approach');
    React.useEffect(() => { if (readOnly && reviewField === 'pedagogy_approach') setAdvancedOpen(true); }, [readOnly, reviewField]);
    const briefInputRef = React.useRef<HTMLTextAreaElement>(null);
    // "Add pages from a PDF": the PDFs chosen this visit stay in the browser, and each set of pages
    // added is listed under its field. The text added to the field is what is saved.
    const [pdfFiles, setPdfFiles] = React.useState<File[]>([]);
    const [pdfDialog, setPdfDialog] = React.useState<PdfDestination | null>(null);
    const [pdfSources, setPdfSources] = React.useState<AddedPdfPages[]>([]);
    // After pages for the brief are removed: the targets chosen from them stay in the target lists, which is said.
    const [targetsKeptNote, setTargetsKeptNote] = React.useState(false);
    const nextPdfSource = React.useRef(1);
    // pdf.js keeps a worker for each open PDF; end them when the teacher leaves this lesson.
    const pdfFilesRef = React.useRef(pdfFiles);
    pdfFilesRef.current = pdfFiles;
    React.useEffect(() => () => {
        const files = pdfFilesRef.current;
        if (files.length) void import('./pdfImport/pdfPages').then(module => module.releasePdfs(files));
    }, []);
    // Each import's record (its pages and warnings, with the teacher's decisions), saved as the pages are added.
    const [pdfImports, setPdfImports] = React.useState<IVerbaScholaPdfImport[]>([]);
    const [pdfImportsError, setPdfImportsError] = React.useState<string | null>(null);
    const [pdfPictureError, setPdfPictureError] = React.useState<string | null>(null);
    // Pages whose picture could not even be drawn in the popup (so none is kept): said, as they cannot be checked again.
    const [unmadePictures, setUnmadePictures] = React.useState<string | null>(null);
    // Pictures of added pages that could not be kept yet, to try again (they stay in this visit's memory).
    const [unkeptPictures, setUnkeptPictures] = React.useState<{ record: IVerbaScholaPdfImport; pageNumber: number; picture: Blob }[]>([]);
    React.useEffect(() => {
        let current = true;
        VerbaScholaApi.listLessonPdfImports(lesson.id)
            .then(response => { if (current) setPdfImports(Array.isArray(response.data) ? response.data : []); })
            .catch(() => { if (current) setPdfImportsError('The warnings of pages added from a PDF could not be loaded. Reload the page to see them.'); });
        return () => { current = false; };
    }, [lesson.id]);
    const aiContextInputRef = React.useRef<HTMLTextAreaElement>(null);
    const pdfImportStates = importStates(pdfImports, { brief: shown.brief, ai_context_prompt_text: shown.aiContextPromptText });

    // Imports whose pages the saved lesson still holds: forgetting them waits for the removal to be saved.
    const savedTexts = [lesson.brief ?? '', readTargetGuidance(lesson.ai_context_prompt_text).context];
    const savedImportIds = new Set(pdfImports.filter(record => savedTexts.some(text => importBlocks(text, record.id).length)).map(record => record.id));
    // An import's record is saved as its pages are added, before the lesson is. Those added during this visit that the
    // saved lesson does not hold when the teacher leaves (discarding the edits, reloading, closing the tab) are forgotten,
    // so they are not left over as pages "not found in the text". The server refuses to forget one the saved lesson holds.
    // While a request is in progress (``saving``: a save, say) none is forgotten, as that save may yet hold it: a
    // forgetting that reached the server first would leave the saved pages without their record. Any left over then is
    // shown as not found, to forget.
    const visitImports = React.useRef(new Set<number>());
    const savedTextsRef = React.useRef(savedTexts);
    savedTextsRef.current = savedTexts;
    const savingRef = React.useRef(saving);
    savingRef.current = saving;
    React.useEffect(() => {
        const unsaved = () => (savingRef.current ? []
            : [...visitImports.current].filter(id => !savedTextsRef.current.some(text => importBlocks(text, id).length)));
        const onPageHide = () => unsaved().forEach(id => VerbaScholaApi.forgetPdfImportOnLeave(id));
        window.addEventListener('pagehide', onPageHide);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
            unsaved().forEach(id => void VerbaScholaApi.forgetPdfImport(id).catch(() => undefined));
        };
    }, []);
    const pdfChecks = (field: IVerbaScholaPdfImport['lesson_field'], text: string, input: React.RefObject<HTMLTextAreaElement>) => (
        <PdfImportChecks imports={pdfImportStates.filter(item => (item.field ?? item.record.lesson_field) === field)} savedImportIds={savedImportIds}
            loadError={[text.includes(SOURCE_LINE_PREFIX) ? pdfImportsError : null, pdfPictureError, unmadePictures].filter(Boolean).join(' ') || null}
            onDecide={async (record, warning, decision) => {
                const response = await VerbaScholaApi.decidePdfImportWarning(record.id, warning.id, decision);
                setPdfImports(current => current.map(item => (item.id === record.id ? response.data : item)));
            }}
            onForget={async record => {
                await VerbaScholaApi.forgetPdfImport(record.id);
                setPdfImports(current => current.filter(item => item.id !== record.id));
            }}
            onCheckAgain={async (record, pageNumber, text) => {
                const response = await VerbaScholaApi.checkPdfImportPageAgain(record.id, pageNumber, text);
                setPdfImports(current => current.map(item => (item.id === record.id ? response.data : item)));
            }}
            onShow={(start, length) => selectInField(input.current, start, length)}
            onRetryPictures={unkeptPictures.length ? () => void keepPagePictures(unkeptPictures) : undefined} />
    );
    // The pages' pictures (those the AI check was made on) are kept after the pages are added, one at a time, to look at
    // when settling a warning later; a page whose picture could not be kept is said to be so.
    async function keepPagePictures(pages: { record: IVerbaScholaPdfImport; pageNumber: number; picture: Blob }[]) {
        const failed: typeof pages = [];
        for (const page of pages) {
            try {
                await VerbaScholaApi.savePdfImportPicture(page.record.id, page.pageNumber, page.picture);
                setPdfImports(current => current.map(item => (item.id !== page.record.id ? item
                    : { ...item, pages_json: item.pages_json.map(row => (row.page_number === page.pageNumber ? { ...row, picture: true } : row)) })));
            } catch {
                failed.push(page);
            }
        }
        setUnkeptPictures(current => [...current.filter(item => !pages.includes(item)), ...failed]);
        setPdfPictureError(failed.length ? `The ${failed.length === 1 ? 'picture of 1 page' : `pictures of ${failed.length} pages`} added could not be kept, `
            + `so ${failed.length === 1 ? 'it' : 'they'} cannot be opened or checked again here later. Try again while this page is open.` : null);
    }
    async function savePagePictures(record: IVerbaScholaPdfImport, result: PdfPagesResult) {
        if (result.picturesMissing.length) {
            setUnmadePictures(`The ${result.picturesMissing.length === 1 ? 'picture of PDF page' : 'pictures of PDF pages'} ${result.picturesMissing.join(', ')} `
                + `of ${result.fileName} could not be made, so ${result.picturesMissing.length === 1 ? 'it' : 'they'} cannot be opened or checked again here. `
                + 'Add those pages again to keep their pictures.');
        }
        await keepPagePictures(result.pages.flatMap(page => (page.picture ? [{ record, pageNumber: page.pageNumber, picture: page.picture }] : [])));
    }
    // Saves the import's record first, so the block can name it; if that fails, nothing is added (the popup says so).
    async function addPdfPages(destination: PdfDestination, result: PdfPagesResult) {
        const record = (await VerbaScholaApi.createLessonPdfImport(lesson.id, pdfImportPayload(result, destination))).data;
        visitImports.current.add(record.id);
        setPdfImports(current => [...current, record]);
        const block = pdfPagesBlock(result, record.id);
        const withTargets = Boolean(result.targets && (result.targets.vocabulary.length || result.targets.grammar.length));
        setPdfSources(current => [...current, { id: nextPdfSource.current++, destination, block, summary: pdfPagesSummary(result), withTargets }]);
        setTargetsKeptNote(false);
        setPdfDialog(null);
        void savePagePictures(record, result);
        if (destination === 'brief') {
            // Add chosen targets as reviewed. Only a list receiving new entries switches to "I'll provide".
            // Empty, skipped and duplicate-only results preserve that list's mode.
            const value = withPdfPages(shown.brief, block);
            const chosen = result.targets ?? { vocabulary: [], grammar: [] };
            setLists(current => {
                const vocabulary = [...current.vocabulary];
                for (const word of chosen.vocabulary) {
                    if (!vocabulary.some(entry => entry.hanzi.trim() === word.hanzi)) {
                        vocabulary.push({ key: newEntryKey(), hanzi: word.hanzi, pinyin: word.pinyin, meaning: word.meaning, source: 'required' });
                    }
                }
                const grammar = [...current.grammar];
                for (const point of chosen.grammar) {
                    if (!grammar.some(entry => samePoint(entry.pattern, point.pattern))) {
                        grammar.push({ key: newEntryKey(), pattern: point.pattern, meaning: point.explanation, example: point.example, source: 'required' });
                    }
                }
                return { ...current, vocabulary, grammar };
            });
            const wordMode = chosen.vocabulary.some(word => !lists.vocabulary.some(entry => entry.hanzi.trim() === word.hanzi)) ? 'provide' : shown.vocabularyTargetMode;
            const pointMode = chosen.grammar.some(point => !lists.grammar.some(entry => samePoint(entry.pattern, point.pattern))) ? 'provide' : shown.grammarTargetMode;
            change({ brief: value, vocabularyTargetMode: wordMode, grammarTargetMode: pointMode }, () => {
                setBrief(value);
                setVocabularyTargetMode(wordMode);
                setGrammarTargetMode(pointMode);
            });
            return;
        }
        // As when a teacher appends a textbook introduction by hand, the combined guidance is Custom.
        const value = withPdfPages(shown.aiContextPromptText, block);
        change({ aiContextPromptText: value, pedagogyApproach: 'custom' }, () => { setAiContextPromptText(value); setPedagogyApproach('custom'); });
    }
    function removePdfPages(source: AddedPdfPages) {
        const fieldText = source.destination === 'brief' ? shown.brief : shown.aiContextPromptText;
        const value = withoutPdfPages(fieldText, source.block);
        setPdfSources(current => current.filter(item => item.id !== source.id));
        if (value === null) return;
        if (source.withTargets) setTargetsKeptNote(true);
        if (source.destination === 'brief') change({ brief: value }, () => setBrief(value));
        else change({ aiContextPromptText: value }, () => setAiContextPromptText(value));
    }

    function addBriefSuggestion(label: string) {
        setBrief(current => `${current.trim()} ${label}.`.trim());
        // Flash the brief so the teacher sees where the suggestion went. Reduced motion
        // jumps instead of scrolling and holds the highlight without fading it.
        const field = briefInputRef.current?.closest<HTMLElement>('.MuiOutlinedInput-root');
        if (!field) return;
        const reducedMotion = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
        const flash = () => field.animate?.([
            { boxShadow: `0 0 0 3px ${BLUE}`, backgroundColor: SOFT_BLUE },
            { boxShadow: '0 0 0 3px transparent', backgroundColor: 'transparent' },
        ], { duration: 1500, easing: reducedMotion ? 'step-end' : 'ease-out' });
        const rect = field.getBoundingClientRect();
        if (rect.top >= 0 && rect.bottom <= window.innerHeight) { flash(); return; }
        field.scrollIntoView?.({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
        if (reducedMotion || typeof IntersectionObserver === 'undefined') { flash(); return; }
        // Flash once the smooth scroll has actually brought the field (mostly) into view,
        // with a fallback in case the scroll is interrupted.
        let flashed = false;
        const flashOnce = () => { if (flashed) return; flashed = true; observer.disconnect(); window.clearTimeout(fallback); flash(); };
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) flashOnce();
        }, { threshold: [0.5] });
        const fallback = window.setTimeout(flashOnce, 1500);
        observer.observe(field);
    }
    // A suggestion counts as added only as its own sentence, so "Do not include pair
    // work." leaves "Include pair work" available.
    const briefSentences = new Set(brief.split(/[.。!！?？;；\n]+/).map(sentence => sentence.trim().toLowerCase()));

    // The workspace remounts only after an explicit refresh. Background job reads
    // and successful target saves must never replace these local inputs.
    const [editingTargets, setEditingTargets] = React.useState<Record<string, boolean>>({});
    const onTargetEditingChange = React.useCallback((key: string, editing: boolean) => {
        setEditingTargets(current => ({ ...current, [key]: editing }));
    }, []);

    // The target lists' changes, saved with the lesson (not while a proposal is reviewed: its rows are shown instead).
    const changes = review ? undefined : targetChanges(lists, vocabularyTargets, grammarTargets);
    const problems = review ? new Map<string, string>() : entryProblems(lists);
    const savePayload: Partial<IVerbaScholaLesson> & { target_changes?: IVerbaScholaTargetChanges } = {
        title: title.trim(),
        brief: optionalText(brief),
        description: optionalText(description),
        duration_minutes: optionalNumber(durationMinutes),
        exercise_duration_minutes: optionalNumber(exerciseDurationMinutes),
        proficiency_level: proficiencyLevel,
        character_set: characterSet,
        vocabulary_target_mode: vocabularyTargetMode,
        grammar_target_mode: grammarTargetMode,
        pedagogy_approach: pedagogyApproach,
        ai_context_prompt_text: writeTargetGuidance(aiContextPromptText, vocabularyInput, grammarInput),
        ...(changes ? { target_changes: changes } : {}),
    };
    const baseline = {
        title: lesson.title.trim(), brief: optionalText(lesson.brief ?? ''), description: optionalText(lesson.description ?? ''),
        duration_minutes: lesson.duration_minutes ?? null,
        exercise_duration_minutes: lesson.exercise_duration_minutes ?? null,
        proficiency_level: lesson.proficiency_level ?? null,
        character_set: lesson.character_set ?? null, vocabulary_target_mode: lesson.vocabulary_target_mode ?? null,
        grammar_target_mode: lesson.grammar_target_mode ?? null, pedagogy_approach: lesson.pedagogy_approach ?? null,
        ai_context_prompt_text: optionalText(lesson.ai_context_prompt_text ?? ''),
    };
    const dirty = JSON.stringify(savePayload) !== JSON.stringify(baseline)
        || (!review && Object.values(editingTargets).some(Boolean));
    React.useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
    const formBlocked = readOnly || saving || title.trim().length === 0 || Object.values(editingTargets).some(Boolean) || problems.size > 0;
    const canSave = title.trim().length > 0 && !Object.values(editingTargets).some(Boolean) && problems.size === 0;
    // The lists' targets, each edited in its own popup (null: a new one); a saved target removed is deleted when the
    // lesson is saved, and those not saved yet are marked so.
    const [entryEditor, setEntryEditor] = React.useState<{ scope: 'vocabulary' | 'grammar'; key: string | null } | null>(null);
    // Changes in an open editor are the page's unsaved edits too, as an open target row's are.
    const onEntryEditorUnsaved = React.useCallback((unsaved: boolean) => onTargetEditingChange('target-editor', unsaved), [onTargetEditingChange]);
    const unsaved = review ? new Set<string>() : unsavedEntries(lists, vocabularyTargets, grammarTargets);
    const removeEntry = (scope: 'vocabulary' | 'grammar', entry: VocabularyEntry | GrammarEntry) => setLists(current => (scope === 'vocabulary'
        ? { ...current, vocabulary: current.vocabulary.filter(item => item.key !== entry.key),
            removedVocabulary: entry.id === undefined ? current.removedVocabulary : [...current.removedVocabulary, entry.id] }
        : { ...current, grammar: current.grammar.filter(item => item.key !== entry.key),
            removedGrammar: entry.id === undefined ? current.removedGrammar : [...current.removedGrammar, entry.id] }));
    const briefHasPdfPages = shown.brief.includes(SOURCE_LINE_PREFIX);
    const savePayloadKey = JSON.stringify(savePayload);
    const savePayloadRef = React.useRef(savePayload);
    savePayloadRef.current = savePayload;
    React.useEffect(() => { onFormStateChange?.(savePayloadRef.current, canSave); }, [savePayloadKey, canSave, onFormStateChange]);

    return (
        <Box data-testid="description-editor-content" sx={{ maxWidth: 980 + extraWidth, mx: 'auto', ...builderPanelResize('max-width') }}>
            {reviewBar}
            {/* The centred title keeps a control (the narrow-screen copilot opener) at the right of its row. */}
            <Box sx={{ mb: 4, display: 'grid', alignItems: 'center', columnGap: 1, gridTemplateColumns: headerAction ? 'minmax(64px, 1fr) minmax(0, auto) minmax(64px, 1fr)' : '1fr' }}>
                <Typography component="h1" sx={{ gridColumn: headerAction ? 2 : 1, textAlign: 'center', fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 28, md: 36 }, color: NAVY, letterSpacing: 0 }}>
                    What would you like to teach?
                </Typography>
                {headerAction && <Box sx={{ gridColumn: 3, justifySelf: 'end' }}>{headerAction}</Box>}
            </Box>

            <VerbaScholaCard>
                <Stack spacing={2.25} sx={{ p: { xs: 2.5, md: 3.5 } }}>
                    <TextField data-review-field="lesson:title" label="Lesson title" value={shown.title} onChange={event => { const value = event.target.value; change({ title: value }, () => setTitle(value)); }} InputProps={{ readOnly: fieldReadOnly }} fullWidth />
                    <TextField data-review-field="lesson:description" InputProps={{ readOnly: fieldReadOnly }} label="Teacher-facing description" value={shown.description} onChange={event => { const value = event.target.value; change({ description: value }, () => setDescription(value)); }} helperText="Optional short summary for teachers." fullWidth multiline minRows={3} />
                    <TextField data-review-field="lesson:brief" InputProps={{ readOnly: fieldReadOnly, inputComponent: 'textarea' }} label="Lesson brief" inputRef={briefInputRef} value={shown.brief} onChange={event => { const value = event.target.value; change({ brief: value }, () => setBrief(value)); }} placeholder="Paste a textbook unit or describe the lesson you want..." helperText="Required to create a lesson plan. Paste a whole textbook unit, or add its pages from a PDF, to plan all of it as one lesson, divided into class periods of the class length below, with about as many minutes of online exercises (applying the plan sets the Exercise target); or describe the lesson you want. The AI reads the source's time guidance for the number of periods. Markdown image links do not import images." fullWidth multiline rows={6}
                        // Six lines high whatever it holds (pages added from a PDF run long): it scrolls, and its handle sets the
                        // height. A plain textarea (inputComponent), not MUI's self-sizing one, which would undo the handle's height
                        // as the teacher types.
                        sx={{ '& textarea': { resize: 'vertical', overflowY: 'auto', minHeight: '3em' } }} />
                    {!readOnly && <Box sx={{ mt: '8px !important' }}>
                        <PdfPagesSources sources={pdfSources.filter(source => source.destination === 'brief')} fieldText={shown.brief}
                            note={targetsKeptNote ? 'The targets chosen from the pages removed stay in the target lists; remove any you no longer want there.' : null}
                            onAdd={() => setPdfDialog('brief')} onRemove={removePdfPages} />
                        {pdfChecks('brief', shown.brief, briefInputRef)}
                    </Box>}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap" alignItems={{ md: 'flex-start' }}>
                        <Stack data-review-field="lesson:proficiency_level" spacing={0.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>Proficiency level</Typography>
                            <Select readOnly={fieldReadOnly} value={shown.proficiencyLevel} onChange={(event: SelectChangeEvent<string>) => { const value = event.target.value; change({ proficiencyLevel: value }, () => setProficiencyLevel(value)); }} inputProps={{ 'aria-label': 'Proficiency level' }} sx={{ minWidth: 230, backgroundColor: '#fff', fontFamily: INTER }}>
                                {levelOptions.map(level => <MenuItem key={level} value={level}>{LanguageLevelLabels[level]}</MenuItem>)}
                            </Select>
                        </Stack>
                        <Stack data-review-field="lesson:duration_minutes" spacing={0.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>Class length</Typography>
                            <Select readOnly={fieldReadOnly} value={shown.durationMinutes} onChange={(event: SelectChangeEvent<string>) => { const value = event.target.value; change({ durationMinutes: value }, () => setDurationMinutes(value)); }} inputProps={{ 'aria-label': 'Class length' }} sx={{ minWidth: 145, backgroundColor: '#fff', fontFamily: INTER }}>
                                {Array.from(new Set([...durationMinutesOptions, ...(shownLesson.duration_minutes ? [shownLesson.duration_minutes] : [])])).sort((a, b) => a - b).map(minutes => <MenuItem key={minutes} value={String(minutes)}>{minutes} minutes</MenuItem>)}
                            </Select>
                        </Stack>
                        <Stack data-review-field="lesson:character_set" spacing={0.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>Characters</Typography>
                            <Select readOnly={fieldReadOnly} value={shown.characterSet} onChange={(event: SelectChangeEvent<string>) => { const value = event.target.value as CharacterSet; change({ characterSet: value }, () => setCharacterSet(value)); }} inputProps={{ 'aria-label': 'Characters' }} sx={{ minWidth: 145, backgroundColor: '#fff', fontFamily: INTER }}>
                                <MenuItem value="simplified">Simplified</MenuItem>
                                <MenuItem value="traditional">Traditional</MenuItem>
                            </Select>
                        </Stack>
                        {/* On a row of its own, the select as wide as its longest choice with its long help text beside it (below
                            it on a phone), so the settings take two rows. */}
                        <Stack data-review-field="lesson:exercise_duration_minutes" spacing={0.5} sx={{ width: '100%' }}>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>Exercise target (total for this lesson)</Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 2 }} alignItems={{ md: 'center' }}>
                                <Select readOnly={fieldReadOnly} displayEmpty value={shown.exerciseDurationMinutes} onChange={(event: SelectChangeEvent<string>) => { const value = event.target.value; change({ exerciseDurationMinutes: value }, () => setExerciseDurationMinutes(value)); }} inputProps={{ 'aria-label': 'Exercise target' }} sx={{ width: { xs: '100%', sm: 310 }, flexShrink: 0, backgroundColor: '#fff', fontFamily: INTER }}>
                                    <MenuItem value="">Not set</MenuItem>
                                    {Array.from(new Set([...exerciseMinutesOptions, ...(shownLesson.exercise_duration_minutes ? [shownLesson.exercise_duration_minutes] : [])])).sort((a, b) => a - b).map(minutes => <MenuItem key={minutes} value={String(minutes)}>{exerciseMinutesLabel(minutes)}</MenuItem>)}
                                </Select>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, maxWidth: { md: 560 } }}>
                                    Sessions aim for {PRACTICE_SESSION_MINUTES} minutes. Applying a plan for a pasted textbook unit sets this to about as long as its class periods together (up to 8 hours); you can change it afterwards. More sessions may be added to cover all targets.
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                </Stack>
            </VerbaScholaCard>

            {!readOnly && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ justifyContent: 'center', mt: 2.5 }}>
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, alignSelf: 'center', mr: 0.5 }}>Try adding:</Typography>
                {['More speaking practice', 'Keep language ACTFL-aligned', 'Include pair work', 'Add an exit ticket'].map(label => {
                    const added = briefSentences.has(label.toLowerCase());
                    return added
                        ? <Chip key={label} label={label} icon={<CheckRoundedIcon />} aria-label={`${label} (added to brief)`} sx={{ minHeight: 44, fontFamily: INTER, color: SLATE, backgroundColor: SURFACE_MUTED, '& .MuiChip-icon': { color: SLATE } }} />
                        : <Chip key={label} label={label} onClick={() => addBriefSuggestion(label)} sx={{ minHeight: 44, fontFamily: INTER, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />;
                })}
            </Stack>}

            <Box data-review-field="lesson:targets" sx={{ mt: 4 }}>
                <VerbaScholaCard>
                    <Stack spacing={2.5} sx={{ p: { xs: 2.5, md: 3.5 } }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                            <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}>
                                <TipsAndUpdatesOutlinedIcon sx={{ color: BLUE }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>Language targets</Typography>
                                    <Chip label="Optional" size="small" sx={{ fontFamily: INTER, color: SLATE, backgroundColor: SURFACE_MUTED }} />
                                </Stack>
                                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13, mt: 0.5 }}>
                                    Generate lesson plan, or Suggest updates from my changes, reads these fields and the lesson brief. With I'll provide, the lesson's targets are the ones you list: the AI adds none.
                                    With Mine + AI, your listed targets are kept and the AI adds more, following the box; with AI suggest, the AI chooses them. Listed targets are saved with the lesson.
                                </Typography>
                            </Box>
                        </Stack>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                            <LanguageTargetCard title="Vocabulary" icon={TranslateRoundedIcon} mode={shown.vocabularyTargetMode} pdfPages={briefHasPdfPages} onModeChange={fieldReadOnly ? () => undefined : value => change({ vocabularyTargetMode: value }, () => setVocabularyTargetMode(value))} count={shownVocabulary.length}>
                                <Stack spacing={1.25}>
                                    {shown.vocabularyTargetMode !== 'provide' && <TextField InputProps={{ readOnly: fieldReadOnly }} label="Vocabulary targets and instructions" multiline minRows={4} fullWidth value={shown.vocabularyInput} onChange={event => { const value = event.target.value; change({ vocabularyInput: value }, () => setVocabularyInput(value)); }} placeholder="Paste targets in any format, or describe what to focus on..." helperText="Chinese, English meanings, pinyin, bullets, tables, and teaching instructions are welcome. Included when you create the lesson plan or suggest updates. Check translations and parsing warnings in the proposal before saving it." />}
                                    {review
                                        ? shownVocabulary.map(target => <VocabularyTargetRow key={target.id} onRevise={reviseTarget ? () => reviseTarget('vocabulary_target', target.id) : undefined} target={target} saving={saving} onSave={onUpdateVocabulary} onDelete={onDeleteVocabulary} onEditingChange={onTargetEditingChange} />)
                                        : shown.vocabularyTargetMode !== 'suggest' && <TargetList scope="vocabulary" entries={lists.vocabulary} problems={problems} unsaved={unsaved}
                                            onEdit={key => setEntryEditor({ scope: 'vocabulary', key })} onRemove={entry => removeEntry('vocabulary', entry)}
                                            onRevise={reviseTarget && !changes ? id => reviseTarget('vocabulary_target', id) : undefined} />}
                                    {/* Kept mounted in every mode: it may be following a request for more targets. */}
                                    <Box sx={{ display: shown.vocabularyTargetMode === 'provide' && !shown.vocabularyInput.trim() ? 'none' : 'block' }}>{renderTargetActions?.('vocabulary', savePayload, canSave)}</Box>
                                    {shown.vocabularyTargetMode === 'provide' && !review && shown.vocabularyInput.trim() && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>
                                        The vocabulary text typed for Mine + AI and AI suggest is kept for them (choose Mine + AI to see it); I'll provide does not read it. To add the targets it lists to the list, use Extract vocabulary targets: they are added for you to check.
                                    </Typography>}
                                </Stack>
                            </LanguageTargetCard>

                            <LanguageTargetCard title="Grammar" icon={AccountTreeOutlinedIcon} mode={shown.grammarTargetMode} pdfPages={briefHasPdfPages} onModeChange={fieldReadOnly ? () => undefined : value => change({ grammarTargetMode: value }, () => setGrammarTargetMode(value))} count={shownGrammar.length}>
                                <Stack spacing={1.25}>
                                    {shown.grammarTargetMode !== 'provide' && <TextField InputProps={{ readOnly: fieldReadOnly }} label="Grammar targets and instructions" multiline minRows={4} fullWidth value={shown.grammarInput} onChange={event => { const value = event.target.value; change({ grammarInput: value }, () => setGrammarInput(value)); }} placeholder="Paste targets in any format, or describe what to focus on..." helperText="Describe patterns or goals in Chinese or English, with examples if useful. Any list format is welcome. Included when you create the lesson plan or suggest updates. Check interpretations and parsing warnings in the proposal before saving it." />}
                                    {review
                                        ? shownGrammar.map(target => <GrammarTargetRow key={target.id} onRevise={reviseTarget ? () => reviseTarget('grammar_target', target.id) : undefined} target={target} saving={saving} onSave={onUpdateGrammar} onDelete={onDeleteGrammar} onEditingChange={onTargetEditingChange} />)
                                        : shown.grammarTargetMode !== 'suggest' && <TargetList scope="grammar" entries={lists.grammar} problems={problems} unsaved={unsaved}
                                            onEdit={key => setEntryEditor({ scope: 'grammar', key })} onRemove={entry => removeEntry('grammar', entry)}
                                            onRevise={reviseTarget && !changes ? id => reviseTarget('grammar_target', id) : undefined} />}
                                    <Box sx={{ display: shown.grammarTargetMode === 'provide' && !shown.grammarInput.trim() ? 'none' : 'block' }}>{renderTargetActions?.('grammar', savePayload, canSave)}</Box>
                                    {shown.grammarTargetMode === 'provide' && !review && shown.grammarInput.trim() && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>
                                        The grammar text typed for Mine + AI and AI suggest is kept for them (choose Mine + AI to see it); I'll provide does not read it. To add the targets it lists to the list, use Extract grammar targets: they are added for you to check.
                                    </Typography>}
                                </Stack>
                            </LanguageTargetCard>
                        </Box>
                    </Stack>
                </VerbaScholaCard>
            </Box>

            <Box data-review-field="lesson:pedagogy_approach" sx={{ mt: 2.5 }}>
                <VerbaScholaCard>
                    <Button fullWidth onClick={() => setAdvancedOpen(open => !open)} aria-expanded={advancedOpen} sx={{ justifyContent: 'flex-start', p: { xs: 2.5, md: 3 }, textAlign: 'left', textTransform: 'none', borderRadius: 0, color: NAVY }}>
                        <Box sx={{ width: 42, height: 42, mr: 1.5, flexShrink: 0, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: SURFACE_MUTED }}>
                            <TuneRoundedIcon sx={{ color: BLUE, fontSize: 21 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Advanced AI guidance</Typography>
                                <Chip label="Optional" size="small" sx={{ fontFamily: INTER, color: SLATE, backgroundColor: SURFACE_MUTED }} />
                            </Stack>
                            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12.5, lineHeight: 1.45, mt: 0.35 }}>Save this guidance before requesting generation.</Typography>
                        </Box>
                        <Typography sx={{ ml: 1.5, fontFamily: INTER, fontWeight: 700, fontSize: 12, color: BLUE }}>{advancedOpen ? 'Hide' : 'Edit'}</Typography>
                    </Button>

                    <Collapse in={advancedOpen}>
                        <Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 } }}>
                            <Divider sx={{ mb: 2.5 }} />
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pedagogy approach</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
                                <Select readOnly={fieldReadOnly} size="small" value={shown.pedagogyApproach}
                                    onChange={(event: SelectChangeEvent<string>) => {
                                        const approach = event.target.value as PedagogyApproach;
                                        const preset = pedagogyOptions.find(option => option.id === approach);
                                        change({ pedagogyApproach: approach, ...(preset ? { aiContextPromptText: preset.context } : {}) }, () => {
                                            setPedagogyApproach(approach);
                                            if (preset) setAiContextPromptText(preset.context);
                                        });
                                    }} inputProps={{ 'aria-label': 'Pedagogy approach' }}
                                    sx={{ width: { xs: '100%', sm: 360 }, maxWidth: '100%', fontFamily: INTER, backgroundColor: '#fff' }}>
                                    {pedagogyOptions.map(option => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                                    <MenuItem value="custom">Custom</MenuItem>
                                </Select>
                            </Stack>
                            <TextField InputProps={{ readOnly: fieldReadOnly }} label="Additional AI context" inputRef={aiContextInputRef} fullWidth multiline minRows={8} maxRows={12} value={shown.aiContextPromptText}
                                helperText="Selecting an approach loads its guidance. Append the textbook introduction below it, or add its pages from a PDF; the combined guidance is saved as Custom."
                                onChange={event => {
                                    const value = event.target.value;
                                    const preset = pedagogyOptions.find(option => option.id === shown.pedagogyApproach);
                                    const custom = Boolean(preset && value !== preset.context);
                                    change({ aiContextPromptText: value, ...(custom ? { pedagogyApproach: 'custom' as PedagogyApproach } : {}) }, () => {
                                        setAiContextPromptText(value);
                                        if (custom) setPedagogyApproach('custom');
                                    });
                                }} placeholder="Example: Use task-based learning. Prioritize authentic communication and student talk time..." />
                            {!fieldReadOnly && <PdfPagesSources sources={pdfSources.filter(source => source.destination === 'guidance')} fieldText={shown.aiContextPromptText}
                                onAdd={() => setPdfDialog('guidance')} onRemove={removePdfPages} />}
                            {!fieldReadOnly && pdfChecks('ai_context_prompt_text', shown.aiContextPromptText, aiContextInputRef)}
                        </Box>
                    </Collapse>
                </VerbaScholaCard>
            </Box>

            {!readOnly && renderLessonActions?.(savePayload, canSave)}
            <Stack spacing={1.5} sx={stageFooterSx}>
                {generatePlanReason && <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
                    <CancelRoundedIcon sx={{ color: ERROR_TEXT, fontSize: 18, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, minWidth: 0, textAlign: 'right', color: WARNING_TEXT }}>{generatePlanReason}</Typography>
                </Stack>}
                <StageFooterActions>
                    <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} disabled={formBlocked} onClick={() => void onSaveLesson(savePayload).then(() => { onDirtyChange?.(false); onOpenPlan(); }).catch(() => undefined)} sx={{ px: 2.5, minHeight: 44, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                        Continue without generating
                    </Button>
                    <Button variant="contained" endIcon={<AutoAwesomeRoundedIcon />} disabled={formBlocked || (planExists ? generatePlanDisabled || !onGeneratePlan : createPlanDisabled || !brief.trim())} onClick={() => { if (planExists) onGeneratePlan?.(); else onCreatePlan?.(); }} sx={{ ...aiButtonSx, px: 3 }}>
                        {planExists ? 'Generate plan and continue' : 'Generate lesson plan'}
                    </Button>
                </StageFooterActions>
            </Stack>
            {entryEditor?.scope === 'vocabulary' && (() => {
                const index = lists.vocabulary.findIndex(entry => entry.key === entryEditor.key);
                const entry = index < 0 ? undefined : lists.vocabulary[index];
                return <VocabularyEntryDialog title={entry ? `Edit word ${index + 1}` : 'Add a word'} entry={entry ?? { hanzi: '', pinyin: '', meaning: '' }}
                    others={lists.vocabulary.filter(item => item !== entry).map(item => item.hanzi)} onClose={() => setEntryEditor(null)} onUnsavedChange={onEntryEditorUnsaved}
                    onDone={fields => {
                        setLists(current => ({ ...current, vocabulary: entry ? current.vocabulary.map(item => (item.key === entry.key ? { ...item, ...fields } : item))
                            : [...current.vocabulary, { key: newEntryKey(), ...fields }] }));
                        setEntryEditor(null);
                    }} />;
            })()}
            {entryEditor?.scope === 'grammar' && (() => {
                const index = lists.grammar.findIndex(entry => entry.key === entryEditor.key);
                const entry = index < 0 ? undefined : lists.grammar[index];
                return <GrammarEntryDialog title={entry ? `Edit grammar point ${index + 1}` : 'Add a grammar point'} entry={entry ?? { pattern: '', meaning: '', example: '' }}
                    others={lists.grammar.filter(item => item !== entry).map(item => item.pattern)} onClose={() => setEntryEditor(null)} onUnsavedChange={onEntryEditorUnsaved}
                    onDone={fields => {
                        setLists(current => ({ ...current, grammar: entry ? current.grammar.map(item => (item.key === entry.key ? { ...item, ...fields } : item))
                            : [...current.grammar, { key: newEntryKey(), ...fields }] }));
                        setEntryEditor(null);
                    }} />;
            })()}
            {pdfDialog && <React.Suspense fallback={<Backdrop open sx={{ zIndex: theme => theme.zIndex.modal }}><LinearProgress aria-label="Opening the PDF tool" sx={{ width: 200 }} /></Backdrop>}>
                <VerbaScholaPdfPagesDialog lessonId={lesson.id} destination={pdfDialog} files={pdfFiles}
                    fieldTexts={{ brief: shown.brief, guidance: shown.aiContextPromptText }}
                    classMinutes={Number(shown.durationMinutes)} level={LanguageLevelLabels[shown.proficiencyLevel as LanguageLevelEnum] ?? shown.proficiencyLevel} characterSet={shown.characterSet}
                    existingTargets={{ vocabulary: lists.vocabulary.map(entry => ({ hanzi: entry.hanzi.trim(), pinyin: pinyinWords(entry.pinyin) })),
                        grammar: lists.grammar.map(entry => entry.pattern) }}
                    onAddFile={file => setPdfFiles(current => (current.includes(file) ? current : [...current, file]))}
                    onClose={() => setPdfDialog(null)} onSave={result => addPdfPages(pdfDialog, result)} />
            </React.Suspense>}
        </Box>
    );
}

/** Selects part of a text field and brings it into view, in a field that scrolls inside or one that grows with its text. */
function selectInField(input: HTMLTextAreaElement | null, start: number, length: number) {
    if (!input) return;
    input.focus({ preventScroll: true });
    input.setSelectionRange(start, start + length);
    const before = input.value.slice(0, start).split('\n').length - 1;
    const share = before / Math.max(1, input.value.split('\n').length);
    if (input.scrollHeight > input.clientHeight + 1) {
        input.scrollTop = Math.max(0, input.scrollHeight * share - input.clientHeight / 2);
        input.scrollIntoView?.({ block: 'nearest' });
    } else {
        const rect = input.getBoundingClientRect();
        window.scrollBy({ top: rect.top + rect.height * share - window.innerHeight / 2 });
    }
}

function LanguageTargetCard({ title, icon: Icon, mode, onModeChange, count, pdfPages = false, children }: { title: string; icon: React.ElementType; mode: TargetMode; onModeChange: (mode: TargetMode) => void; count: number; pdfPages?: boolean; children: React.ReactNode }) {
    return (
        <Box sx={{ minWidth: 0, p: { xs: 2, sm: 2.25 }, border: `1px solid ${BORDER}`, borderRadius: '16px', backgroundColor: SURFACE_TINT }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box sx={{ width: 38, height: 38, flexShrink: 0, borderRadius: '12px', display: 'grid', placeItems: 'center', color: BLUE, backgroundColor: SOFT_BLUE }}>
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15 }}>{title}</Typography>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5 }}>{count} saved targets</Typography>
                </Box>
            </Stack>
            <ToggleButtonGroup exclusive size="small" value={mode}
                onChange={(_event, value: TargetMode | null) => value && onModeChange(value)}
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75, mt: 2, '& .MuiToggleButtonGroup-grouped': { minWidth: 0, border: `1px solid ${BORDER} !important`, borderRadius: '10px !important', px: 0.75, fontFamily: INTER, fontWeight: 700, fontSize: 11.5, lineHeight: 1.25, textTransform: 'none' } }}>
                <ToggleButton value="suggest">AI suggest</ToggleButton>
                <ToggleButton value="provide">I'll provide</ToggleButton>
                <ToggleButton value="hybrid">Mine + AI</ToggleButton>
            </ToggleButtonGroup>
            {/* With textbook pages added from a PDF, I'll provide keeps the targets chosen in the popup; the other modes let the AI add more from the pages. */}
            {pdfPages && mode !== 'provide'
                ? <Typography role="alert" sx={{ fontFamily: INTER, color: WARNING_TEXT, fontWeight: 700, fontSize: 11.5, mt: 1 }}>
                    The lesson brief holds pages added from a PDF. In this mode the AI adds {title.toLowerCase()} targets too, from those pages: it may add ones you left out in the PDF popup. Choose I'll provide so it adds none.
                </Typography>
                : <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, mt: 1 }}>
                    {mode === 'provide' ? `The lesson's ${title.toLowerCase()} targets are the ones listed below: the AI adds none.`
                        : mode === 'hybrid' ? 'This mode allows AI additions; the targets listed below are kept.' : 'This mode allows AI additions; existing teacher targets are preserved.'}
                </Typography>}
            <Box sx={{ mt: 2 }}>{children}</Box>
        </Box>
    );
}

/** A target list (component/targetEntries.ts), its targets shown as they are: each opens in its own editor
 * (TargetEntryDialog), and can be removed; a saved one says where it came from and can be revised with the AI, and one
 * not saved yet (added, changed) is marked so until the lesson is saved. */
function TargetList({ scope, entries, problems, unsaved, onEdit, onRemove, onRevise }: {
    scope: 'vocabulary' | 'grammar'; entries: (VocabularyEntry | GrammarEntry)[]; problems: Map<string, string>; unsaved: Set<string>;
    onEdit: (key: string | null) => void; onRemove: (entry: VocabularyEntry | GrammarEntry) => void; onRevise?: (id: number) => void;
}) {
    const kind = scope === 'vocabulary' ? 'vocabulary_target' : 'grammar_target';
    return (
        <Stack spacing={1} data-testid={`${scope}-target-list`}>
            {entries.map((entry, index) => {
                const label = scope === 'vocabulary' ? `word ${index + 1}` : `grammar point ${index + 1}`;
                const term = 'hanzi' in entry ? entry.hanzi : entry.pattern;
                const detail = 'hanzi' in entry
                    ? [instructionalPinyin(pinyinWords(entry.pinyin)), entry.meaning.trim()].filter(Boolean).join(' - ')
                    : [entry.meaning.trim(), entry.example.trim() && `Example: ${entry.example.trim()}`].filter(Boolean).join('\n');
                const problem = problems.get(entry.key);
                return (
                    <Stack key={entry.key} data-plan-target-id={entry.id === undefined ? undefined : `${kind}:${entry.id}`} data-plan-target-text={`${kind}:${term}`}
                        tabIndex={-1} direction="row" spacing={1} alignItems="center"
                        sx={{ p: 1.25, border: `1px solid ${problem ? ERROR_TEXT : BORDER}`, borderRadius: '12px', backgroundColor: '#fff' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 13.5 }}>{term}</Typography>
                            {detail && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, whiteSpace: 'pre-wrap' }}>{detail}</Typography>}
                            {problem && <Typography role="alert" sx={{ fontFamily: INTER, color: ERROR_TEXT, fontSize: 12 }}>{problem}</Typography>}
                        </Box>
                        {unsaved.has(entry.key)
                            ? <Chip label="Not saved yet" size="small" sx={{ height: 22, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: SLATE, backgroundColor: SURFACE_MUTED }} />
                            : <SmallSourceChip source={entry.source} />}
                        {onRevise && entry.id !== undefined && <IconButton aria-label={scope === 'vocabulary' ? 'Revise vocabulary target with AI' : 'Revise grammar target with AI'}
                            onClick={() => onRevise(entry.id as number)} sx={{ color: BLUE, width: 44, height: 44 }}><AutoAwesomeRoundedIcon fontSize="small" /></IconButton>}
                        <IconButton aria-label={`Edit ${label}`} onClick={() => onEdit(entry.key)} sx={{ color: BLUE, width: 44, height: 44 }}><EditRoundedIcon fontSize="small" /></IconButton>
                        <IconButton aria-label={`Remove ${label}`} onClick={() => onRemove(entry)} sx={{ color: SLATE, width: 44, height: 44 }}><CloseRoundedIcon fontSize="small" /></IconButton>
                    </Stack>
                );
            })}
            <Button startIcon={<AddRoundedIcon />} onClick={() => onEdit(null)} sx={{ alignSelf: 'flex-start', fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                {scope === 'vocabulary' ? 'Add a word' : 'Add a grammar point'}
            </Button>
        </Stack>
    );
}

function VocabularyTargetRow({ target, saving, onSave, onDelete, onEditingChange, onRevise }: {
    target: IVerbaScholaLessonVocabularyTarget; saving: boolean;
    onSave: (targetId: number, payload: Partial<IVerbaScholaLessonVocabularyTarget>) => Promise<void>;
    onRevise?: () => void; onDelete: (targetId: number) => Promise<void>; onEditingChange?: (key: string, editing: boolean) => void;
}) {
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const saveLabel = useReviewSaveLabel('Save');
    const [hanzi, setHanzi] = React.useState(target.hanzi);
    const [pinyin, setPinyin] = React.useState(editablePinyin(target.pinyin));
    const [meaning, setMeaning] = React.useState(target.meaning ?? '');
    const [editing, setEditing] = React.useState(false);

    React.useEffect(() => { onEditingChange?.("vocabulary-" + target.id, editing);
        return () => onEditingChange?.("vocabulary-" + target.id, false);
    }, [editing, onEditingChange, target.id]);

    React.useEffect(() => {
        if (editing) return;
        setHanzi(target.hanzi);
        setPinyin(editablePinyin(target.pinyin));
        setMeaning(target.meaning ?? '');
    }, [target, editing]);
    // In a review the editor exists only in Your version: elsewhere, or once the review closes, its Save would not be a
    // review edit, so an editor opened in the review closes and drops its unkept text (the text the review asked about).
    const openedInReview = React.useRef(false);
    React.useEffect(() => {
        if (editing && reviewEdit) openedInReview.current = true;
        if (!reviewEdit && (readOnly || openedInReview.current)) { openedInReview.current = false; setEditing(false); }
        else if (!editing) openedInReview.current = false;
    }, [editing, readOnly, reviewEdit]);
    useReviewUnkept(`vocabulary_target:${target.id}`, reviewEdit && editing
        && (hanzi !== target.hanzi || pinyin !== editablePinyin(target.pinyin) || meaning !== (target.meaning ?? '')));

    if (!editing || (readOnly && !reviewEdit)) {
        return (
            <Stack data-plan-target-id={`vocabulary_target:${target.id}`} data-plan-target-text={`vocabulary_target:${target.hanzi}`} tabIndex={-1} direction="row" spacing={1} alignItems="center" sx={{ p: 1.25, border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: '#fff' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 13.5 }}>{target.hanzi}</Typography>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>{instructionalPinyin(target.pinyin)}{target.meaning ? ` - ${target.meaning}` : ''}</Typography>
                </Box>
                <SmallSourceChip source={target.source} />
                {onRevise && <IconButton disabled={saving} aria-label="Revise vocabulary target with AI" onClick={onRevise} sx={{ color: BLUE, width: 44, height: 44 }}><AutoAwesomeRoundedIcon fontSize="small" /></IconButton>}
                {(!readOnly || reviewEdit) && <IconButton aria-label="Edit vocabulary target" onClick={() => setEditing(true)} sx={{ color: BLUE, width: 44, height: 44 }}><EditRoundedIcon fontSize="small" /></IconButton>}
            </Stack>
        );
    }

    return (
        <Stack spacing={1} sx={{ p: 1.25, border: `1px solid ${BLUE}`, borderRadius: '12px', backgroundColor: '#fff' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Word" value={hanzi} onChange={event => setHanzi(event.target.value)} fullWidth />
                <VerbaScholaPinyinField hanzi={hanzi} value={pinyin} onChange={setPinyin} />
            </Stack>
            <TextField label="Meaning" value={meaning} onChange={event => setMeaning(event.target.value)} fullWidth />
            <Stack direction="row" spacing={1}>
                <Button
                    disabled={saving || hanzi.trim().length === 0}
                    onClick={() => onSave(target.id, { hanzi: hanzi.trim(), pinyin: optionalText(pinyin)?.split(/[,\s]+/).filter(Boolean) ?? null, meaning: optionalText(meaning) }).then(() => setEditing(false)).catch(() => undefined)}
                    sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>{saveLabel}</Button>
                <Button onClick={() => setEditing(false)} sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, textTransform: 'none' }}>Cancel</Button>
                {!readOnly && <VerbaScholaConfirmDeleteButton label="Delete word" disabled={saving} onConfirm={() => onDelete(target.id)} sx={{ ml: 'auto !important' }} />}
            </Stack>
        </Stack>
    );
}

function GrammarTargetRow({ target, saving, onSave, onDelete, onEditingChange, onRevise }: {
    target: IVerbaScholaLessonGrammarTarget; saving: boolean;
    onSave: (targetId: number, payload: Partial<IVerbaScholaLessonGrammarTarget>) => Promise<void>;
    onRevise?: () => void; onDelete: (targetId: number) => Promise<void>; onEditingChange?: (key: string, editing: boolean) => void;
}) {
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const saveLabel = useReviewSaveLabel('Save');
    const [pattern, setPattern] = React.useState(target.pattern);
    const [meaning, setMeaning] = React.useState(target.meaning ?? '');
    const [example, setExample] = React.useState(target.example ?? '');
    const [editing, setEditing] = React.useState(false);

    React.useEffect(() => { onEditingChange?.("grammar-" + target.id, editing);
        return () => onEditingChange?.("grammar-" + target.id, false);
    }, [editing, onEditingChange, target.id]);

    React.useEffect(() => {
        if (editing) return;
        setPattern(target.pattern);
        setMeaning(target.meaning ?? '');
        setExample(target.example ?? '');
    }, [target, editing]);
    // In a review the editor exists only in Your version: elsewhere, or once the review closes, its Save would not be a
    // review edit, so an editor opened in the review closes and drops its unkept text (the text the review asked about).
    const openedInReview = React.useRef(false);
    React.useEffect(() => {
        if (editing && reviewEdit) openedInReview.current = true;
        if (!reviewEdit && (readOnly || openedInReview.current)) { openedInReview.current = false; setEditing(false); }
        else if (!editing) openedInReview.current = false;
    }, [editing, readOnly, reviewEdit]);
    useReviewUnkept(`grammar_target:${target.id}`, reviewEdit && editing
        && (pattern !== target.pattern || meaning !== (target.meaning ?? '') || example !== (target.example ?? '')));

    if (!editing || (readOnly && !reviewEdit)) {
        return (
            <Stack data-plan-target-id={`grammar_target:${target.id}`} data-plan-target-text={`grammar_target:${target.pattern}`} tabIndex={-1} direction="row" spacing={1} alignItems="center" sx={{ p: 1.25, border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: '#fff' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 13.5 }}>{target.pattern}</Typography>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>{target.meaning || 'No meaning'}{target.example ? ` - ${target.example}` : ''}</Typography>
                </Box>
                <SmallSourceChip source={target.source} />
                {onRevise && <IconButton disabled={saving} aria-label="Revise grammar target with AI" onClick={onRevise} sx={{ color: BLUE, width: 44, height: 44 }}><AutoAwesomeRoundedIcon fontSize="small" /></IconButton>}
                {(!readOnly || reviewEdit) && <IconButton aria-label="Edit grammar target" onClick={() => setEditing(true)} sx={{ color: BLUE, width: 44, height: 44 }}><EditRoundedIcon fontSize="small" /></IconButton>}
            </Stack>
        );
    }

    return (
        <Stack spacing={1} sx={{ p: 1.25, border: `1px solid ${BLUE}`, borderRadius: '12px', backgroundColor: '#fff' }}>
            <TextField label="Pattern" value={pattern} onChange={event => setPattern(event.target.value)} fullWidth />
            <TextField label="Meaning" value={meaning} onChange={event => setMeaning(event.target.value)} fullWidth />
            <TextField label="Example" value={example} onChange={event => setExample(event.target.value)} fullWidth />
            <Stack direction="row" spacing={1}>
                <Button
                    disabled={saving || pattern.trim().length === 0}
                    onClick={() => onSave(target.id, { pattern: pattern.trim(), meaning: optionalText(meaning), example: optionalText(example) }).then(() => setEditing(false)).catch(() => undefined)}
                    sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>{saveLabel}</Button>
                <Button onClick={() => setEditing(false)} sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, textTransform: 'none' }}>Cancel</Button>
                {!readOnly && <VerbaScholaConfirmDeleteButton label="Delete grammar target" disabled={saving} onConfirm={() => onDelete(target.id)} sx={{ ml: 'auto !important' }} />}
            </Stack>
        </Stack>
    );
}

const MaterialRevisionContext = React.createContext<((materialId: number) => void) | undefined>(undefined);
const PlanRevisionContext = React.createContext<((sectionId: number) => void) | undefined>(undefined);

// Plan and Exercises share their header controls so both stages look and sit the same. Marked
// buttons keep the theme's size inside the Plan editor, which shrinks its other buttons.
const STAGE_HEADER_BUTTON = 'stage-header-button';
const stageHeaderButtonSx = { minHeight: 44, whiteSpace: 'nowrap', fontFamily: INTER, fontWeight: 700, textTransform: 'none' } as const;
/** Each stage's footer: its status note and actions at the end of the page. It scrolls with the page rather than
 *  staying pinned, which took too much of a phone's screen. */
const stageFooterSx = { mt: 3, mb: 1, p: 1.5, borderRadius: '16px', border: `1px solid ${BORDER}`, backgroundColor: PAGE_BACKGROUND } as const;

/** Footer actions: one right-aligned row when they fit at their natural widths; otherwise, as on a phone or
 *  in a narrow column, two per row with a lone last action taking its row. */
function StageFooterActions({ children }: { children: React.ReactNode }) {
    const ref = React.useRef<HTMLDivElement>(null);
    const [paired, setPaired] = React.useState(false);
    const measure = React.useCallback(() => {
        const row = ref.current;
        if (!row) return;
        // Measure every action at its natural width whichever layout is showing; the inline style is gone before paint.
        const items = Array.from(row.children) as HTMLElement[];
        items.forEach(item => { item.style.flex = '0 0 auto'; });
        const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
        const oneRow = items.reduce((total, item) => total + item.getBoundingClientRect().width, 0) + gap * Math.max(0, items.length - 1);
        items.forEach(item => { item.style.flex = ''; });
        setPaired(oneRow > row.clientWidth + 0.5);
    }, []);
    // The actions change with the lesson's state, so measure after each render as well as on resize.
    React.useLayoutEffect(measure);
    React.useEffect(() => {
        const row = ref.current;
        if (!row || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => measure());
        observer.observe(row);
        return () => observer.disconnect();
    }, [measure]);
    return <Box ref={ref} data-footer-layout={paired ? 'paired' : 'one-row'} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1.5,
        '& > *': { flex: paired ? '1 1 calc(50% - 6px)' : '0 0 auto' } }}>{children}</Box>;
}

/** What sits on a stage's title row: readiness, and below lg the side panels' drawer openers at either end. */
interface StageTitleControls {
    readiness: React.ReactNode;
    /** Opens the lesson structure drawer; shown at the row's left edge. */
    leading?: React.ReactNode;
    /** Opens the copilot drawer; shown at the row's right edge. */
    trailing?: React.ReactNode;
}

/** A stage or view title with its controls. Wide screens keep them on the title's row and long titles wrap;
 *  below lg, where the side panels are drawers, the controls take their own row above the title. */
function StageTitleRow({ title, readiness, leading, trailing }: StageTitleControls & { title: React.ReactNode }) {
    return <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', lg: 'nowrap' }, columnGap: { xs: 1, lg: 2 }, rowGap: 1.5 }}>
        {leading && <Box sx={{ order: 0, flexShrink: 0 }}>{leading}</Box>}
        <Typography component="h1" sx={{ order: { xs: 4, lg: 1 }, flex: { xs: '1 1 100%', lg: '1 1 0' }, minWidth: 0, overflowWrap: 'anywhere',
            fontFamily: POPPINS, fontWeight: 600, color: NAVY, lineHeight: 1.25, fontSize: { xs: 23, md: 27 }, letterSpacing: '-0.02em' }}>{title}</Typography>
        <Box sx={{ order: 2, flex: { xs: '1 1 0', lg: '0 0 auto' }, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>{readiness}</Box>
        {trailing && <Box sx={{ order: 3, flexShrink: 0 }}>{trailing}</Box>}
    </Box>;
}

/** A compact drawer opener: the panel's icon with arrows pointing into the page, like the wide-screen rails. */
export function DrawerIconButton({ side, label, icon, expanded, onClick }: {
    side: 'left' | 'right'; label: string; icon: React.ReactNode; expanded: boolean; onClick: () => void;
}) {
    const arrow = side === 'left' ? <KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize: 16 }} /> : <KeyboardDoubleArrowLeftRoundedIcon sx={{ fontSize: 16 }} />;
    return <Tooltip title={label}>
        <ButtonBase aria-label={label} aria-expanded={expanded} onClick={onClick}
            sx={{ height: 44, px: 1.25, gap: 0.25, borderRadius: '11px', border: `1px solid ${BORDER}`, backgroundColor: '#fff', color: BLUE_TEXT,
                '&:hover': { borderColor: BLUE, backgroundColor: SOFT_BLUE } }}>
            {side === 'left' ? <>{icon}{arrow}</> : <>{arrow}{icon}</>}
        </ButtonBase>
    </Tooltip>;
}

/** The stage's readiness and its Mark all ready button. Narrow rows wrap the two, right-aligned. */
function StageReadiness({ percent, children }: { percent: number; children: React.ReactNode }) {
    return <Stack role="group" aria-label="Stage readiness" direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, borderRadius: '999px', backgroundColor: '#fff', border: `1px solid ${BORDER}` }}>
            <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 18 }} />
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 13, whiteSpace: 'nowrap' }}>{percent}% ready</Typography>
        </Box>
        {children}
    </Stack>;
}

/** The header of each Exercises view: title beside readiness, the breadcrumb with the view's controls, then its details. */
function ExerciseViewHeader({ title, titleControls, location, controls, details }: {
    title: React.ReactNode; titleControls: StageTitleControls; location: React.ReactNode; controls?: React.ReactNode; details: React.ReactNode;
}) {
    return <Box sx={{ mb: 2.5 }}>
        <StageTitleRow title={title} {...titleControls} />
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mt: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{location}</Box>
            {controls}
        </Stack>
        <Box aria-label="View details" sx={{ mt: 1 }}>{details}</Box>
    </Box>;
}

export function PlanEditor({
    selectedPanel, onPanelChange, procedureViewState,
    onReviseSection, onReviseMaterial, onReviseTarget,
    copilot, copilotOpen, onCopilotOpenChange, reviewOpen = false, readOnly = false, reviewEditable = false, reviewBar,
    contentVersion = 0,
    onDirtyChange,
    lesson,
    course,
    planSections,
    generatedMaterials,
    vocabularyTargets,
    grammarTargets,
    saving,
    copilotDisabled = saving,
    onBack,
    onContinue,
    continueDisabled = false,
    onGenerateExercises,
    generateExercisesLabel = 'Continue and generate exercises',
    generateExercisesDisabled = false,
    generateExercisesBlockReason,
    exercisesLockReason,
    onMarkAllReady,
    markAllReadyDisabled = false,
    markAllReadyBlockReason,
    copilotAttention,
    onAddSection,
    onUpdateSection,
    onDeleteSection,
    onAddMaterial,
    onUpdateMaterial,
    onDeleteMaterial,
    onAddVocabulary,
    onUpdateVocabulary,
    onDeleteVocabulary,
    onAddGrammar,
    onUpdateGrammar,
    onDeleteGrammar,
}: {
    procedureViewState?: ProcedureViewState;
    selectedPanel?: PlanPanel; onPanelChange?: (panel: PlanPanel) => void;
    copilot: React.ReactNode;
    copilotOpen?: boolean;
    onCopilotOpenChange?: (open: boolean) => void;
    reviewOpen?: boolean;
    contentVersion?: number | string;
    onReviseMaterial?: (materialId: number) => void;
    onReviseTarget?: (target_type: 'vocabulary_target' | 'grammar_target', target_id: number) => void;
    onReviseSection?: (sectionId: number) => void;
    onDirtyChange?: (value: boolean) => void;
    lesson: IVerbaScholaLesson;
    course: IVerbaScholaCourse | null;
    planSections: IVerbaScholaLessonPlanSection[];
    generatedMaterials: IVerbaScholaLessonGeneratedMaterial[];
    vocabularyTargets: IVerbaScholaLessonVocabularyTarget[];
    grammarTargets: IVerbaScholaLessonGrammarTarget[];
    saving: boolean;
    /** Disables the copilot drawer on a narrow window, whose portal the page's own fieldset does not reach; defaults to
     *  saving, but a page whose content is read-only for another reason (a review) keeps its copilot usable. */
    copilotDisabled?: boolean;
    onBack: () => void;
    /** Opens the exercises without generating. */
    onContinue: () => void;
    continueDisabled?: boolean;
    /** Generates exercise drafts from the saved plan, then opens their review. */
    onGenerateExercises: () => void;
    generateExercisesLabel?: string;
    generateExercisesDisabled?: boolean;
    generateExercisesBlockReason?: string | null;
    /** Why both exercise actions are closed, shown in the footer. */
    exercisesLockReason?: string | null;
    onMarkAllReady: () => void;
    markAllReadyDisabled?: boolean;
    markAllReadyBlockReason?: string | null;
    /** Why a folded copilot needs a look, marked on its rail. */
    copilotAttention?: string | null;
    /** Shows a lesson version for proposal review: no edits, no dirty state. */
    readOnly?: boolean;
    /** With ``readOnly``: the review shows Your version, whose content the teacher may edit. */
    reviewEditable?: boolean;
    /** Proposal review controls above the plan content. */
    reviewBar?: React.ReactNode;
    onAddSection: (payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onUpdateSection: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onDeleteSection: (sectionId: number) => Promise<void>;
    onAddMaterial: (payload: Partial<IVerbaScholaLessonGeneratedMaterial>) => Promise<void>;
    onUpdateMaterial: (materialId: number, payload: Partial<IVerbaScholaLessonGeneratedMaterial>) => Promise<void>;
    onDeleteMaterial: (materialId: number) => Promise<void>;
    onAddVocabulary: (payload: Partial<IVerbaScholaLessonVocabularyTarget>) => Promise<void>;
    onUpdateVocabulary: (targetId: number, payload: Partial<IVerbaScholaLessonVocabularyTarget>) => Promise<void>;
    onDeleteVocabulary: (targetId: number) => Promise<void>;
    onAddGrammar: (payload: Partial<IVerbaScholaLessonGrammarTarget>) => Promise<void>;
    onUpdateGrammar: (targetId: number, payload: Partial<IVerbaScholaLessonGrammarTarget>) => Promise<void>;
    onDeleteGrammar: (targetId: number) => Promise<void>;
}) {
    const [localPanel, setLocalPanel] = React.useState<PlanPanel>('language');
    const theme = useTheme();
    const compactPlan = useMediaQuery(theme.breakpoints.down('lg'));
    const [localCopilotOpen, setLocalCopilotOpen] = React.useState(false);
    const planCopilotOpen = copilotOpen ?? localCopilotOpen;
    const setPlanCopilotOpen = onCopilotOpenChange ?? setLocalCopilotOpen;
    const { open: panelsOpen, setOpen: setPanelOpen } = useBuilderPanels();
    const copilotFolded = !compactPlan && !panelsOpen.copilot;
    const showPlanCopilot = () => compactPlan ? setPlanCopilotOpen(true) : setPanelOpen('copilot', true);
    const activePanel = selectedPanel ?? localPanel;
    const setActivePanel = (panel: PlanPanel) => { setLocalPanel(panel); onPanelChange?.(panel); };
    const sectionsByType = {
        context: planSections.filter(section => planSectionType(section) === 'context'),
        outcomes: planSections.filter(section => planSectionType(section) === 'outcomes'),
        language: planSections.filter(section => planSectionType(section) === 'language'),
        preparation: planSections.filter(section => planSectionType(section) === 'preparation'),
        support: planSections.filter(section => planSectionType(section) === 'support'),
        procedure: planSections.filter(section => planSectionType(section) === 'procedure'),
        assessment: planSections.filter(section => planSectionType(section) === 'assessment'),
    };
    const readiness = planReadiness(planSections, generatedMaterials);
    const readyCount = readiness.ready;
    const totalCount = readiness.required;
    const readinessPercent = totalCount === 0 ? 0 : Math.round((readyCount / totalCount) * 100);
    const procedureMinutes = sectionsByType.procedure.reduce((total, section) => total + (section.duration_minutes ?? 0), 0);
    // A pasted unit's procedure has no one class length: it holds all its class periods.
    const periodMinutes = sectionsByType.procedure.flatMap(section => procedurePeriodMinutes(section.body));
    const stageCount = sectionsByType.procedure.reduce((count, section) => count + planProcedureSteps(section.body).length, 0);

    function planPanelReadiness(panel: PlanPanel): string {
        const items = panel === 'materials'
            ? generatedMaterials.map(material => material.status)
            : sectionsByType[panel].filter(section => section.title !== TEACHER_REFLECTION_TITLE).map(section => section.readiness_status);
        return `${items.filter(status => status === 'ready').length}/${items.length} ready`;
    }

    function planPanelDetail(panel: PlanPanel): string {
        if (panel === 'context') return `${6 + sectionsByType.context.length} class details`;
        if (panel === 'language') return `${vocabularyTargets.length} words - ${grammarTargets.length} grammar points`;
        if (panel === 'materials') return `${generatedMaterials.length} ${generatedMaterials.length === 1 ? 'material' : 'materials'}`;
        if (panel === 'procedure') {
            if (periodMinutes.length) return `${periodMinutes.length} class periods - ${periodMinutes.reduce((total, minutes) => total + minutes, 0)} minutes`;
            return procedureMinutes > 0 ? `${procedureMinutes} minutes` : `${stageCount} ${stageCount === 1 ? 'stage' : 'stages'}`;
        }
        const count = sectionsByType[panel].length;
        return count > 0 ? `${count} saved ${count === 1 ? 'section' : 'sections'}` : 'Not drafted';
    }

    return (
        <ProcedureViewStateContext.Provider value={procedureViewState ?? null}><MaterialRevisionContext.Provider value={onReviseMaterial}><PlanRevisionContext.Provider value={onReviseSection}><Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: `minmax(0, 1fr) ${copilotFolded ? BUILDER_RAIL_WIDTH : BUILDER_COPILOT_WIDTH}px` }, gap: 3, alignItems: 'start', ...builderPanelResize('grid-template-columns'), '& .MuiChip-root': { height: 22, fontSize: 11 }, [`& .MuiButton-root:not(.${STAGE_HEADER_BUTTON})`]: { fontFamily: INTER, fontSize: 12, textTransform: 'none' },
            '@keyframes planChangeFlash': { '0%, 100%': { outlineColor: 'transparent' }, '20%, 70%': { outlineColor: BLUE } },
            '& [data-plan-highlight="true"]': { outline: `3px solid ${BLUE}`, outlineOffset: 4, animation: 'planChangeFlash 2s ease-out' },
            '@media (prefers-reduced-motion: reduce)': { '& [data-plan-highlight="true"]': { animation: 'none' } },
        }}>
            <LessonReviewEditContext.Provider value={readOnly && reviewEditable}><LessonReviewReadOnlyContext.Provider value={readOnly}><Box key={`content:${contentVersion}`} sx={{ minWidth: 0 }} onChangeCapture={event => { if (!readOnly && !(event.target as HTMLElement).closest('[data-image-generator-guidance]') && (event.target as HTMLInputElement).type !== 'checkbox') queueMicrotask(() => onDirtyChange?.(true)); }} onClickCapture={event => { if (!readOnly && !(event.target as HTMLElement).closest('[data-image-generator-guidance]') && (event.target as HTMLElement).closest('[role="option"]')) queueMicrotask(() => onDirtyChange?.(true)); }}>
                <Box data-testid="plan-editor-content" sx={{ width: '100%', maxWidth: 1280 + (copilotFolded ? BUILDER_COPILOT_WIDTH - BUILDER_RAIL_WIDTH : 0), mx: 'auto', ...builderPanelResize('max-width') }}>
                {reviewBar}
                <Box sx={{ mb: 3 }}>
                    <StageTitleRow title={lesson.title}
                        readiness={<StageReadiness percent={readinessPercent}>
                            <Button className={STAGE_HEADER_BUTTON} variant="outlined" startIcon={<CheckCircleRoundedIcon />} disabled={saving || markAllReadyDisabled || (readiness.markable === 0 && readiness.needsContent.length === 0)} onClick={onMarkAllReady}
                                aria-describedby={markAllReadyBlockReason && (readiness.markable > 0 || readiness.needsContent.length > 0) ? 'plan-ready-block-reason' : undefined}
                                sx={stageHeaderButtonSx}>Mark all ready{readiness.markable > 0 ? ` (${readiness.markable})` : ''}</Button>
                        </StageReadiness>}
                        trailing={compactPlan && <DrawerIconButton side="right" label="Copilot" icon={<AutoAwesomeRoundedIcon fontSize="small" />}
                            expanded={planCopilotOpen} onClick={() => setPlanCopilotOpen(true)} />} />
                    {/* Written like the session overview's details line. */}
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13.5, mt: 1 }}>{course?.name ?? 'Class'}{lesson.duration_minutes ? ` · about ${lesson.duration_minutes} min` : ''}</Typography>
                </Box>

                <VerbaScholaCard>
                    <Box sx={{ p: 2.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="baseline" spacing={1}>
                                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 17 }}>Lesson plan readiness</Typography>
                                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: BLUE, fontSize: 17 }}>{readinessPercent}%</Typography>
                                </Stack>
                                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12.5 }}>{readyCount} of {totalCount} saved items marked ready{totalCount > 0 && readyCount < totalCount ? '. All must be ready before publishing.' : ''}</Typography>
                                {/* Why Mark all ready (in the header above) is unavailable. */}
                                {markAllReadyBlockReason && (readiness.markable > 0 || readiness.needsContent.length > 0) && <Typography id="plan-ready-block-reason" sx={{ fontFamily: INTER, color: WARNING_TEXT, fontSize: 12.5, mt: 0.5 }}>{markAllReadyBlockReason}</Typography>}
                            </Box>
                        </Stack>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1 }}>
                            {planPanelConfigs.map(section => {
                                const selected = activePanel === section.id;
                                return (
                                    <Button key={section.id} onClick={() => setActivePanel(section.id)} sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1.25, borderRadius: '12px', border: `1px solid ${selected ? BLUE : BORDER}`, backgroundColor: selected ? SOFT_BLUE : SURFACE_TINT }}>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography noWrap sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 11.5 }}>{section.label}</Typography>
                                            <Typography noWrap sx={{ fontFamily: INTER, color: SLATE, fontSize: 11 }}>{planPanelDetail(section.id)}</Typography>
                                            <Typography sx={{ fontFamily: INTER, color: BLUE_TEXT, fontSize: 11, fontWeight: 600 }}>{planPanelReadiness(section.id)}</Typography>
                                        </Box>
                                        <ArrowForwardRoundedIcon sx={{ ml: 0.75, color: BLUE, fontSize: 17 }} />
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                </VerbaScholaCard>
                <Box sx={{ height: 20 }} />

                <Box id={`plan-panel-${activePanel}`} tabIndex={-1} sx={{ scrollMarginTop: '100px', borderRadius: '20px' }}>
                {activePanel === 'context' && <PlanContextCard course={course} lesson={lesson} sections={sectionsByType.context} saving={saving} onAdd={onAddSection} onUpdate={onUpdateSection} onDelete={onDeleteSection} />}
                {activePanel === 'materials' && <GeneratedMaterialList materials={generatedMaterials} saving={saving} onAdd={onAddMaterial} onUpdate={onUpdateMaterial} onDelete={onDeleteMaterial} />}
                {activePanel === 'language' && (
                    <PlanLanguageTargetsCard onReviseTarget={onReviseTarget}
                        vocabularyTargets={vocabularyTargets}
                        grammarTargets={grammarTargets}
                        sections={sectionsByType.language}
                        saving={saving}
                        onAdd={onAddSection}
                        onUpdate={onUpdateSection}
                        onDelete={onDeleteSection}
                        onAddVocabulary={onAddVocabulary}
                        onUpdateVocabulary={onUpdateVocabulary}
                        onDeleteVocabulary={onDeleteVocabulary}
                        onAddGrammar={onAddGrammar}
                        onUpdateGrammar={onUpdateGrammar}
                        onDeleteGrammar={onDeleteGrammar}
                    />
                )}
                {activePanel === 'outcomes' && (
                    <PlanNamedSectionGroup
                        sectionType="outcomes"
                        cards={[{
                            title: 'Learning objectives',
                            description: 'Can-do goals students should be able to demonstrate by the end of class.',
                            placeholder: 'Students can order one food and one drink politely using complete sentences and correct measure words.',
                            variant: 'checklist',
                        }]}
                        sections={sectionsByType.outcomes}
                        saving={saving}
                        onAdd={onAddSection}
                        onUpdate={onUpdateSection}
                        onDelete={onDeleteSection}
                    />
                )}
                {activePanel === 'preparation' && (
                    <PlanNamedSectionGroup
                        sectionType="preparation"
                        cards={[
                            {
                                title: 'Preparation checklist',
                                description: 'Setup tasks, handouts, media, and room preparation before class starts.',
                                placeholder: 'Print menu handouts and role cards.\nOpen the lesson visual and model-dialogue audio.\nArrange students into pairs.',
                                variant: 'checklist',
                            },
                            {
                                title: 'Board plan',
                                description: 'What should be visible on the board while students work.',
                                placeholder: 'WORDS\nmenu, server, dumplings\n\nPATTERN\nPlease give me + number + measure word + noun\n\nGOAL\nI can order politely.',
                                variant: 'plain',
                            },
                        ]}
                        sections={sectionsByType.preparation}
                        saving={saving}
                        onAdd={onAddSection}
                        onUpdate={onUpdateSection}
                        onDelete={onDeleteSection}
                    />
                )}
                {activePanel === 'support' && (
                    <PlanNamedSectionGroup
                        sectionType="support"
                        cards={[
                            {
                                title: 'Anticipated difficulties',
                                description: 'Likely student errors or friction points to watch during class.',
                                placeholder: 'Students may omit measure words or translate requests too directly. Model complete polite request chunks.',
                                variant: 'plain',
                                accent: 'warning',
                            },
                            {
                                title: 'Differentiation',
                                description: 'Support and extension paths for students who need different levels of scaffolding.',
                                placeholder: 'SUPPORT\nProvide pinyin, response starters, and a model exchange.\n\nEXTENSION\nAsk about price, request a recommendation, and modify an order.',
                                variant: 'plain',
                            },
                        ]}
                        sections={sectionsByType.support}
                        saving={saving}
                        onAdd={onAddSection}
                        onUpdate={onUpdateSection}
                        onDelete={onDeleteSection}
                    />
                )}
                {activePanel === 'procedure' && <PlanSectionList sections={sectionsByType.procedure} saving={saving} onAdd={onAddSection} onUpdate={onUpdateSection} onDelete={onDeleteSection} />}
                {activePanel === 'assessment' && (
                    <PlanNamedSectionGroup
                        sectionType="assessment"
                        cards={[
                            {
                                title: 'Assessment',
                                description: 'Checks for understanding and evidence to collect during class.',
                                placeholder: 'During pair work, listen for correct measure words and polite phrasing. Collect an individual exit-ticket recording.',
                                variant: 'plain',
                            },
                            {
                                title: 'Companion practice',
                                description: 'Follow-up VerbaSchola practice students should complete after class.',
                                placeholder: 'Assign a 25-minute exercise set after class: vocabulary review, measure-word listening, guided ordering, role-play, and an independent check.',
                                variant: 'plain',
                            },
                            {
                                title: 'Teacher reflection',
                                description: 'Notes to complete after class before reusing the lesson. Visible to the AI assistant as read-only context; edit these notes yourself.',
                                placeholder: 'Which targets were secure? Where did students need support? What should change before this lesson is reused?',
                                variant: 'plain',
                            },
                        ]}
                        sections={sectionsByType.assessment}
                        saving={saving}
                        onAdd={onAddSection}
                        onUpdate={onUpdateSection}
                        onDelete={onDeleteSection}
                    />
                )}

                </Box>
                <Stack spacing={1.5} sx={stageFooterSx}>
                    {/* Under review the page saves nothing itself: only a reason or the readiness reminder is worth a line. */}
                    {(generateExercisesBlockReason || exercisesLockReason || !readiness.allReady || !readOnly) && <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
                        {generateExercisesBlockReason || exercisesLockReason
                            ? <CancelRoundedIcon sx={{ color: ERROR_TEXT, fontSize: 18, flexShrink: 0 }} />
                            : <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 18, flexShrink: 0 }} />}
                        <Typography id={generateExercisesBlockReason ? 'plan-exercises-block-reason' : undefined} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, minWidth: 0, textAlign: 'right', color: generateExercisesBlockReason || exercisesLockReason ? WARNING_TEXT : SUCCESS_TEXT }}>{generateExercisesBlockReason ?? exercisesLockReason ?? (!readiness.allReady ? 'You can work on exercises now. Mark both the plan and exercises Ready before publishing.' : 'Manual changes save with the buttons in each section')}</Typography>
                    </Stack>}
                    <StageFooterActions>
                        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ minHeight: 44, color: SLATE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Back to description</Button>
                        <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} disabled={saving || continueDisabled || Boolean(exercisesLockReason)} onClick={onContinue} sx={{ minHeight: 44, px: 2.5, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Continue without generating</Button>
                        <Button variant="contained" endIcon={<AutoAwesomeRoundedIcon />} disabled={saving || generateExercisesDisabled || Boolean(exercisesLockReason)} onClick={onGenerateExercises}
                            aria-describedby={generateExercisesBlockReason ? 'plan-exercises-block-reason' : undefined}
                            sx={{ ...aiButtonSx, px: 3 }}>{generateExercisesLabel}</Button>
                    </StageFooterActions>
                </Stack>
                </Box>
            </Box></LessonReviewReadOnlyContext.Provider></LessonReviewEditContext.Provider>
            {compactPlan ? <>
                <Backdrop open={planCopilotOpen && !reviewOpen} onClick={() => setPlanCopilotOpen(false)} sx={{ zIndex: theme.zIndex.drawer - 1 }} />
                <Drawer anchor="right" variant="persistent" open={planCopilotOpen} onKeyDown={event => { if (event.key === 'Escape') setPlanCopilotOpen(false); }}
                    PaperProps={{ sx: { width: 'min(92vw, 380px)' } }}>
                <Box component="fieldset" disabled={copilotDisabled} sx={{ border: 0, p: 0, m: 0, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Lesson copilot</Typography>
                    <IconButton aria-label="Close lesson copilot" onClick={() => setPlanCopilotOpen(false)} sx={{ width: 44, height: 44 }}><CloseRoundedIcon /></IconButton>
                </Stack>
                {copilot}
                </Box>
                </Drawer>
            </> : <>
                {/* Stretched to the row so the copilot's own sticky pane still follows the page; full width at
                    the right edge while the column eases wider, so its text does not reflow. */}
                <Box data-builder-panel="copilot" sx={{ minWidth: 0, width: BUILDER_COPILOT_WIDTH, justifySelf: 'end', alignSelf: 'stretch', display: copilotFolded ? 'none' : 'block' }}>{copilot}</Box>
                {copilotFolded && <BuilderPanelRail panel="copilot" title="Lesson copilot" icon={<AutoAwesomeRoundedIcon fontSize="small" />} attention={copilotAttention} />}
            </>}
        </Box></PlanRevisionContext.Provider></MaterialRevisionContext.Provider></ProcedureViewStateContext.Provider>
    );
}

type PlanNamedCardConfig = {
    title: string;
    description: string;
    placeholder: string;
    variant: 'plain' | 'checklist';
    accent?: 'warning';
};

function PlanContextCard({
    course,
    lesson,
    sections,
    saving,
    onAdd,
    onUpdate,
    onDelete,
}: {
    course: IVerbaScholaCourse | null;
    lesson: IVerbaScholaLesson;
    sections: IVerbaScholaLessonPlanSection[];
    saving: boolean;
    onAdd: (payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onUpdate: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onDelete: (sectionId: number) => Promise<void>;
}) {
    const details = [
        ['Course', course?.name ?? 'Class'],
        ['Course number', course?.course_number ?? 'Not set'],
        ['Semester', course?.semester ?? 'Not set'],
        ['Room', course?.room ?? 'Not set'],
        ['Meeting days', (course?.meeting_days ?? []).join(' / ') || 'Not set'],
        ['Duration', lesson.duration_minutes ? `${lesson.duration_minutes} minutes` : 'Not set'],
    ];
    return (
        <VerbaScholaCard>
            <Box sx={{ p: 3 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 17, mb: 2 }}>Class context</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.25 }}>
                    {details.map(([label, value]) => (
                        <Box key={label} sx={{ p: 1.5, borderRadius: '12px', backgroundColor: SURFACE_SUBTLE }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, letterSpacing: '0.04em' }}>{label.toUpperCase()}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 600, color: NAVY, fontSize: 13, mt: 0.5 }}>{value}</Typography>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25, mt: 1.25 }}>
                    <PlanContentCard
                        sectionType="context"
                        title="Prior knowledge"
                        description="What students already know before this lesson starts."
                        placeholder="Students know basic food vocabulary but need practice using it in complete requests."
                        variant="plain"
                        orderIndex={storedPlanSectionOrder.context}
                        section={sectionForCard(sections, 'Prior knowledge', 0)}
                        saving={saving}
                        onAdd={onAdd}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                    />
                    <PlanContentCard
                        sectionType="context"
                        title="Curriculum alignment"
                        description="Standards, curriculum goal, or unit objective this lesson supports."
                        placeholder="ACTFL Intermediate Mid - Interpersonal Communication: exchange information in familiar situations using sentences and questions."
                        variant="plain"
                        orderIndex={storedPlanSectionOrder.context + 0.01}
                        section={sectionForCard(sections, 'Curriculum alignment', 1)}
                        saving={saving}
                        onAdd={onAdd}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                    />
                </Box>
            </Box>
        </VerbaScholaCard>
    );
}

function PlanLanguageTargetsCard({
    onReviseTarget,
    vocabularyTargets,
    grammarTargets,
    sections,
    saving,
    onAdd,
    onUpdate,
    onDelete,
    onAddVocabulary,
    onUpdateVocabulary,
    onDeleteVocabulary,
    onAddGrammar,
    onUpdateGrammar,
    onDeleteGrammar,
}: {
    onReviseTarget?: (target_type: 'vocabulary_target' | 'grammar_target', target_id: number) => void;
    vocabularyTargets: IVerbaScholaLessonVocabularyTarget[];
    grammarTargets: IVerbaScholaLessonGrammarTarget[];
    sections: IVerbaScholaLessonPlanSection[];
    saving: boolean;
    onAdd: (payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onUpdate: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onDelete: (sectionId: number) => Promise<void>;
    onAddVocabulary: (payload: Partial<IVerbaScholaLessonVocabularyTarget>) => Promise<void>;
    onUpdateVocabulary: (targetId: number, payload: Partial<IVerbaScholaLessonVocabularyTarget>) => Promise<void>;
    onDeleteVocabulary: (targetId: number) => Promise<void>;
    onAddGrammar: (payload: Partial<IVerbaScholaLessonGrammarTarget>) => Promise<void>;
    onUpdateGrammar: (targetId: number, payload: Partial<IVerbaScholaLessonGrammarTarget>) => Promise<void>;
    onDeleteGrammar: (targetId: number) => Promise<void>;
}) {
    const [newVocab, setNewVocab] = React.useState({ hanzi: '', pinyin: '', meaning: '' });
    const [newGrammar, setNewGrammar] = React.useState({ pattern: '', meaning: '', example: '' });

    return (
        <VerbaScholaCard>
            <Box sx={{ p: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                    <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 17 }}>Language targets</Typography>
                    <Chip label="Manual edits save to the lesson targets" size="small" sx={{ fontFamily: INTER, color: SLATE, backgroundColor: SURFACE_MUTED }} />
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    <Box>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>VOCABULARY - {vocabularyTargets.length} ITEMS</Typography>
                        <Stack spacing={0.75}>
                            {vocabularyTargets.length === 0 && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13 }}>No vocabulary targets yet.</Typography>}
                            {vocabularyTargets.map(target => <VocabularyTargetRow key={target.id} onRevise={onReviseTarget ? () => onReviseTarget('vocabulary_target', target.id) : undefined} target={target} saving={saving} onSave={onUpdateVocabulary} onDelete={onDeleteVocabulary} />)}
                            <Box sx={{ p: 1.25, border: `1px dashed ${BORDER}`, borderRadius: '12px', backgroundColor: '#fff' }}>
                                <Stack spacing={1}>
                                    <ManualAddition label="Add your own vocabulary"><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <TextField label="Word" value={newVocab.hanzi} onChange={event => setNewVocab(value => ({ ...value, hanzi: event.target.value }))} fullWidth />
                                        <VerbaScholaPinyinField hanzi={newVocab.hanzi} value={newVocab.pinyin} onChange={pinyin => setNewVocab(value => ({ ...value, pinyin }))} />
                                    </Stack>
                                    <TextField label="Meaning" value={newVocab.meaning} onChange={event => setNewVocab(value => ({ ...value, meaning: event.target.value }))} fullWidth />
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddRoundedIcon />}
                                        disabled={saving || newVocab.hanzi.trim().length === 0}

                                            onClick={() => onAddVocabulary({ hanzi: newVocab.hanzi.trim(), pinyin: optionalText(newVocab.pinyin)?.split(/[,\s]+/).filter(Boolean) ?? null, meaning: optionalText(newVocab.meaning), source: 'added', is_review: false, order_index: vocabularyTargets.length + 1 }).then(() => setNewVocab({ hanzi: '', pinyin: '', meaning: '' })).catch(() => undefined)}
                                        sx={{ alignSelf: 'flex-start', fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}
                                    >
                                        Add vocabulary
                                    </Button></ManualAddition>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>GRAMMAR - {grammarTargets.length} POINTS</Typography>
                        <Stack spacing={0.75}>
                            {grammarTargets.length === 0 && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13 }}>No grammar targets yet.</Typography>}
                            {grammarTargets.map(target => <GrammarTargetRow key={target.id} onRevise={onReviseTarget ? () => onReviseTarget('grammar_target', target.id) : undefined} target={target} saving={saving} onSave={onUpdateGrammar} onDelete={onDeleteGrammar} />)}
                            <Box sx={{ p: 1.25, border: `1px dashed ${BORDER}`, borderRadius: '12px', backgroundColor: '#fff' }}>
                                <Stack spacing={1}>
                                    <ManualAddition label="Add your own grammar"><TextField label="Pattern" value={newGrammar.pattern} onChange={event => setNewGrammar(value => ({ ...value, pattern: event.target.value }))} fullWidth />
                                    <TextField label="Meaning" value={newGrammar.meaning} onChange={event => setNewGrammar(value => ({ ...value, meaning: event.target.value }))} fullWidth />
                                    <TextField label="Example" value={newGrammar.example} onChange={event => setNewGrammar(value => ({ ...value, example: event.target.value }))} fullWidth />
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddRoundedIcon />}
                                        disabled={saving || newGrammar.pattern.trim().length === 0}

                                            onClick={() => onAddGrammar({ pattern: newGrammar.pattern.trim(), meaning: optionalText(newGrammar.meaning), example: optionalText(newGrammar.example), source: 'added', order_index: grammarTargets.length + 1 }).then(() => setNewGrammar({ pattern: '', meaning: '', example: '' })).catch(() => undefined)}
                                        sx={{ alignSelf: 'flex-start', fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}
                                    >
                                        Add grammar
                                    </Button></ManualAddition>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mt: 2 }}>
                    <PlanContentCard
                        sectionType="language"
                        title="Pronunciation focus"
                        description="Sounds, tones, rhythm, or pronunciation patterns to emphasize."
                        placeholder="Tone changes, natural rhythm, or sound contrasts students should notice and practice."
                        variant="plain"
                        orderIndex={storedPlanSectionOrder.language}
                        section={sectionForCard(sections, 'Pronunciation focus', 0)}
                        saving={saving}
                        onAdd={onAdd}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                    />
                    <PlanContentCard
                        sectionType="language"
                        title="Culture & pragmatics"
                        description="Register, politeness, culture, or usage notes students need for the target situation."
                        placeholder="When to use formal address, how to address service staff, or what sounds natural in context."
                        variant="plain"
                        orderIndex={storedPlanSectionOrder.language + 0.01}
                        section={sectionForCard(sections, 'Culture & pragmatics', 1)}
                        saving={saving}
                        onAdd={onAdd}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                    />
                </Box>
            </Box>
        </VerbaScholaCard>
    );
}

function PlanNamedSectionGroup({
    sectionType,
    cards,
    sections,
    saving,
    onAdd,
    onUpdate,
    onDelete,
}: {
    sectionType: LessonPlanSectionType;
    cards: PlanNamedCardConfig[];
    sections: IVerbaScholaLessonPlanSection[];
    saving: boolean;
    onAdd: (payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onUpdate: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onDelete: (sectionId: number) => Promise<void>;
}) {
    const standalone = sectionType === 'support' || sectionType === 'assessment';
    const content = <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: cards.length === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {cards.map((card, index) => <PlanContentCard key={card.title} {...card} sectionType={sectionType}
            orderIndex={storedPlanSectionOrder[sectionType] + index / 100}
            section={sectionForCard(sections, card.title, index)} saving={saving} onAdd={onAdd} onUpdate={onUpdate} onDelete={onDelete} />)}
    </Box>;
    if (standalone) return content;
    return <VerbaScholaCard><Box sx={{ p: { xs: 2, sm: 3 } }}>{content}</Box></VerbaScholaCard>;
}

function PlanSectionActions({ section, saving, onSave }: { section: IVerbaScholaLessonPlanSection; saving: boolean; onSave: (id: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void> }) {
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const onRevise = React.useContext(PlanRevisionContext);
    if (readOnly) return null;
    return <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {section.title !== 'Teacher reflection' && <Button disabled={saving || !onRevise} onClick={() => onRevise?.(section.id)}>Revise with AI</Button>}
        <Button startIcon={section.readiness_status === 'ready' ? undefined : <CheckCircleRoundedIcon />} disabled={saving || (section.readiness_status !== 'ready' && !section.body?.trim())} onClick={() => void onSave(section.id, { readiness_status: section.readiness_status === 'ready' ? 'draft' : 'ready' }).catch(() => undefined)}>{section.readiness_status === 'ready' ? 'Return to draft' : 'Mark ready'}</Button>
    </Stack>;
}

function PlanContentCard({
    sectionType,
    title,
    description,
    placeholder,
    variant,
    accent,
    orderIndex,
    section,
    saving,
    onAdd,
    onUpdate,
    onDelete,
}: PlanNamedCardConfig & {
    sectionType: LessonPlanSectionType;
    orderIndex: number;
    section: IVerbaScholaLessonPlanSection | null;
    saving: boolean;
    onAdd: (payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onUpdate: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onDelete: (sectionId: number) => Promise<void>;
}) {
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const lines = splitBodyLines(section?.body);
    const [checking, setChecking] = React.useState(false);
    const [checkError, setCheckError] = React.useState<string | null>(null);
    const isPreparation = sectionType === 'preparation' && title === 'Preparation checklist';
    const standalone = sectionType === 'support' || sectionType === 'assessment';
    const contentTitle = section?.title ?? title;
    return (
        <Box data-testid={`plan-card-${sectionType}-${title}`} data-plan-card-title={title} data-plan-section-id={section?.id} tabIndex={-1} sx={{ ...planContentCardSx, scrollMarginTop: '100px', p: standalone ? 2.5 : sectionType === 'outcomes' || isPreparation ? 0 : 1.5,
            borderRadius: standalone ? '20px' : '12px', border: standalone ? 'none' : sectionType === 'outcomes' || isPreparation ? 'none' : `1px solid ${BORDER}`,
            boxShadow: standalone ? '0 4px 12px rgba(22, 58, 99, 0.08)' : 'none',
            backgroundColor: title === 'Curriculum alignment' || title === 'Pronunciation focus' ? SOFT_BLUE : title === 'Culture & pragmatics' ? WARNING_SOFT : standalone || sectionType === 'outcomes' || isPreparation ? '#fff' : SURFACE_TINT,
            borderLeft: accent === 'warning' ? `4px solid ${YELLOW}` : undefined,
            gridColumn: title === 'Teacher reflection' ? '1 / -1' : undefined }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: sectionType === 'outcomes' || isPreparation ? 17 : standalone ? 16 : 13 }}>{contentTitle}</Typography>
                    {contentTitle === title && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, mt: 0.25 }}>{description}</Typography>}
                </Box>
                {section && <SmallSourceChip source={section.authoring_source} />}
                {section && <Chip label={titleCase(section.readiness_status ?? 'draft')} size="small" sx={statusChipSx(section.readiness_status ?? 'draft')} />}
                {(!readOnly || (reviewEdit && section)) && <IconButton size="small" aria-label={`Edit ${contentTitle}`} onClick={() => setDialogOpen(true)} sx={{ color: BLUE, width: 44, height: 44 }}><EditRoundedIcon fontSize="small" /></IconButton>}
            </Stack>
            {checkError && <Alert severity="error">{checkError}</Alert>}
            {lines.length === 0 ? (
                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13, lineHeight: 1.55 }}>Not drafted yet.</Typography>
            ) : isPreparation && section ? (
                <Stack spacing={1}>
                    {preparationChecklistItems(section.body).map(item => item.heading ? (
                        <LessonText key={item.lineIndex} sx={{ fontFamily: INTER, fontWeight: 600, color: NAVY, fontSize: 12.5, lineHeight: 1.45 }}>{item.text}</LessonText>
                    ) : (
                        <Stack key={item.lineIndex} component="label" direction="row" spacing={1} alignItems="flex-start" sx={{ cursor: saving || checking ? 'default' : 'pointer' }}>
                            <Checkbox checked={item.checked} disabled={saving || checking} size="small"
                                sx={{ p: 0, mt: 0.15 }} onChange={async event => {
                                    const checked = event.target.checked;
                                    setChecking(true); setCheckError(null);
                                    try { await onUpdate(section.id, { body: togglePreparationItem(section.body ?? '', item.lineIndex, checked) }); }
                                    catch { setCheckError('Could not save checklist completion. Please try again.'); }
                                    finally { setChecking(false); }
                                }} />
                            <LessonText sx={{ fontFamily: INTER, color: NAVY, fontSize: 12.5, lineHeight: 1.45 }}>{item.text}</LessonText>
                        </Stack>
                    ))}
                    <Button disabled={saving || checking || !preparationChecklistItems(section.body).some(item => item.checked)}
                        sx={{ alignSelf: 'flex-start' }} onClick={async () => {
                            setChecking(true); setCheckError(null);
                            try { await onUpdate(section.id, { body: resetPreparationChecklist(section.body ?? '') }); }
                            catch { setCheckError('Could not reset the checklist. Please try again.'); }
                            finally { setChecking(false); }
                        }}>Reset checklist</Button>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11 }}>Completion saves in this lesson and carries over when duplicated. Changes make existing AI proposals out of date. Mark ready tracks review of the whole section.</Typography>
                </Stack>
            ) : sectionType === 'procedure' ? (
                <VerbaScholaProcedureSteps sectionId={section?.id ?? 0} body={section?.body} saving={saving} onSave={body => section ? onUpdate(section.id, { body }) : Promise.resolve()} />
            ) : variant === 'checklist' ? (
                <Stack spacing={1}>
                    {lines.map(line => (
                        <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                            <CheckRoundedIcon sx={{ color: GREEN, fontSize: 17, mt: 0.15 }} />
                            <LessonText sx={{ fontFamily: INTER, color: NAVY, fontSize: 12.5, lineHeight: 1.45 }}>{line}</LessonText>
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <LessonText sx={{ fontFamily: INTER, color: NAVY, fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{section?.body}</LessonText>
            )}
            {section && <PlanSectionActions section={section} saving={saving} onSave={onUpdate} />}
            <PlanSectionEditDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                sectionType={sectionType}
                title={contentTitle}
                placeholder={placeholder}
                section={section}
                saving={saving}
                orderIndex={orderIndex}
                showDuration={false}
                onAdd={onAdd}
                onUpdate={onUpdate}
                onDelete={onDelete}
            />
        </Box>
    );
}

function PlanSectionEditDialog({
    open,
    onClose,
    sectionType,
    title,
    placeholder,
    section,
    saving,
    orderIndex,
    showDuration,
    editableTitle = false,
    onAdd,
    onUpdate,
    onDelete,
}: {
    open: boolean;
    onClose: () => void;
    sectionType: LessonPlanSectionType;
    title: string;
    placeholder: string;
    section: IVerbaScholaLessonPlanSection | null;
    saving: boolean;
    orderIndex: number;
    showDuration: boolean;
    editableTitle?: boolean;
    onAdd: (payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onUpdate: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onDelete: (sectionId: number) => Promise<void>;
}) {
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const saveLabel = useReviewSaveLabel('Save');
    const [sectionTitle, setSectionTitle] = React.useState(section?.title ?? title);
    const [body, setBody] = React.useState(section?.body ?? '');
    const [durationMinutes, setDurationMinutes] = React.useState(section?.duration_minutes?.toString() ?? '');

    React.useEffect(() => {
        if (open) {
            setSectionTitle(section?.title ?? title);
            setBody(section?.body ?? '');
            setDurationMinutes(section?.duration_minutes?.toString() ?? '');
        }
    }, [open, section, title]);

    function saveSection(markReady = false) {
        const payload: Partial<IVerbaScholaLessonPlanSection> = {
            title: editableTitle ? sectionTitle.trim() : title,
            body: optionalText(body),
            duration_minutes: showDuration ? optionalNumber(durationMinutes) : (section?.duration_minutes ?? null),
            ...(markReady ? { readiness_status: 'ready' } : {}),
            section_type: sectionType,
        };
        const promise = section ? onUpdate(section.id, payload) : onAdd({ ...payload, order_index: orderIndex });
        return promise.then(onClose);
    }

    function deleteSection() {
        if (!section) return Promise.resolve();
        return onDelete(section.id).then(onClose);
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>{section ? `Edit ${title}` : `Draft ${title}`}</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ pt: 1 }}>
                    {editableTitle && <TextField disabled={saving} label="Stage title" value={sectionTitle} onChange={event => setSectionTitle(event.target.value)} fullWidth />}
                    <TextField disabled={saving} label={title} value={body} onChange={event => setBody(event.target.value)} placeholder={placeholder} fullWidth multiline minRows={7} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        {showDuration && <TextField disabled={saving} label="Minutes" value={durationMinutes} onChange={event => setDurationMinutes(event.target.value.replace(/[^\d]/g, '').slice(0, 3))} fullWidth />}
                        <SmallSourceChip source={section?.authoring_source ?? 'teacher'} />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                {section && !readOnly && <VerbaScholaConfirmDeleteButton label="Delete plan section" disabled={saving} onConfirm={deleteSection} sx={{ mr: 'auto' }} />}
                <Button disabled={saving} onClick={onClose} sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, textTransform: 'none' }}>Cancel</Button>
                {!readOnly && <Button disabled={saving || !body.trim() || (editableTitle && !sectionTitle.trim())} onClick={() => saveSection(true)}>Save and mark ready</Button>}
                <Button variant="contained" disabled={saving || (editableTitle && sectionTitle.trim().length === 0)} onClick={() => saveSection()} sx={{ ...primaryButtonSx, px: 2.75 }}>{saveLabel}</Button>
            </DialogActions>
        </Dialog>
    );
}

function PlanSectionList({ sections, saving, onAdd, onUpdate, onDelete }: {
    sections: IVerbaScholaLessonPlanSection[];
    saving: boolean;
    onAdd: (payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onUpdate: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>;
    onDelete: (sectionId: number) => Promise<void>
}) {
    const [newSectionTitle, setNewSectionTitle] = React.useState('');
    const [newSectionBody, setNewSectionBody] = React.useState('');
    const [newSectionDurationMinutes, setNewSectionDurationMinutes] = React.useState('');
    const stages = sections.filter(section => section.title !== 'Check for understanding');
    const checks = sections.find(section => section.title === 'Check for understanding') ?? null;
    const stepCount = stages.reduce((count, section) => count + planProcedureSteps(section.body).length, 0);

    return (
        <VerbaScholaCard>
            <VerbaScholaCardHeader title="Classroom procedure" meta={`${stepCount} ${stepCount === 1 ? 'step' : 'steps'}`} />
            {stages.length === 0 ? <VerbaScholaEmptyState message="This lesson has no plan sections yet." /> : (
                <Stack divider={<Box sx={{ height: '1px', backgroundColor: BORDER }} />}>
                    {stages.map((section, index) => <PlanSectionRow key={section.id} index={index} section={section} saving={saving} onSave={onUpdate} onDelete={onDelete} />)}
                </Stack>
            )}
            <Divider />
            <Box sx={{ p: 3 }}><PlanContentCard sectionType="procedure" title="Check for understanding"
                description="Observable questions and checks during the classroom tasks." placeholder="What will you ask or observe to confirm understanding?"
                variant="plain" orderIndex={storedPlanSectionOrder.procedure + 0.99} section={checks} saving={saving}
                onAdd={onAdd} onUpdate={onUpdate} onDelete={onDelete} /></Box>
            <ManualAddition label="Add classroom stage"><Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.75 }, backgroundColor: SURFACE_TINT }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15 }}>Stage details</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField label="Section title" value={newSectionTitle} onChange={event => setNewSectionTitle(event.target.value)} fullWidth />
                    <TextField label="Minutes" value={newSectionDurationMinutes} onChange={event => setNewSectionDurationMinutes(event.target.value.replace(/[^\d]/g, '').slice(0, 3))} sx={{ width: { xs: '100%', sm: 130 } }} />
                </Stack>
                <TextField label="Body" value={newSectionBody} onChange={event => setNewSectionBody(event.target.value)} fullWidth multiline minRows={3} />
                <Button
                    variant="outlined"
                    startIcon={<AddRoundedIcon />}
                    disabled={saving || newSectionTitle.trim().length === 0}

                        onClick={() => onAdd({ title: newSectionTitle.trim(), body: optionalText(newSectionBody), duration_minutes: optionalNumber(newSectionDurationMinutes), readiness_status: 'draft', section_type: 'procedure', order_index: storedPlanSectionOrder.procedure + sections.length / 100 }).then(() => { setNewSectionTitle(''); setNewSectionBody(''); setNewSectionDurationMinutes(''); })}
                    sx={{ alignSelf: 'flex-start', fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}
                >
                    Add stage
                </Button>
            </Stack></ManualAddition>
        </VerbaScholaCard>
    );
}

function PlanSectionRow({ section, index, saving, onSave, onDelete }: { section: IVerbaScholaLessonPlanSection; index: number; saving: boolean; onSave: (sectionId: number, payload: Partial<IVerbaScholaLessonPlanSection>) => Promise<void>; onDelete: (sectionId: number) => Promise<void> }) {
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const [expanded, setExpanded] = React.useState(index === 0 || readOnly);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    return (
        <Box data-plan-section-id={section.id} data-plan-card-title={section.title} tabIndex={-1} onFocus={event => { if (event.target === event.currentTarget) setExpanded(true); }} sx={{ scrollMarginTop: '100px' }}>
            {section.title === 'Classroom procedure' ? <Stack data-testid="classroom-procedure-steps" spacing={1.5} sx={{ p: 3 }}>
                <VerbaScholaProcedureSteps sectionId={section.id} body={section.body} saving={saving} onSave={body => onSave(section.id, { body })} />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <SmallSourceChip source={section.authoring_source} />
                    <Chip label={titleCase(section.readiness_status ?? 'draft')} size="small" sx={statusChipSx(section.readiness_status ?? 'draft')} />
                    <PlanSectionActions section={section} saving={saving} onSave={onSave} />
                    {(!readOnly || reviewEdit) && <Button startIcon={<EditRoundedIcon />} onClick={() => setDialogOpen(true)} sx={{ color: BLUE, fontFamily: INTER, fontSize: 12 }}>Edit procedure</Button>}
                </Stack>
            </Stack> : <>
            <Box onClick={() => setExpanded(open => !open)} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '11px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE, color: BLUE_TEXT, fontFamily: POPPINS, fontWeight: 600, fontSize: 13, flexShrink: 0 }}>{index + 1}</Box>
                <Box sx={{ flex: { xs: '1 1 calc(100% - 90px)', sm: 1 }, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15 }}>{section.title}</Typography>
                    <LessonText noWrap sx={{ fontFamily: INTER, color: SLATE, fontSize: 13, mt: 0.25 }}>{section.body || 'No notes yet.'}</LessonText>
                </Box>
                <SmallSourceChip source={section.authoring_source} />
                <Chip label={titleCase(section.readiness_status ?? 'draft')} size="small" sx={statusChipSx(section.readiness_status ?? 'draft')} />
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, color: SLATE, fontSize: 13, whiteSpace: 'nowrap' }}>{section.duration_minutes ?? 0} min</Typography>
                {(!readOnly || reviewEdit) && <IconButton
                    aria-label={`Edit ${section.title}`}
                    onClick={event => {
                        event.stopPropagation();
                        setDialogOpen(true);
                    }}
                    sx={{ color: BLUE }}
                >
                    <EditRoundedIcon fontSize="small" />
                </IconButton>}
                <ExpandMoreRoundedIcon sx={{ color: SLATE, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
            </Box>
            <Collapse in={expanded}>
                <Stack spacing={1.5} sx={{ px: 3, pb: 3 }}>
                    <Divider />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11 }}>TEACHER ACTIONS</Typography>
                            <Box sx={{ mt: 1 }}><VerbaScholaProcedureSteps sectionId={section.id} body={section.body} saving={saving} onSave={body => onSave(section.id, { body })} /></Box>
                        </Box>

                    </Box>
                    <PlanSectionActions section={section} saving={saving} onSave={onSave} />
                    {(!readOnly || reviewEdit) && <Button startIcon={<EditRoundedIcon />} onClick={() => setDialogOpen(true)} sx={{ alignSelf: 'flex-start', color: BLUE, fontFamily: INTER, fontSize: 12, px: 0 }}>Edit this stage</Button>}
                </Stack>
            </Collapse>
            </>}
            <PlanSectionEditDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                sectionType="procedure"
                title={section.title}
                placeholder="Teacher actions, student actions, language practiced, and check-for-understanding notes."
                section={section}
                saving={saving}
                orderIndex={section.order_index ?? storedPlanSectionOrder.procedure + index / 100}
                showDuration
                editableTitle
                onAdd={() => Promise.resolve()}
                onUpdate={onSave}
                onDelete={onDelete}
            />
        </Box>
    );
}

function GeneratedMaterialList({ materials, saving, onAdd, onUpdate, onDelete }: {
    materials: IVerbaScholaLessonGeneratedMaterial[];
    saving: boolean;
    onAdd: (payload: Partial<IVerbaScholaLessonGeneratedMaterial>) => Promise<void>;
    onUpdate: (materialId: number, payload: Partial<IVerbaScholaLessonGeneratedMaterial>) => Promise<void>;
    onDelete: (materialId: number) => Promise<void>
}) {
    const [title, setTitle] = React.useState('');
    const [materialFormat, setMaterialFormat] = React.useState('');
    const [bodyText, setBodyText] = React.useState('');
    const readyCount = materials.filter(material => material.status === 'ready').length;
    return (
        <VerbaScholaCard>
            <VerbaScholaCardHeader
                title="Teaching materials"
                meta={`${materials.length} ${materials.length === 1 ? 'material' : 'materials'}`}
                actions={materials.length > 0 ? <Chip icon={<CheckRoundedIcon />} label={`${readyCount} of ${materials.length} ready`} size="small" sx={{ fontFamily: INTER, fontWeight: 600, color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT, '& .MuiChip-icon': { color: GREEN } }} /> : undefined}
            />
            <Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.75 } }}>
                {materials.length === 0 && <VerbaScholaEmptyState message="This lesson has no materials yet." />}
                {materials.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                        {materials.map(material => <GeneratedMaterialRow key={material.id} material={material} saving={saving} onSave={onUpdate} onDelete={onDelete} />)}
                    </Box>
                )}
                <ManualAddition label="Add your own material"><Box sx={{ p: 1.75, borderRadius: '14px', border: `1px solid ${BORDER}`, backgroundColor: SURFACE_TINT }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15, mb: 1.5 }}>Add material</Typography>
                    <Stack spacing={1.25}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField label="Title" value={title} onChange={event => setTitle(event.target.value)} fullWidth />
                            <TextField label="Format" value={materialFormat} onChange={event => setMaterialFormat(event.target.value)} fullWidth />
                        </Stack>
                        <TextField label="Body text" value={bodyText} onChange={event => setBodyText(event.target.value)} fullWidth multiline minRows={3} />
                        <Button
                            variant="outlined"
                            startIcon={<AddRoundedIcon />}
                            disabled={saving || title.trim().length === 0}

                                onClick={() => onAdd({ title: title.trim(), material_format: optionalText(materialFormat), body_text: optionalText(bodyText), asset_s3_key: null, status: 'draft', order_index: materials.length + 1 }).then(() => { setTitle(''); setMaterialFormat(''); setBodyText(''); })}
                            sx={{ alignSelf: 'flex-start', fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}
                        >
                            Add material
                        </Button>
                    </Stack>
                </Box></ManualAddition>
            </Stack>
        </VerbaScholaCard>
    );
}

function GeneratedMaterialRow({ material, saving, onSave, onDelete }: { material: IVerbaScholaLessonGeneratedMaterial; saving: boolean; onSave: (materialId: number, payload: Partial<IVerbaScholaLessonGeneratedMaterial>) => Promise<void>; onDelete: (materialId: number) => Promise<void> }) {
    const onRevise = React.useContext(MaterialRevisionContext);
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const [viewOpen, setViewOpen] = React.useState(false);
    const [editOpen, setEditOpen] = React.useState(false);

    return (
        <Box role="region" aria-label={`Material: ${material.title}`} data-plan-material-id={material.id} data-plan-material-title={material.title} tabIndex={-1} sx={{ scrollMarginTop: '100px', border: `1px solid ${BORDER}`, borderRadius: '16px', overflow: 'hidden', backgroundColor: '#fff' }}>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', px: 1.75, py: 1.25, backgroundColor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '9px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}><AutoAwesomeRoundedIcon sx={{ color: BLUE, fontSize: 15 }} /></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 13 }}>{material.title}</Typography>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5 }}>{material.material_format || 'Material'}</Typography>
                </Box>
                <SmallSourceChip source={material.authoring_source} />
                    <Chip label={titleCase(material.status ?? 'draft')} size="small" sx={statusChipSx(material.status ?? 'draft')} />
            </Stack>
            <Box sx={{ p: 1.75, minHeight: 116, backgroundColor: SURFACE_TINT }}>
                <LessonText sx={{ fontFamily: INTER, color: SLATE, fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{material.body_text || 'No material content yet.'}</LessonText>
            </Box>
            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', px: 1.25, pb: 1.25 }}>
                <Button size="small" startIcon={<VisibilityRoundedIcon />} onClick={() => setViewOpen(true)} sx={{ minHeight: 44, minWidth: 0, color: BLUE, fontFamily: INTER, fontSize: 11.5 }}>View</Button>
                {(!readOnly || reviewEdit) && <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => setEditOpen(true)} sx={{ minHeight: 44, minWidth: 0, color: BLUE, fontFamily: INTER, fontSize: 11.5 }}>Edit</Button>}
                {!readOnly && <>
                <Button size="small" startIcon={material.status === 'ready' ? undefined : <CheckCircleRoundedIcon />} disabled={saving || (material.status !== 'ready' && !material.body_text?.trim())} onClick={() => void onSave(material.id, { status: material.status === 'ready' ? 'draft' : 'ready' }).catch(() => undefined)} sx={{ minHeight: 44 }}>{material.status === 'ready' ? 'Return to draft' : 'Mark ready'}</Button>
                <Button size="small" disabled={saving || !onRevise} onClick={() => onRevise?.(material.id)} startIcon={<AutoAwesomeRoundedIcon />} sx={{ minHeight: 44, minWidth: 0, fontFamily: INTER, fontSize: 11.5 }}>Revise with AI</Button></>}
            </Stack>
            <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>{material.title}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>{material.material_format || 'Material'}</Typography>
                    <LessonText sx={{ fontFamily: INTER, color: NAVY, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{material.body_text || 'No material content yet.'}</LessonText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setViewOpen(false)} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>
            <GeneratedMaterialEditDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                material={material}
                saving={saving}
                onSave={onSave}
                onDelete={onDelete}
            />
        </Box>
    );
}

function GeneratedMaterialEditDialog({ open, onClose, material, saving, onSave, onDelete }: {
    open: boolean;
    onClose: () => void;
    material: IVerbaScholaLessonGeneratedMaterial;
    saving: boolean;
    onSave: (materialId: number, payload: Partial<IVerbaScholaLessonGeneratedMaterial>) => Promise<void>;
    onDelete: (materialId: number) => Promise<void>
}) {
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const saveLabel = useReviewSaveLabel('Save');
    const [title, setTitle] = React.useState(material.title);
    const [materialFormat, setMaterialFormat] = React.useState(material.material_format ?? '');
    const [bodyText, setBodyText] = React.useState(material.body_text ?? '');

    React.useEffect(() => {
        if (open) {
            setTitle(material.title);
            setMaterialFormat(material.material_format ?? '');
            setBodyText(material.body_text ?? '');
        }
    }, [material, open]);

    function saveMaterial(markReady = false) {
        return onSave(material.id, { title: title.trim(), material_format: optionalText(materialFormat), body_text: optionalText(bodyText), ...(markReady ? { status: 'ready' } : {}) }).then(onClose);
    }

    function deleteMaterial() {
        return onDelete(material.id).then(onClose);
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Edit material</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ pt: 1 }}>
                    <TextField disabled={saving} label="Title" value={title} onChange={event => setTitle(event.target.value)} fullWidth />
                    <TextField disabled={saving} label="Format" value={materialFormat} onChange={event => setMaterialFormat(event.target.value)} fullWidth />
                    <TextField disabled={saving} label="Body text" value={bodyText} onChange={event => setBodyText(event.target.value)} fullWidth multiline minRows={6} />
                    <SmallSourceChip source={material.authoring_source} />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                {!readOnly && <VerbaScholaConfirmDeleteButton label="Delete material" disabled={saving} onConfirm={deleteMaterial} sx={{ mr: 'auto' }} />}
                <Button disabled={saving} onClick={onClose} sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, textTransform: 'none' }}>Cancel</Button>
                {!readOnly && <Button disabled={saving || !title.trim() || !bodyText.trim()} onClick={() => saveMaterial(true)}>Save and mark ready</Button>}
                <Button variant="contained" disabled={saving || title.trim().length === 0} onClick={() => saveMaterial()} sx={{ ...primaryButtonSx, px: 2.75 }}>{saveLabel}</Button>
            </DialogActions>
        </Dialog>
    );
}

function orderedExerciseSteps(rows: IVerbaScholaLessonExerciseStep[]) {
    return [...rows].sort((a, b) => (a.order_index ?? -1) - (b.order_index ?? -1) || a.id - b.id);
}

/** A session opened from the outline, or from a review finding that names it. */
export type ExerciseSessionSelection = { number: number; title?: string; detail?: string; suggestion?: string | null };

const activityKindLabel: Record<string, string> = { quiz_question: 'quiz', speak_with_grade: 'speaking', type_speak: 'typed answer', conversation_listen: 'listening dialogue' };
const MAX_SESSION_NUMBER = 200;

/** Every session the outline shows, and the session each exercise is in; an unnumbered exercise joins the preceding one. */
function exerciseSessionLayout(exercises: IVerbaScholaLessonExercise[], practiceSessions: IVerbaScholaPracticeSession[], targetCoverage?: IVerbaScholaTargetCoverage) {
    const sessionNumbers = [...new Set([...practiceSessions.map(session => session.number),
        ...(targetCoverage?.sessions.map(session => session.session_number) ?? []),
        ...exercises.map(exercise => exercise.session_number).filter((number): number is number => number !== null && number !== undefined)])].sort((a, b) => a - b);
    const resolvedSessions = new Map<number, number>();
    let precedingSession = exercises.find(exercise => exercise.session_number != null)?.session_number ?? practiceSessions[0]?.number;
    for (const exercise of exercises) {
        const previewSession = practiceSessions.find(session => session.items.some(item => item.exercise_id === exercise.id))?.number;
        precedingSession = exercise.session_number ?? previewSession ?? precedingSession;
        if (precedingSession != null) resolvedSessions.set(exercise.id, precedingSession);
    }
    return { sessionNumbers, resolvedSessions };
}

export type ExerciseLayoutItem = { id: number; session_number: number | null };

/**
 * The complete saved layout that puts an exercise at `index` among the other exercises of a
 * session, or null when it is already there. `exercises` is in saved display order. Every
 * exercise is sent with the session the outline shows: an unnumbered legacy exercise belongs
 * to the session before it, so saving only the moved one could silently regroup a neighbour.
 * The server numbers the list from 1, so equal saved orders never block a move. The saved order
 * is grouped by session, as the outline shows it, so exercise numbers run through the sessions
 * even where older saved orders interleaved them.
 */
function exerciseLayoutAfterMove(exercises: IVerbaScholaLessonExercise[], resolvedSessions: Map<number, number>,
    exerciseId: number, sessionNumber: number, index: number): ExerciseLayoutItem[] | null {
    const moving = exercises.find(row => row.id === exerciseId);
    if (!moving || sessionNumber < 1 || sessionNumber > MAX_SESSION_NUMBER) return null;
    const others = exercises.filter(row => row.id !== exerciseId);
    const members = others.filter(row => resolvedSessions.get(row.id) === sessionNumber);
    const position = Math.max(0, Math.min(index, members.length));
    // Unchanged when the exercise already sits at this point of this session.
    if (resolvedSessions.get(exerciseId) === sessionNumber
        && exercises.filter(row => resolvedSessions.get(row.id) === sessionNumber).indexOf(moving) === position) return null;
    const insertAt = position < members.length ? others.indexOf(members[position])
        : members.length ? others.indexOf(members[members.length - 1]) + 1 : others.length;
    const session = (row: IVerbaScholaLessonExercise) => row.id === exerciseId ? sessionNumber : resolvedSessions.get(row.id);
    // A stable sort by session keeps each session's own order, with the moved exercise at `position`.
    const ordered = [...others.slice(0, insertAt), moving, ...others.slice(insertAt)]
        .map((row, at) => ({ row, at, number: session(row) ?? Number.MAX_SAFE_INTEGER }))
        .sort((a, b) => a.number - b.number || a.at - b.at).map(({ row }) => row);
    return ordered.map(row => ({ id: row.id, session_number: session(row) ?? row.session_number ?? null }));
}

/**
 * The layout that moves a whole session to `index` among the other sessions. The session
 * numbers themselves stay in place and are reassigned by position, so the vocabulary plan,
 * which follows the session number, stays with the number rather than the moved exercises.
 */
function sessionLayoutAfterMove(exercises: IVerbaScholaLessonExercise[], resolvedSessions: Map<number, number>,
    sessionNumbers: number[], moving: number, index: number): { layout: ExerciseLayoutItem[]; renumber: Map<number, number> } | null {
    if (!sessionNumbers.includes(moving)) return null;
    const others = sessionNumbers.filter(number => number !== moving);
    const position = Math.max(0, Math.min(index, others.length));
    const order = [...others.slice(0, position), moving, ...others.slice(position)];
    if (order.every((number, at) => number === sessionNumbers[at])) return null;
    const renumber = new Map(order.map((old, at) => [old, sessionNumbers[at]]));
    const next = (row: IVerbaScholaLessonExercise) => {
        const session = resolvedSessions.get(row.id);
        return session === undefined ? undefined : renumber.get(session) ?? session;
    };
    // Group by the new numbers, keeping each session's own exercise order.
    const ordered = exercises.map((row, at) => ({ row, at, session: next(row) }))
        .sort((a, b) => (a.session ?? Number.MAX_SAFE_INTEGER) - (b.session ?? Number.MAX_SAFE_INTEGER) || a.at - b.at);
    return { layout: ordered.map(({ row, session }) => ({ id: row.id, session_number: session ?? row.session_number ?? null })), renumber };
}

/** Width of the Exercises lesson-structure column from the md breakpoint up. */
const EXERCISE_STRUCTURE_WIDTH = 240;

/** Review markers for the exercise outline: new and moved activities, new exercises, and saved activities the version on screen removes. */
export interface ReviewOutlineMarks {
    steps: Record<number, string>;
    exercises: Record<number, string>;
    removed: { exerciseId: number; id: number; title: string; position: number }[];
    /** Shows a removed activity as saved: its removal, in Before. */
    onViewOriginal: (stepId: number) => void;
}
const ReviewOutlineMarksContext = React.createContext<ReviewOutlineMarks | null>(null);

export function ExercisesEditor({
    copilot, copilotOpen, onCopilotOpenChange, reviewOpen = false, contentVersion = 0, initialViewMode = 'edit', onReviseExercise, readinessDisabled = false,
    readOnly = false, reviewEditable = false, reviewBar, studentSteps, reviewExerciseField, reviewMarks = null,
    copilotImageBusy = false, copilotAttention,
    renderStep, renderReviewStep,
    lesson,
    course,
    exercises,
    practiceSessions = [],
    targetCoverage,
    practiceTargets = [],
    onSavePracticeTargets,
    onFillCoverageGaps,
    steps,
    choices,
    sentences,
    conversations,
    vocabularyTargets,
    grammarTargets,
    selectedExerciseId,
    selectedStepId,
    selectedSessionReview,
    reviewTextFocus,
    saving,
    onSelectExercise,
    onSelectSession,
    onSelectStep,
    onBack,
    lessonActions,
    lessonActionsNote,
    markableExerciseCount,
    readyExerciseCount,
    markAllReadyBlockReason,
    onMarkAllReady,
    onAddExercise,
    onUpdateExercise,
    onArrangeExercises,
    onReorderSteps,
    onDeleteExercise,
    onAddStep,
    onUpdateStep,
    onDeleteStep,
    onChildMutation,
    onMarkDirty,
    onCancelScope,
    authoringRevision,
}: {
    copilot: React.ReactNode;
    copilotOpen?: boolean;
    onCopilotOpenChange?: (open: boolean) => void;
    reviewOpen?: boolean;
    contentVersion?: number | string;
    initialViewMode?: 'edit' | 'preview';
    readinessDisabled?: boolean;
    /** A copilot picture request is running; its preview stays open until the request finishes. */
    copilotImageBusy?: boolean;
    /** Why a folded copilot needs a look, marked on its rail. */
    copilotAttention?: string | null;
    /** Shows a lesson version for proposal review: no edits, no dirty state. */
    readOnly?: boolean;
    /** With ``readOnly``: the review shows Your version, whose content the teacher may edit. */
    reviewEditable?: boolean;
    /** Proposal review controls above the exercise content. */
    reviewBar?: React.ReactNode;
    /** Student views of proposed steps, by step id, from a review projection. */
    studentSteps?: Record<string, IVerbaScholaStudentStep>;
    /** The exercise field a proposal review shows (``settings`` for its other settings), so a collapsed section holding it opens. */
    reviewExerciseField?: { exerciseId: number; field: string } | null;
    /** Markers for the outline while a review shows a proposed version. */
    reviewMarks?: ReviewOutlineMarks | null;
    onReviseExercise?: (id: number) => void;
    renderStep?: (step: IVerbaScholaLessonExerciseStep) => React.ReactNode;
    /** Your version under review: an activity's editor whose save keeps its edits in the review draft (null: it shows locked). */
    renderReviewStep?: (step: IVerbaScholaLessonExerciseStep) => React.ReactNode;
    lesson: IVerbaScholaLesson;
    course: IVerbaScholaCourse | null;
    exercises: IVerbaScholaLessonExercise[];
    practiceSessions?: IVerbaScholaPracticeSession[];
    targetCoverage?: IVerbaScholaTargetCoverage;
    practiceTargets?: IVerbaScholaExercisePracticeTargets[];
    onSavePracticeTargets?: (exerciseId: number, vocabularyTargetIds: number[], grammarTargetIds: number[]) => Promise<void>;
    onFillCoverageGaps?: () => void;
    steps: IVerbaScholaLessonExerciseStep[];
    choices: IVerbaScholaExerciseChoice[];
    sentences: IVerbaScholaSpeakingSentence[];
    conversations: IVerbaScholaExerciseConversation[];
    vocabularyTargets: IVerbaScholaLessonVocabularyTarget[];
    grammarTargets: IVerbaScholaLessonGrammarTarget[];
    selectedExerciseId: number | null;
    selectedStepId: number | null;
    selectedSessionReview?: ExerciseSessionSelection | null;
    reviewTextFocus?: { stepId: number; issueId: number; message: string; key: number } | null;
    saving: boolean;
    onSelectExercise: (exerciseId: number | null) => void;
    /** Opens a session's overview in the main panel. */
    onSelectSession?: (sessionNumber: number) => void;
    onSelectStep: (exerciseId: number, stepId: number) => void;
    onBack: () => void;
    /** Save, exit and publication buttons for the lesson's current state. */
    lessonActions: React.ReactNode;
    /** Why publication is unavailable, shown beside the actions. */
    lessonActionsNote?: string | null;
    markableExerciseCount: number;
    readyExerciseCount: number;
    markAllReadyBlockReason?: string | null;
    onMarkAllReady: () => void;
    onAddExercise: (payload: Partial<IVerbaScholaLessonExercise>) => Promise<void>;
    onUpdateExercise: (exerciseId: number, payload: Partial<IVerbaScholaLessonExercise>, localError?: boolean) => Promise<void>;
    /** Saves every exercise's order and session in one revision. */
    onArrangeExercises: (layout: ExerciseLayoutItem[]) => Promise<void>;
    /** Saves one exercise's complete step order in one revision. */
    onReorderSteps: (exerciseId: number, orderedIds: number[]) => Promise<void>;
    onDeleteExercise: (exerciseId: number) => Promise<void>;
    onAddStep: (payload: Partial<IVerbaScholaLessonExerciseStep>) => Promise<void>;
    onUpdateStep: (stepId: number, payload: Partial<IVerbaScholaLessonExerciseStep>, scope?: string) => Promise<void>;
    onDeleteStep: (stepId: number) => Promise<void>;
    onChildMutation: (operation: () => Promise<unknown>, scope: string) => Promise<void>;
    onMarkDirty?: (scope: string) => void;
    onCancelScope?: (scope: string) => void;
    authoringRevision: number;
}) {
    const selectedExercise = exercises.find(exercise => exercise.id === selectedExerciseId) ?? null;
    const selectedSteps = orderedExerciseSteps(steps.filter(step => step.verbaschola_lesson_exercise_id === selectedExerciseId));
    const activeStep = selectedSteps.find(step => step.id === selectedStepId) ?? selectedSteps[0] ?? null;
    const selectedSession = practiceSessions.find(session => session.items.some(item => item.exercise_id === selectedExerciseId));
    const [viewMode, setViewMode] = React.useState<'edit' | 'preview'>(initialViewMode);
    const theme = useTheme();
    const compactStructure = useMediaQuery(theme.breakpoints.down('md'));
    const compactCopilot = useMediaQuery(theme.breakpoints.down('lg'));
    const [structureOpen, setStructureOpen] = React.useState(false);
    const [localCopilotOpen, setLocalCopilotOpen] = React.useState(false);
    const isCopilotOpen = copilotOpen ?? localCopilotOpen;
    const setCopilotOpen = onCopilotOpenChange ?? setLocalCopilotOpen;
    // Wide-screen columns fold into rails; the content's width cap grows by the width each fold gives back.
    const { open: panelsOpen, setOpen: setPanelOpen } = useBuilderPanels();
    const structureFolded = !compactStructure && !panelsOpen.structure;
    const copilotFolded = !compactCopilot && !panelsOpen.copilot;
    const railColumn = BUILDER_RAIL_WIDTH + 12;
    const structureColumn = `${structureFolded ? railColumn : EXERCISE_STRUCTURE_WIDTH}px`;
    const totalMinutes = Math.round(exercises.reduce((sum, exercise) => sum + (exercise.content_estimated_minutes ?? 0), 0) * 10) / 10;
    const savedMinutes = exercises.reduce((sum, exercise) => sum + (exercise.duration_minutes ?? 0), 0);
    // Compare content with the goal generation works toward: the exercise target, raised when
    // the lesson's words and grammar need added sessions.
    const contentGoal = Math.max(lesson.exercise_duration_minutes ?? 0, targetCoverage?.content_goal_minutes ?? 0) || null;
    const progress = exercises.length === 0 ? 0 : Math.round((exercises.filter(exercise => exercise.status === 'ready').length / exercises.length) * 100);
    const readyButtonHelp = markAllReadyBlockReason ?? (exercises.some(exercise => exercise.status !== 'ready')
        ? 'Review which exercises can be marked Ready.'
        : `${readyExerciseCount} of ${exercises.length} saved exercises marked ready.`);
    const defaultSelectionApplied = React.useRef(false);
    const { sessionNumbers, resolvedSessions } = exerciseSessionLayout(exercises, practiceSessions, targetCoverage);
    const moveExercise = (exerciseId: number, sessionNumber: number, index: number) => {
        const layout = exerciseLayoutAfterMove(exercises, resolvedSessions, exerciseId, sessionNumber, index);
        return layout ? onArrangeExercises(layout) : Promise.resolve();
    };
    const [pendingSessionMove, setPendingSessionMove] = React.useState<{ moving: number; to: number; layout: ExerciseLayoutItem[]; renumber: Map<number, number>; changes: string[] } | null>(null);
    const performSessionMove = (layout: ExerciseLayoutItem[], renumber: Map<number, number>) => onArrangeExercises(layout).then(() => {
        // An open overview follows its session's exercises to their new number. A review finding
        // described the old number's content, so selecting the new number leaves the finding behind.
        const shown = selectedExerciseId === null ? selectedSessionReview?.number : undefined;
        const next = shown === undefined ? undefined : renumber.get(shown);
        if (next !== undefined && next !== shown) onSelectSession?.(next);
    }).catch(() => undefined);
    const requestSessionMove = (moving: number, index: number) => {
        const move = sessionLayoutAfterMove(exercises, resolvedSessions, sessionNumbers, moving, index);
        if (!move) return;
        // `planned_focus` covers every saved session; coverage rows exist only for playable ones.
        const focus = (number: number) => targetCoverage?.planned_focus?.find(row => row.session_number === number)?.focus
            ?? targetCoverage?.sessions.find(row => row.session_number === number)?.focus;
        const labels = (number: number) => {
            const rows = focus(number) ?? [];
            return rows.slice(0, 6).map(row => row.label).join(', ') + (rows.length > 6 ? ', …' : '');
        };
        const keys = (number: number) => focus(number)?.map(row => row.key).sort().join('|');
        // The plan follows the session number, so exercises moving to a number planned for other
        // targets leave both sessions short of their plan. Say so before saving.
        const changes = [...move.renumber].filter(([old, next]) => old !== next
            && exercises.some(row => resolvedSessions.get(row.id) === old)
            && keys(old) !== undefined && keys(next) !== undefined && keys(old) !== keys(next))
            .map(([old, next]) => `Session ${next} is planned for ${labels(next)}. It will hold the exercises written for Session ${old}: ${labels(old)}.`);
        const to = move.renumber.get(moving) ?? moving;
        if (changes.length) setPendingSessionMove({ moving, to, layout: move.layout, renumber: move.renumber, changes });
        else void performSessionMove(move.layout, move.renumber);
    };

    React.useEffect(() => {
        if (defaultSelectionApplied.current || exercises.length === 0) return;
        defaultSelectionApplied.current = true;
        if (selectedExerciseId === null) onSelectExercise(exercises[0].id);
    }, [exercises, onSelectExercise, selectedExerciseId]);
    // Each view's header puts these controls on its title row (with the drawer openers at the ends below
    // their breakpoints) and this breadcrumb right below the title.
    const titleControls: StageTitleControls = {
        readiness: <StageReadiness percent={progress}>
            <Tooltip title={readyButtonHelp} describeChild>
                <span style={{ flexShrink: 0 }}>
                    <Button variant="outlined" startIcon={<CheckCircleRoundedIcon />} disabled={saving || Boolean(markAllReadyBlockReason) || exercises.every(exercise => exercise.status === 'ready')} onClick={onMarkAllReady}
                        sx={stageHeaderButtonSx}>Mark all ready{markableExerciseCount > 0 ? ` (${markableExerciseCount})` : ''}</Button>
                </span>
            </Tooltip>
        </StageReadiness>,
        leading: compactStructure && <DrawerIconButton side="left" label="Lesson structure" icon={<AccountTreeOutlinedIcon fontSize="small" />}
            expanded={structureOpen} onClick={() => setStructureOpen(true)} />,
        trailing: compactCopilot && <DrawerIconButton side="right" label="Copilot" icon={<AutoAwesomeRoundedIcon fontSize="small" />}
            expanded={isCopilotOpen} onClick={() => setCopilotOpen(true)} />,
    };
    const location = <Box aria-label="Exercise location" sx={{ display: 'flex', alignItems: 'center', columnGap: 0.65, rowGap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
        {([['Course', course?.name ?? 'Class'], ['Assignment', lesson.title],
            ...(selectedSessionReview ? [['Session', String(selectedSessionReview.number)]] : []),
            ...(selectedExercise ? [['Exercise', selectedExercise.title]] : []),
            ...(activeStep ? [['Step', activeStep.title]] : [])] as string[][]).map(([kind, label], index, items) => <React.Fragment key={kind}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0, maxWidth: '100%' }}>
                <Chip label={kind} size="small" sx={{ height: 22, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: kind === 'Step' ? BLUE_TEXT : SLATE, backgroundColor: kind === 'Step' ? SOFT_BLUE : '#fff' }} />
                <Typography sx={{ minWidth: 0, overflowWrap: 'anywhere', fontFamily: INTER, color: kind === 'Step' ? NAVY : SLATE, fontWeight: kind === 'Step' ? 700 : 500, fontSize: 11.5 }}>{label}</Typography>
                {index < items.length - 1 && <ChevronRightRoundedIcon sx={{ display: { xs: 'none', sm: 'block' }, color: MUTED, fontSize: 15, flexShrink: 0 }} />}
            </Box>
        </React.Fragment>)}
    </Box>;

    return (
        <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: { xs: '1fr', md: `${structureColumn} minmax(0, 1fr)`, lg: `${structureColumn} minmax(0, 1fr) ${copilotFolded ? railColumn : BUILDER_COPILOT_WIDTH}px` }, gap: 0, border: `1px solid ${BORDER}`, borderRadius: '20px', backgroundColor: '#fff', ...builderPanelResize('grid-template-columns') }}>
            <ReviewOutlineMarksContext.Provider value={reviewMarks}><LessonReviewEditContext.Provider value={readOnly && reviewEditable}><LessonReviewReadOnlyContext.Provider value={readOnly}>
            {!compactStructure && <ExerciseOutlinePanel key={`outline:${contentVersion}`} hidden={structureFolded} hideControl={<BuilderPanelHideButton panel="structure" title="Lesson structure" />}
                lesson={lesson} course={course} exercises={exercises} practiceSessions={practiceSessions}
                targetCoverage={targetCoverage} sessionNumbers={sessionNumbers} resolvedSessions={resolvedSessions} steps={steps} selectedExerciseId={selectedExerciseId}
                selectedStepId={activeStep?.id ?? null} selectedSessionNumber={selectedSessionReview?.number ?? null} totalMinutes={totalMinutes} contentGoal={contentGoal} progress={progress}
                onSelectExercise={onSelectExercise} onSelectSession={onSelectSession} onSelectStep={onSelectStep} onAddExercise={onAddExercise} onMoveExercise={moveExercise}
                onMoveSession={requestSessionMove} onReorderSteps={onReorderSteps} saving={saving || readinessDisabled || readOnly} />}
            {structureFolded && <BuilderPanelRail panel="structure" title="Lesson structure" icon={<AccountTreeOutlinedIcon fontSize="small" />} sx={{ p: '6px' }} />}
            <Box data-exercise-content tabIndex={-1} key={`content:${contentVersion}`} sx={{ minWidth: 0, minHeight: 0, p: { xs: 2, lg: 3 }, backgroundColor: PAGE_BACKGROUND, outline: 'none', '&[data-exercise-highlight="true"]': { outline: `3px solid ${BLUE}`, outlineOffset: -4 } }}>
                <Box data-testid="exercise-editor-content" sx={{ width: '100%', maxWidth: 920 + (structureFolded ? EXERCISE_STRUCTURE_WIDTH - railColumn : 0) + (copilotFolded ? BUILDER_COPILOT_WIDTH - railColumn : 0), mx: 'auto', ...builderPanelResize('max-width') }}>
                    {reviewBar}
                    {selectedSessionReview && selectedExercise === null ? <SessionOverview selection={selectedSessionReview} location={location} titleControls={titleControls}
                        session={practiceSessions.find(row => row.number === selectedSessionReview.number)}
                        coverage={targetCoverage?.sessions.find(row => row.session_number === selectedSessionReview.number)}
                        exercises={exercises.filter(row => resolvedSessions.get(row.id) === selectedSessionReview.number)}
                        exerciseNumber={id => exercises.findIndex(row => row.id === id) + 1} saving={saving || readinessDisabled || readOnly}
                        onSelectExercise={onSelectExercise} onFillCoverageGaps={targetCoverage?.has_gaps ? onFillCoverageGaps : undefined}
                        sessionPosition={{ index: sessionNumbers.indexOf(selectedSessionReview.number), count: sessionNumbers.length }}
                        onMoveSession={index => requestSessionMove(selectedSessionReview.number, index)} /> : selectedExercise === null ? <LanguageSummary vocabularyTargets={vocabularyTargets} grammarTargets={grammarTargets} location={location} titleControls={titleControls}
                        coverage={<LessonCoverageCard targetCoverage={targetCoverage} exerciseCount={exercises.length} totalMinutes={totalMinutes} savedMinutes={savedMinutes} contentGoal={contentGoal}
                            saving={saving || readinessDisabled || readOnly} onFillCoverageGaps={onFillCoverageGaps} />} /> : (
                        <ExerciseDetail readinessDisabled={readinessDisabled} onReviseExercise={onReviseExercise} renderStep={renderStep} renderReviewStep={renderReviewStep} location={location} titleControls={titleControls}
                            exercise={selectedExercise} steps={selectedSteps} activeStep={activeStep} sessionNumber={resolvedSessions.get(selectedExercise.id) ?? selectedExercise.session_number ?? selectedSession?.number} viewMode={viewMode} onViewModeChange={setViewMode}
                            reviewTextFocus={reviewTextFocus} studentSteps={studentSteps} reviewExerciseField={reviewExerciseField}
                            courseId={course?.id ?? 0} lessonId={lesson.id}
                            onSelectStep={stepId => onSelectStep(selectedExercise.id, stepId)} choices={choices} sentences={sentences} conversations={conversations}
                            saving={saving} onSaveExercise={onUpdateExercise} onDeleteExercise={onDeleteExercise}
                            onAddStep={onAddStep} onUpdateStep={onUpdateStep} onDeleteStep={onDeleteStep}
                            onChildMutation={onChildMutation} onMarkDirty={onMarkDirty} onCancelScope={onCancelScope} authoringRevision={authoringRevision}
                            practiceTargetEditor={onSavePracticeTargets && <ExercisePracticeTargetsEditor key={`targets:${selectedExercise.id}:${contentVersion}`} exerciseId={selectedExercise.id}
                                vocabularyTargets={vocabularyTargets} grammarTargets={grammarTargets} saving={saving}
                                tags={practiceTargets.find(row => row.exercise_id === selectedExercise.id)}
                                onSave={(vocabularyIds, grammarIds) => onSavePracticeTargets(selectedExercise.id, vocabularyIds, grammarIds)} />} />
                    )}
                    <Stack spacing={1.5} sx={stageFooterSx}>
                        {(lessonActionsNote || !readOnly) && <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
                            {lessonActionsNote
                                ? <CancelRoundedIcon sx={{ color: ERROR_TEXT, fontSize: 18, flexShrink: 0 }} />
                                : <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 18, flexShrink: 0 }} />}
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, minWidth: 0, textAlign: 'right', color: lessonActionsNote ? WARNING_TEXT : SUCCESS_TEXT }}>{lessonActionsNote ?? 'Manual exercise edits are saved per card'}</Typography>
                        </Stack>}
                        <StageFooterActions>
                            <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ minHeight: 44, color: SLATE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Back to lesson plan</Button>
                            {lessonActions}
                        </StageFooterActions>
                    </Stack>
                </Box>
            </Box>
            </LessonReviewReadOnlyContext.Provider></LessonReviewEditContext.Provider></ReviewOutlineMarksContext.Provider>
            {compactCopilot ? <>
                <Backdrop open={isCopilotOpen && !reviewOpen} onClick={() => { if (!copilotImageBusy) setCopilotOpen(false); }} sx={{ zIndex: theme.zIndex.drawer - 1 }} />
                <Drawer anchor="right" variant="persistent" open={isCopilotOpen} onKeyDown={event => { if (event.key === 'Escape' && !copilotImageBusy) setCopilotOpen(false); }}
                    PaperProps={{ sx: { width: 'min(92vw, 380px)' } }}>
                <Box component="fieldset" disabled={saving} sx={{ border: 0, p: 0, m: 0, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Exercise copilot</Typography>
                    <IconButton aria-label="Close exercise copilot" disabled={copilotImageBusy} onClick={() => setCopilotOpen(false)} sx={{ width: 44, height: 44 }}><CloseRoundedIcon /></IconButton>
                </Stack>
                {copilot}
                </Box>
                </Drawer>
            </> : <>
                {/* `contents` keeps the copilot itself the grid item, so its sticky pane still follows the page. */}
                <Box sx={{ display: copilotFolded ? 'none' : 'contents' }}>{copilot}</Box>
                {copilotFolded && <BuilderPanelRail panel="copilot" title="Exercise copilot" icon={<AutoAwesomeRoundedIcon fontSize="small" />} attention={copilotAttention} sx={{ p: '6px' }} />}
            </>}
            {compactStructure && <Drawer anchor="left" open={structureOpen} onClose={() => setStructureOpen(false)}
                ModalProps={{ keepMounted: true }} PaperProps={{ sx: { width: 'min(88vw, 340px)' } }}>
                <Box component="fieldset" disabled={saving} sx={{ border: 0, p: 0, m: 0, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Lesson structure</Typography>
                    <IconButton aria-label="Close lesson structure" onClick={() => setStructureOpen(false)} sx={{ width: 44, height: 44 }}><CloseRoundedIcon /></IconButton>
                </Stack>
                <ReviewOutlineMarksContext.Provider value={reviewMarks}><LessonReviewReadOnlyContext.Provider value={readOnly}><ExerciseOutlinePanel key={`mobile-outline:${contentVersion}`} lesson={lesson} course={course} exercises={exercises} practiceSessions={practiceSessions} targetCoverage={targetCoverage} sessionNumbers={sessionNumbers} resolvedSessions={resolvedSessions} steps={steps} selectedExerciseId={selectedExerciseId} selectedStepId={activeStep?.id ?? null} selectedSessionNumber={selectedSessionReview?.number ?? null} totalMinutes={totalMinutes} contentGoal={contentGoal} progress={progress}
                    onSelectExercise={id => { onSelectExercise(id); setStructureOpen(false); }}
                    onSelectSession={onSelectSession && (number => { onSelectSession(number); setStructureOpen(false); })}
                    onSelectStep={(exerciseId, stepId) => { onSelectStep(exerciseId, stepId); setStructureOpen(false); }}
                    onAddExercise={onAddExercise} onMoveExercise={moveExercise} onMoveSession={requestSessionMove} onReorderSteps={onReorderSteps} saving={saving || readinessDisabled || readOnly} /></LessonReviewReadOnlyContext.Provider></ReviewOutlineMarksContext.Provider>
                </Box>
            </Drawer>}
            <Dialog open={pendingSessionMove !== null} onClose={() => setPendingSessionMove(null)} aria-labelledby="session-move-title" maxWidth="sm" fullWidth>
                <DialogTitle id="session-move-title" sx={{ fontFamily: POPPINS, color: NAVY }}>Move Session {pendingSessionMove?.moving} to Session {pendingSessionMove?.to}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 14, mb: 1.5 }}>
                        The word and grammar plan stays with each session number, so after this move these sessions' exercises no longer match their planned words and grammar. Coverage will report the gaps until the exercises are revised.
                    </Typography>
                    <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, my: 0 }}>
                        {pendingSessionMove?.changes.map(change => <Typography component="li" key={change} sx={{ fontFamily: INTER, color: NAVY, fontSize: 13.5 }}>{change}</Typography>)}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingSessionMove(null)} sx={{ textTransform: 'none', fontFamily: INTER }}>Cancel</Button>
                    <Button variant="contained" disabled={saving || readinessDisabled} sx={{ textTransform: 'none', fontFamily: INTER }} onClick={() => {
                        const move = pendingSessionMove;
                        setPendingSessionMove(null);
                        if (move) void performSessionMove(move.layout, move.renumber);
                    }}>Move anyway</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

type TargetChipRow = { key: string; label: string; practised?: boolean };

/** Targets as chips. With practice data, practised targets are checked and the rest dashed. */
function TargetChips({ rows, showPractised = true }: { rows: TargetChipRow[]; showPractised?: boolean }) {
    return <Stack role="list" direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
        {rows.map(row => {
            const status = showPractised && row.practised !== undefined;
            return <Chip key={row.key} role="listitem" size="small" label={row.label} variant="outlined"
                icon={status && row.practised ? <CheckRoundedIcon aria-hidden="true" /> : undefined}
                aria-label={status ? `${row.label}, ${row.practised ? 'practised' : 'not practised yet'}` : row.label}
                sx={{ fontFamily: INTER, ...(status ? row.practised
                    ? { color: SUCCESS_TEXT, borderColor: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT, '& .MuiChip-icon': { color: SUCCESS_TEXT } }
                    : { color: WARNING_TEXT, borderColor: WARNING_TEXT, borderStyle: 'dashed', backgroundColor: WARNING_SOFT }
                    : { color: SLATE, borderColor: BORDER, backgroundColor: '#fff' }) }} />;
        })}
    </Stack>;
}

/** A session's planned words and grammar and its review targets: listed on hover, as chips on click. */
function SessionTargetsInfo({ number, focus, review }: {
    number: number;
    focus: (TargetChipRow & { kind: 'vocabulary' | 'grammar' })[];
    review: TargetChipRow[];
}) {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    // Closing the popup returns focus to the icon. The tooltip stays closed until focus or the pointer
    // leaves, so it does not reopen over the header, which it covers in a narrow outline. Its open
    // state is controlled: an ignored open request must not be remembered for later.
    const [quiet, setQuiet] = React.useState(false);
    const [tipOpen, setTipOpen] = React.useState(false);
    const words = focus.filter(row => row.kind === 'vocabulary');
    const grammar = focus.filter(row => row.kind === 'grammar');
    const name = `Words and grammar for Session ${number}`;
    const list = (rows: TargetChipRow[]) => rows.map(row => row.label).join(', ');
    const hasStatus = focus.some(row => row.practised !== undefined);
    const section = (title: string, rows: TargetChipRow[], showPractised: boolean) => rows.length > 0 && <Box>
        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 0.75 }}>{title}</Typography>
        <TargetChips rows={rows} showPractised={showPractised} />
    </Box>;
    return <>
        {/* describeChild: the list describes the button, which keeps its own name. */}
        <Tooltip arrow describeChild placement="right" open={tipOpen && !anchor} onOpen={() => { if (!quiet) setTipOpen(true); }} onClose={() => setTipOpen(false)}
            title={<Box data-testid={`session-targets-tooltip-${number}`} sx={{ fontFamily: INTER, fontSize: 12, lineHeight: 1.5 }}>
            {words.length > 0 && <Box>Words: {list(words)}</Box>}
            {grammar.length > 0 && <Box>Grammar: {list(grammar)}</Box>}
            {review.length > 0 && <Box>Review: {list(review)}</Box>}
        </Box>}>
            <IconButton size="small" aria-label={name} aria-haspopup="dialog" aria-expanded={Boolean(anchor)}
                onClick={event => { setTipOpen(false); setAnchor(event.currentTarget); }} onBlur={() => setQuiet(false)} onMouseLeave={() => setQuiet(false)} sx={{ color: BLUE }}>
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
        </Tooltip>
        <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => { setAnchor(null); setQuiet(true); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { role: 'dialog', 'aria-label': name, sx: { p: 2, maxWidth: 320, borderRadius: '14px' } } }}>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 14, mb: 1.25 }}>{name}</Typography>
            <Stack spacing={1.5}>
                {section('Words', words, true)}
                {section('Grammar', grammar, true)}
                {section('Review from earlier sessions', review, false)}
                {hasStatus && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5 }}>Checked targets are practised in this session's exercises; dashed ones are not yet.</Typography>}
            </Stack>
        </Popover>
    </>;
}

/** A session's planned focus, coverage gaps and exercises, kept here so the outline stays a list. */
function SessionOverview({ selection, session, coverage, exercises, exerciseNumber, saving, onSelectExercise, onFillCoverageGaps, sessionPosition, onMoveSession, location, titleControls }: {
    /** The breadcrumb, shown below the title. */
    location: React.ReactNode;
    titleControls: StageTitleControls;
    selection: ExerciseSessionSelection;
    session?: IVerbaScholaPracticeSession;
    coverage?: IVerbaScholaSessionTargetCoverage;
    exercises: IVerbaScholaLessonExercise[];
    exerciseNumber: (exerciseId: number) => number;
    saving: boolean;
    onSelectExercise: (exerciseId: number | null) => void;
    onFillCoverageGaps?: () => void;
    /** Where this session sits among the outline's sessions. */
    sessionPosition?: { index: number; count: number };
    /** The non-drag way to move the whole session; dragging its header in the outline does the same. */
    onMoveSession?: (index: number) => void;
}) {
    // Under review the page saves nothing itself, so there is no dragging to suggest.
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const focus = (kind: 'vocabulary' | 'grammar') => coverage?.focus.filter(row => row.kind === kind) ?? [];
    const practised = (rows: IVerbaScholaTargetCoverageRow[]) => `${rows.filter(row => row.practised).length}/${rows.length}`;
    const words = focus('vocabulary');
    const grammar = focus('grammar');
    // Targets practised only in draft exercises: students of this session do not receive them yet.
    const draftOnly = coverage ? coverage.ready_missing.filter(label => !coverage.missing.includes(label)) : [];
    const minutes = session?.estimated_minutes ?? Math.round(exercises.reduce((total, exercise) => total + (exercise.content_estimated_minutes ?? 0), 0) * 10) / 10;
    const label = (text: string) => <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 0.75 }}>{text}</Typography>;
    return <Box data-exercise-session-overview={selection.number} tabIndex={-1} sx={{ outline: 'none', '&[data-exercise-highlight="true"]': { outline: `3px solid ${BLUE}`, outlineOffset: 4 } }}>
        <ExerciseViewHeader title={`Session ${selection.number}`} titleControls={titleControls} location={location}
            controls={onMoveSession && sessionPosition && sessionPosition.index >= 0 && sessionPosition.count > 1 && exercises.length > 0 && <Stack direction="row" spacing={0.5}>
                <Button size="small" variant="outlined" startIcon={<ArrowUpwardRoundedIcon />} disabled={saving || sessionPosition.index === 0}
                    onClick={() => onMoveSession(sessionPosition.index - 1)} sx={{ textTransform: 'none', fontFamily: INTER, borderColor: BORDER }}>Move earlier</Button>
                <Button size="small" variant="outlined" startIcon={<ArrowDownwardRoundedIcon />} disabled={saving || sessionPosition.index >= sessionPosition.count - 1}
                    onClick={() => onMoveSession(sessionPosition.index + 1)} sx={{ textTransform: 'none', fontFamily: INTER, borderColor: BORDER }}>Move later</Button>
            </Stack>}
            details={<Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13.5 }}>{exercises.length} {exercises.length === 1 ? 'exercise' : 'exercises'} · about {minutes} min</Typography>} />
        {/* Opened from the lesson review, which shows the finding in full: here only which one it is. */}
        {selection.title && <Alert severity="warning" sx={{ mb: 2 }} data-testid="session-review-finding"><LessonText>From lesson review: {selection.title}</LessonText></Alert>}
        <Stack spacing={2}>
            {coverage && coverage.focus.length + coverage.review.length > 0 && <Box data-testid={`session-target-coverage-${selection.number}`}>
                <VerbaScholaCard>
                    <VerbaScholaCardHeader title="Planned focus" meta={`${practised(words)} words practised · ${practised(grammar)} grammar tagged`} />
                    <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.75 } }}>
                        {words.length > 0 && <Box>{label('Words')}<TargetChips rows={words} /></Box>}
                        {grammar.length > 0 && <Box>{label('Grammar')}<TargetChips rows={grammar} /></Box>}
                        {coverage.review.length > 0 && <Box>{label('Review from earlier sessions')}<TargetChips rows={coverage.review} showPractised={false} /></Box>}
                        {coverage.missing.length > 0 && <Alert severity="warning" action={onFillCoverageGaps &&
                            <Button color="inherit" size="small" disabled={saving} onClick={onFillCoverageGaps} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Fill coverage gaps</Button>}>
                            Not practised in this session yet: {coverage.missing.join(', ')}
                        </Alert>}
                        {draftOnly.length > 0 && <Alert severity="info">
                            Only in draft exercises, so students of this session miss them: {draftOnly.join(', ')}. Mark the exercises that practise them Ready.
                        </Alert>}
                    </Stack>
                </VerbaScholaCard>
            </Box>}
            {session && session.missing_activity_types.length > 0 && <Alert severity="info" icon={<TipsAndUpdatesOutlinedIcon fontSize="inherit" />}>
                Consider adding: {session.missing_activity_types.map(kind => activityKindLabel[kind] ?? kind).join(', ')}.
            </Alert>}
            <VerbaScholaCard>
                <VerbaScholaCardHeader title="Exercises" meta={readOnly ? undefined : 'Drag exercises in the lesson structure to move or reorder them.'} />
                {exercises.length ? <Stack spacing={1} sx={{ p: { xs: 2, sm: 2.75 } }}>
                    {exercises.map(exercise => <Button key={exercise.id} variant="outlined" onClick={() => onSelectExercise(exercise.id)} endIcon={<ChevronRightRoundedIcon />}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderColor: BORDER, color: NAVY, fontFamily: INTER, '& .MuiButton-endIcon': { ml: 'auto' } }}>
                        <Box component="span" sx={{ color: SLATE, fontWeight: 700, fontSize: 11, letterSpacing: '0.05em', mr: 1, flexShrink: 0 }}>EXERCISE {exerciseNumber(exercise.id)}</Box>
                        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exercise.title}</Box>
                        <Chip label={titleCase(exercise.status)} size="small" sx={{ ...exerciseStatusChipSx(exercise.status), minHeight: 22, fontSize: 11, ml: 1, flexShrink: 0 }} />
                    </Button>)}
                </Stack> : <Typography sx={{ p: { xs: 2, sm: 2.75 }, fontFamily: INTER, color: SLATE, fontSize: 13 }}>No exercises yet. In the lesson structure, drag one onto this session or add one.</Typography>}
            </VerbaScholaCard>
        </Stack>
    </Box>;
}

function ExerciseOutlinePanel({ lesson, course, exercises, practiceSessions, targetCoverage, sessionNumbers, resolvedSessions, steps,
    selectedExerciseId, selectedStepId, selectedSessionNumber, totalMinutes, contentGoal, progress, saving, onSelectExercise, onSelectSession, onSelectStep, onAddExercise, onMoveExercise, onMoveSession, onReorderSteps,
    hidden = false, hideControl }: {
    /** Folded into its rail; stays mounted so a typed exercise title and open sessions survive. */
    hidden?: boolean;
    /** The wide-screen fold button, at the panel's outer top corner where the rail's opener appears. */
    hideControl?: React.ReactNode;
    lesson: IVerbaScholaLesson;
    course: IVerbaScholaCourse | null;
    exercises: IVerbaScholaLessonExercise[];
    practiceSessions: IVerbaScholaPracticeSession[];
    targetCoverage?: IVerbaScholaTargetCoverage;
    sessionNumbers: number[];
    resolvedSessions: Map<number, number>;
    steps: IVerbaScholaLessonExerciseStep[];
    selectedExerciseId: number | null;
    selectedStepId: number | null;
    selectedSessionNumber?: number | null;
    totalMinutes: number;
    contentGoal: number | null;
    progress: number;
    saving: boolean;
    onSelectExercise: (exerciseId: number | null) => void;
    onSelectSession?: (sessionNumber: number) => void;
    onSelectStep: (exerciseId: number, stepId: number) => void;
    onAddExercise: (payload: Partial<IVerbaScholaLessonExercise>) => Promise<void>;
    /** Saves an exercise into a session at a position among that session's other exercises. */
    onMoveExercise: (exerciseId: number, sessionNumber: number, index: number) => Promise<void>;
    /** Moves a whole session to a position among the other sessions (after any plan warning). */
    onMoveSession: (sessionNumber: number, index: number) => void;
    /** Saves one exercise's complete step order. */
    onReorderSteps: (exerciseId: number, orderedIds: number[]) => Promise<void>;
}) {
    const marks = React.useContext(ReviewOutlineMarksContext);
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const [newTitle, setNewTitle] = React.useState('');
    const [addError, setAddError] = React.useState<string | null>(null);
    const coverageTotal = targetCoverage ? targetCoverage.vocabulary.total + targetCoverage.grammar.total : 0;
    // Missing content, not Ready status: drafts are expected while a lesson is being built.
    // `has_gaps` also catches targets practised in the wrong session, e.g. after sessions swap.
    const lessonNeedsContent = Boolean(targetCoverage && (targetCoverage.has_gaps || targetCoverage.vocabulary.missing.length || targetCoverage.grammar.missing.length
        || targetCoverage.sessions_without_exercises.length)) || Boolean(contentGoal && totalMinutes < contentGoal && exercises.length > 0);
    // Drag state. dataTransfer cannot be read during dragover, so the dragged id and the drop
    // point are also kept in refs for the drop handler.
    const dragId = React.useRef<number | null>(null);
    const [dragging, setDragging] = React.useState<number | null>(null);
    const dropRef = React.useRef<{ session: number; index: number } | null>(null);
    const [dropAt, setDropAtState] = React.useState<{ session: number; index: number } | null>(null);
    const openTimer = React.useRef<number | undefined>(undefined);
    const setDropAt = (next: { session: number; index: number } | null) => {
        dropRef.current = next;
        setDropAtState(current => current?.session === next?.session && current?.index === next?.index ? current : next);
    };
    // Sessions and steps drag separately from exercises; each keeps its own source and drop point.
    const sessionDrag = React.useRef<number | null>(null);
    const [draggingSession, setDraggingSession] = React.useState<number | null>(null);
    const sessionDropRef = React.useRef<number | null>(null);
    const [sessionDropAt, setSessionDropAtState] = React.useState<number | null>(null);
    const setSessionDropAt = (next: number | null) => { sessionDropRef.current = next; setSessionDropAtState(next); };
    const stepDrag = React.useRef<number | null>(null);
    const [draggingStep, setDraggingStep] = React.useState<number | null>(null);
    const stepDropRef = React.useRef<number | null>(null);
    const [stepDropAt, setStepDropAtState] = React.useState<number | null>(null);
    const setStepDropAt = (next: number | null) => { stepDropRef.current = next; setStepDropAtState(next); };
    const clearDrag = () => {
        dragId.current = null;
        sessionDrag.current = null;
        stepDrag.current = null;
        window.clearTimeout(openTimer.current);
        openTimer.current = undefined;
        setDragging(null);
        setDropAt(null);
        setDraggingSession(null);
        setSessionDropAt(null);
        setDraggingStep(null);
        setStepDropAt(null);
    };
    const sessionOthers = sessionNumbers.filter(number => number !== draggingSession);
    const dropSession = () => {
        const moving = sessionDrag.current;
        const index = sessionDropRef.current;
        clearDrag();
        if (moving !== null && index !== null) onMoveSession(moving, index);
    };
    // A session drop point that changes nothing shows no outline.
    const sessionGhostIndex = draggingSession !== null && sessionDropAt !== null && sessionDropAt !== sessionNumbers.indexOf(draggingSession) ? sessionDropAt : null;
    const overSessionGhost = (event: React.DragEvent<HTMLElement>) => {
        if (sessionDrag.current === null) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };
    const sessionGhost = <Box key="session-drop-ghost" data-testid="session-drop-ghost" aria-hidden="true" onDragEnter={overSessionGhost} onDragOver={overSessionGhost}
        onDrop={event => { event.preventDefault(); dropSession(); }}
        sx={{ px: 1.25, py: 1.1, borderRadius: '16px', border: `2px dashed ${BLUE}`, backgroundColor: SOFT_BLUE }}>
        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: BLUE_TEXT, fontSize: 13 }}>Session {draggingSession}</Typography>
        <Typography sx={{ fontFamily: INTER, color: BLUE_TEXT, fontSize: 11 }}>Drop to move this session here</Typography>
    </Box>;
    React.useEffect(() => () => window.clearTimeout(openTimer.current), []);
    const dropExercise = (sessionNumber: number) => {
        const id = dragId.current;
        const target = dropRef.current;
        clearDrag();
        if (id !== null && target?.session === sessionNumber) void onMoveExercise(id, target.session, target.index).catch(() => undefined);
    };
    const newSessionNumber = (sessionNumbers[sessionNumbers.length - 1] ?? 0) + 1;
    const overNewSession = (event: React.DragEvent<HTMLElement>) => {
        if (saving || dragId.current === null) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropAt({ session: newSessionNumber, index: 0 });
    };
    const [openSessions, setOpenSessions] = React.useState<number[]>(sessionNumbers[0] ? [sessionNumbers[0]] : []);
    // Touch screens pick a row up by touching and holding it; the drag then runs through the same handlers as a mouse.
    const navRef = React.useRef<HTMLElement>(null);
    useTouchDragAndDrop(navRef);
    const sessionScroll = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (!selectedSessionNumber) return;
        setOpenSessions(current => current.includes(selectedSessionNumber) ? current : [...current, selectedSessionNumber]);
        const timer = window.setTimeout(() => {
            const container = sessionScroll.current;
            const section = container?.querySelector<HTMLElement>(`[data-exercise-session-number="${selectedSessionNumber}"]`);
            if (!container || !section) return;
            container.scrollTo({ top: section.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12,
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }, 0);
        return () => window.clearTimeout(timer);
    }, [selectedSessionNumber]);
    const groups: { session: IVerbaScholaPracticeSession | null; exercises: IVerbaScholaLessonExercise[] }[] = sessionNumbers.map(number => {
        const session = practiceSessions.find(item => item.number === number);
        const members = exercises.filter(exercise => resolvedSessions.get(exercise.id) === number);
        return { session: session ?? { number, items: [], estimated_minutes: Math.round(members.reduce((total, exercise) => total + (exercise.content_estimated_minutes ?? 0), 0) * 10) / 10, activity_types: [], missing_activity_types: [] }, exercises: members };
    });
    const assignedIds = new Set(groups.flatMap(group => group.exercises.map(exercise => exercise.id)));
    const unassigned = exercises.filter(exercise => !assignedIds.has(exercise.id));
    if (unassigned.length) groups.push({ session: null, exercises: unassigned });
    const selectedExerciseSessionNumber = selectedExerciseId == null ? undefined : resolvedSessions.get(selectedExerciseId);
    React.useEffect(() => {
        if (selectedExerciseSessionNumber) setOpenSessions(current => current.includes(selectedExerciseSessionNumber) ? current : [...current, selectedExerciseSessionNumber]);
    }, [selectedExerciseSessionNumber]);
    // As a column it keeps its full width at the left edge while the column eases wider.
    return <Box ref={navRef} component="nav" aria-label="Self-study lesson structure" data-builder-panel={hideControl ? 'structure' : undefined}
        sx={{ minWidth: 0, minHeight: 0, display: hidden ? 'none' : 'flex', flexDirection: 'column', backgroundColor: '#fff', borderRight: `1px solid ${BORDER}`,
            ...(hideControl ? { width: EXERCISE_STRUCTURE_WIDTH, justifySelf: 'start' } : {}) }}>
        <Box sx={{ p: 2.5, pb: 2 }}>
            {hideControl && <Box sx={{ mt: -1.5, ml: -1.5, mb: 0.5 }}>{hideControl}</Box>}
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, letterSpacing: '0.08em' }}>COURSE</Typography>
            <Typography sx={{ fontFamily: INTER, fontWeight: 600, color: NAVY, fontSize: 12.5, mt: 0.35 }}>{course?.name ?? 'Class'}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.25 }}>
                <ArrowForwardRoundedIcon sx={{ color: BLUE, fontSize: 16 }} />
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, letterSpacing: '0.08em' }}>ASSIGNMENT <Box component="span" sx={{ fontWeight: 500, color: MUTED }}>- Lesson</Box></Typography>
            </Stack>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 17, mt: 0.5 }}>{lesson.title}</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
                <LinearProgress aria-label="Lesson plan progress" variant="determinate" value={progress} sx={{ flex: 1, height: 6, borderRadius: 99, backgroundColor: PROGRESS_TRACK, '& .MuiLinearProgress-bar': { borderRadius: 99, backgroundColor: BLUE } }} />
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 12 }}>{progress}%</Typography>
            </Stack>
            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mt: 0.75 }}>{exercises.length} exercises · {steps.length} steps · {contentGoal ? `about ${totalMinutes}/${contentGoal}` : `about ${totalMinutes}`} min of content</Typography>
            {/* Dragging is off under review. */}
            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11, mt: 0.75 }}>{readOnly ? '' : 'Drag an exercise to move or reorder it. '}Students see only Ready exercises.</Typography>
        </Box>
        <Divider />
        <Box ref={sessionScroll} sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
            {(() => {
                const languageSelected = selectedExerciseId === null && !selectedSessionNumber;
                return <Button onClick={() => onSelectExercise(null)} aria-current={languageSelected ? 'location' : undefined}
                    sx={{ width: '100%', justifyContent: 'flex-start', color: NAVY, fontFamily: INTER, fontWeight: 600, fontSize: 13, textTransform: 'none', px: 1.25, py: 1.25, mb: 1, borderRadius: '14px', border: `1px solid ${languageSelected ? BLUE : BORDER}`, borderLeft: `4px solid ${languageSelected ? BLUE : BORDER}`, backgroundColor: languageSelected ? SOFT_BLUE : '#fff', '&:hover': { backgroundColor: SOFT_BLUE, borderColor: BLUE }, '&:focus-visible': { outline: `2px solid ${BLUE}`, outlineOffset: 2 } }}>
                    <AutoAwesomeRoundedIcon sx={{ mr: 1.25, fontSize: 19, color: BLUE }} />
                    <Box component="span" sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <Box component="span" sx={{ display: 'block' }}>Vocabulary & grammar</Box>
                        {/* One line here; the lesson's coverage and timing details open in the main panel. */}
                        {coverageTotal > 0 && targetCoverage && <Box component="span" sx={{ display: 'block', fontWeight: 500, fontSize: 11, color: lessonNeedsContent ? WARNING_TEXT : SLATE }}>
                            Words {targetCoverage.vocabulary.practised}/{targetCoverage.vocabulary.total} · Grammar {targetCoverage.grammar.practised}/{targetCoverage.grammar.total}
                        </Box>}
                    </Box>
                    {lessonNeedsContent && <WarningAmberRoundedIcon titleAccess="Coverage or timing needs attention" sx={{ ml: 0.5, color: WARNING_TEXT, fontSize: 17 }} />}
                    <ChevronRightRoundedIcon aria-hidden="true" sx={{ ml: 0.5, fontSize: 18, color: BLUE }} />
                </Button>;
            })()}
            <Stack spacing={1}>
                {groups.map(({ session, exercises: groupExercises }) => {
                    const number = session?.number;
                    const open = number === undefined || openSessions.includes(number);
                    const plan = number === undefined ? undefined : targetCoverage?.sessions.find(row => row.session_number === number);
                    // The plan for the info icon: the coverage row (with practice status) or the plan alone.
                    const plannedOnly = number === undefined ? undefined : targetCoverage?.planned_focus?.find(row => row.session_number === number);
                    const infoFocus = plan?.focus ?? plannedOnly?.focus ?? [];
                    const infoReview = plan?.review ?? plannedOnly?.review ?? [];
                    const hasInfo = number !== undefined && infoFocus.length + infoReview.length > 0;
                    // Only content gaps are flagged here; Ready status already shows on each exercise.
                    const needsContent = Boolean(plan?.missing.length) || (groupExercises.length === 0 && number !== undefined && Boolean(targetCoverage?.sessions_without_exercises.includes(number)));
                    const overviewSelected = number !== undefined && selectedExerciseId === null && selectedSessionNumber === number;
                    const others = groupExercises.filter(row => row.id !== dragging);
                    // Hide the outline where the drop would change nothing.
                    const ghostIndex = number !== undefined && dragging !== null && dropAt?.session === number
                        && exerciseLayoutAfterMove(exercises, resolvedSessions, dragging, number, dropAt.index) ? dropAt.index : null;
                    const draggedTitle = exercises.find(row => row.id === dragging)?.title ?? '';
                    const overGhost = (event: React.DragEvent<HTMLElement>) => { event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = 'move'; };
                    // dragenter is handled like dragover: some drags report only dragenter until the pointer moves again.
                    const overSession = number === undefined ? undefined : (event: React.DragEvent<HTMLElement>) => {
                        if (sessionDrag.current !== null) {
                            if (saving) return;
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'move';
                            // Before or after this session card, by which half the pointer is over.
                            const rect = event.currentTarget.getBoundingClientRect();
                            const rest = sessionNumbers.filter(row => row !== sessionDrag.current);
                            setSessionDropAt(number === sessionDrag.current ? sessionNumbers.indexOf(number)
                                : rest.indexOf(number) + (event.clientY > rect.top + rect.height / 2 ? 1 : 0));
                            return;
                        }
                        if (saving || dragId.current === null) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        // Over the header or the session row: keep a point already in this session, or go to its end.
                        if (dropRef.current?.session !== number) setDropAt({ session: number, index: others.length });
                        if (!open && openTimer.current === undefined) openTimer.current = window.setTimeout(() => {
                            openTimer.current = undefined;
                            setOpenSessions(current => current.includes(number) ? current : [...current, number]);
                        }, 500);
                    };
                    const ghost = <Box key="drop-ghost" data-testid="exercise-drop-ghost" aria-hidden="true" onDragEnter={overGhost} onDragOver={overGhost}
                        sx={{ m: 0.5, px: 1.25, py: 1, borderRadius: '10px', border: `2px dashed ${BLUE}`, backgroundColor: SOFT_BLUE }}>
                        <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, color: BLUE_TEXT, fontSize: 13 }}>{draggedTitle}</Typography>
                        <Typography sx={{ fontFamily: INTER, color: BLUE_TEXT, fontSize: 11 }}>Drop to move here</Typography>
                    </Box>;
                    const canDragSession = number !== undefined && groupExercises.length > 0 && sessionNumbers.length > 1;
                    return <React.Fragment key={number ?? 'other'}>
                    {number !== undefined && number !== draggingSession && sessionGhostIndex === sessionOthers.indexOf(number) && sessionGhost}
                    <Box data-exercise-session-number={number} data-drop-target={ghostIndex !== null || undefined} data-dragging={draggingSession === number || undefined}
                        onDragEnter={overSession} onDragOver={overSession}
                        onDragLeave={event => {
                            if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                            window.clearTimeout(openTimer.current);
                            openTimer.current = undefined;
                            if (dropRef.current?.session === number) setDropAt(null);
                        }}
                        onDrop={number === undefined ? undefined : event => { event.preventDefault(); if (sessionDrag.current !== null) dropSession(); else dropExercise(number); }}
                        sx={{ border: `1px solid ${number !== undefined && number === selectedSessionNumber ? BLUE : BORDER}`, borderRadius: '16px', overflow: 'hidden',
                            opacity: draggingSession === number ? 0.4 : 1, transition: 'opacity 120ms ease',
                            ...(dragging !== null && dropAt?.session === number ? { outline: `2px dashed ${BLUE}`, outlineOffset: -1 } : {}) }}>
                        <Box data-session-drag-source={number} draggable={canDragSession && !saving}
                            onDragStart={event => {
                                if (!canDragSession || saving || number === undefined) { event.preventDefault(); return; }
                                sessionDrag.current = number;
                                event.dataTransfer.setData('text/plain', `Session ${number}`);
                                event.dataTransfer.effectAllowed = 'move';
                                window.setTimeout(() => { if (sessionDrag.current === number) setDraggingSession(number); }, 0);
                            }}
                            onDragEnd={clearDrag}
                            sx={{ position: 'relative', display: 'flex', alignItems: 'center', backgroundColor: SURFACE_TINT, ...(canDragSession ? touchDragSourceSx : {}),
                                '&:hover [data-session-grip], &:focus-within [data-session-grip]': { opacity: 1 } }}>
                        {canDragSession && <DragIndicatorRoundedIcon data-session-grip aria-hidden="true"
                            sx={{ ...outlineGripSx(saving), left: 2, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }} />}
                        <Button aria-expanded={open}
                            onClick={() => number !== undefined && setOpenSessions(current => current.includes(number) ? current.filter(row => row !== number) : [...current, number])}
                            sx={{ flex: 1, minWidth: 0, justifyContent: 'space-between', textAlign: 'left', pl: canDragSession ? 2.25 : 1.25, pr: 0.75, py: 1.1, color: NAVY, backgroundColor: SURFACE_TINT, textTransform: 'none' }}>
                            <Box><Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 13 }}>{session ? `Session ${session.number}` : 'Other exercises'}</Typography>
                                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11 }}>{groupExercises.length} {groupExercises.length === 1 ? 'exercise' : 'exercises'}{session ? ` · about ${session.estimated_minutes} min` : ''}</Typography></Box>
                            <Stack direction="row" alignItems="center" spacing={0.25}>
                                {needsContent && <WarningAmberRoundedIcon titleAccess="Planned targets not practised yet" sx={{ color: WARNING_TEXT, fontSize: 17 }} />}
                                {/* Room for the info button, which is drawn over this gap: a button cannot sit inside the header button. */}
                                {hasInfo && <Box aria-hidden="true" sx={{ width: 28, flexShrink: 0 }} />}
                                {/* Same size and right inset as the exercise chevrons, so they line up. */}
                                {session && <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: SLATE, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />}
                            </Stack>
                        </Button>
                        {hasInfo && number !== undefined && <Box sx={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}>
                            <SessionTargetsInfo number={number} focus={infoFocus} review={infoReview} />
                        </Box>}
                        </Box>
                        <Collapse in={open}>
                            {number !== undefined && onSelectSession && <Button onClick={() => onSelectSession(number)} aria-current={overviewSelected ? 'location' : undefined}
                                sx={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', pl: 1, pr: 0.75, py: 0.75, borderRadius: 0, borderBottom: `1px solid ${BORDER}`,
                                    borderLeft: `3px solid ${overviewSelected ? BLUE : 'transparent'}`, backgroundColor: overviewSelected ? SOFT_BLUE : 'transparent', '&:hover': { backgroundColor: overviewSelected ? SOFT_BLUE : SURFACE_SUBTLE } }}>
                                <TrackChangesRoundedIcon aria-hidden="true" sx={{ fontSize: 17, color: BLUE, mr: 1 }} />
                                <Typography sx={{ flex: 1, minWidth: 0, fontFamily: INTER, fontWeight: 600, color: NAVY, fontSize: 12.5 }}>Session overview</Typography>
                                <ChevronRightRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: MUTED }} />
                            </Button>}
                            {groupExercises.map(exercise => {
                                const index = exercises.findIndex(row => row.id === exercise.id);
                                const selected = exercise.id === selectedExerciseId;
                                const exerciseSteps = orderedExerciseSteps(steps.filter(step => step.verbaschola_lesson_exercise_id === exercise.id));
                                const position = groupExercises.slice(0, groupExercises.indexOf(exercise)).filter(row => row.id !== dragging).length;
                                const overRow = number === undefined ? undefined : (event: React.DragEvent<HTMLElement>) => {
                                    if (saving || dragId.current === null) return;
                                    event.preventDefault();
                                    event.stopPropagation();
                                    event.dataTransfer.dropEffect = 'move';
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    const after = exercise.id !== dragId.current && event.clientY > rect.top + rect.height / 2;
                                    setDropAt({ session: number, index: position + (after ? 1 : 0) });
                                };
                                return <React.Fragment key={exercise.id}>
                                    {ghostIndex === position && exercise.id !== dragging && ghost}
                                    <Box data-exercise-outline-row={exercise.id} data-dragging={dragging === exercise.id || undefined} draggable={!saving}
                                        onDragStart={event => {
                                            if (saving) { event.preventDefault(); return; }
                                            dragId.current = exercise.id;
                                            event.dataTransfer.setData('text/plain', exercise.title);
                                            event.dataTransfer.effectAllowed = 'move';
                                            // The source dims after the browser captures its drag image, unless the drag already ended.
                                            window.setTimeout(() => { if (dragId.current === exercise.id) setDragging(exercise.id); }, 0);
                                        }}
                                        onDragEnd={clearDrag}
                                        onDragEnter={overRow} onDragOver={overRow}
                                        sx={{ position: 'relative', p: 0.5, opacity: dragging === exercise.id ? 0.4 : 1, transition: 'opacity 120ms ease', ...touchDragSourceSx,
                                            '&:hover [data-drag-grip], &:focus-within [data-drag-grip]': { opacity: 1 } }}>
                                        <DragIndicatorRoundedIcon data-drag-grip aria-hidden="true" sx={{ ...outlineGripSx(saving), left: 1, top: 24, fontSize: 16 }} />
                                        <Button onClick={() => onSelectExercise(exercise.id)} aria-current={selected ? 'location' : undefined}
                                            sx={{ width: '100%', justifyContent: 'flex-start', alignItems: 'center', textAlign: 'left', pl: 1.5, pr: 0.25, py: 1, borderRadius: '10px', borderLeft: `3px solid ${selected ? BLUE : 'transparent'}`, backgroundColor: selected ? SOFT_BLUE : 'transparent', '&:hover': { backgroundColor: selected ? SOFT_BLUE : SURFACE_SUBTLE } }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                                                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: selected ? BLUE_TEXT : SLATE, fontSize: 11, letterSpacing: '0.05em' }}>EXERCISE {index + 1}</Typography>
                                                    <Stack direction="row" spacing={0.5}>
                                                        {marks?.exercises[exercise.id] && <Chip data-review-mark label={marks.exercises[exercise.id]} size="small" sx={{ minHeight: 22, fontSize: 11, fontWeight: 700, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />}
                                                        <Chip label={titleCase(exercise.status)} size="small" sx={{ ...exerciseStatusChipSx(exercise.status), minHeight: 22, fontSize: 11 }} />
                                                    </Stack>
                                                </Stack>
                                                <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 13 }}>{exercise.title}</Typography>
                                                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5 }}>{exerciseSteps.length} {exerciseSteps.length === 1 ? 'step' : 'steps'} · about {exercise.content_estimated_minutes ?? 0} min</Typography>
                                                {hasTimingGap(exercise.duration_minutes, exercise.content_estimated_minutes) && <Typography sx={{ fontFamily: INTER, color: WARNING_TEXT, fontSize: 11 }}>Review timing gap</Typography>}
                                            </Box>
                                            <ChevronRightRoundedIcon aria-hidden="true" sx={{ ml: 0.25, flexShrink: 0, fontSize: 20, color: selected ? BLUE : MUTED, transform: selected ? 'rotate(90deg)' : 'none', transition: 'transform 160ms ease' }} />
                                        </Button>
                                        {selected && (() => {
                                            const stepOthers = exerciseSteps.filter(row => row.id !== draggingStep);
                                            const stepGhostIndex = draggingStep !== null && stepDropAt !== null && stepDropAt !== exerciseSteps.findIndex(row => row.id === draggingStep) ? stepDropAt : null;
                                            const dropStep = () => {
                                                const moving = stepDrag.current;
                                                const at = stepDropRef.current;
                                                clearDrag();
                                                if (moving === null || at === null) return;
                                                const rest = exerciseSteps.filter(row => row.id !== moving).map(row => row.id);
                                                const ordered = [...rest.slice(0, at), moving, ...rest.slice(at)];
                                                if (ordered.every((id, position) => id === exerciseSteps[position].id)) return;
                                                void onReorderSteps(exercise.id, ordered).catch(() => undefined);
                                            };
                                            const overStepGhost = (event: React.DragEvent<HTMLElement>) => {
                                                if (stepDrag.current === null) return;
                                                event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = 'move';
                                            };
                                            const stepGhost = <Box key="step-drop-ghost" data-testid="step-drop-ghost" aria-hidden="true" onDragEnter={overStepGhost} onDragOver={overStepGhost}
                                                onDrop={event => { if (stepDrag.current === null) return; event.preventDefault(); event.stopPropagation(); dropStep(); }}
                                                sx={{ px: 1, py: 0.6, borderRadius: '11px', border: `2px dashed ${BLUE}`, backgroundColor: SOFT_BLUE }}>
                                                <Typography noWrap sx={{ fontFamily: INTER, fontWeight: 600, color: BLUE_TEXT, fontSize: 12 }}>{exerciseSteps.find(row => row.id === draggingStep)?.title}</Typography>
                                            </Box>;
                                            return <Stack spacing={0.25} sx={{ ml: 1.5, mt: 0.25, mb: 0.5, borderLeft: `1px solid ${BORDER}`, pl: 0.75 }}>
                                            {exerciseSteps.map((step, stepIndex) => {
                                                const stepPosition = exerciseSteps.slice(0, stepIndex).filter(row => row.id !== draggingStep).length;
                                                const overStep = (event: React.DragEvent<HTMLElement>) => {
                                                    if (saving || stepDrag.current === null) return;
                                                    event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = 'move';
                                                    const rect = event.currentTarget.getBoundingClientRect();
                                                    const after = step.id !== stepDrag.current && event.clientY > rect.top + rect.height / 2;
                                                    setStepDropAt(stepPosition + (after ? 1 : 0));
                                                };
                                                return <React.Fragment key={step.id}>
                                                {stepGhostIndex === stepPosition && step.id !== draggingStep && stepGhost}
                                                <Box data-step-outline-row={step.id} data-dragging={draggingStep === step.id || undefined} draggable={!saving && exerciseSteps.length > 1}
                                                    onDragStart={event => {
                                                        // Steps drag inside their exercise; the exercise row around them must not start its own drag.
                                                        event.stopPropagation();
                                                        if (saving) { event.preventDefault(); return; }
                                                        stepDrag.current = step.id;
                                                        event.dataTransfer.setData('text/plain', step.title ?? '');
                                                        event.dataTransfer.effectAllowed = 'move';
                                                        window.setTimeout(() => { if (stepDrag.current === step.id) setDraggingStep(step.id); }, 0);
                                                    }}
                                                    onDragEnd={event => { event.stopPropagation(); clearDrag(); }}
                                                    onDragEnter={overStep} onDragOver={overStep}
                                                    onDrop={event => { if (stepDrag.current === null) return; event.preventDefault(); event.stopPropagation(); dropStep(); }}
                                                    sx={{ position: 'relative', opacity: draggingStep === step.id ? 0.4 : 1, ...touchDragSourceSx, '&:hover [data-step-grip]': { opacity: 1 } }}>
                                                {exerciseSteps.length > 1 && <DragIndicatorRoundedIcon data-step-grip aria-hidden="true"
                                                    sx={{ ...outlineGripSx(saving), left: -2, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }} />}
                                                <Button fullWidth onClick={() => onSelectStep(exercise.id, step.id)} aria-current={selectedStepId === step.id ? 'step' : undefined}
                                                sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', fontFamily: INTER, color: selectedStepId === step.id ? BLUE_TEXT : SLATE, backgroundColor: selectedStepId === step.id ? SOFT_BLUE : 'transparent', fontWeight: selectedStepId === step.id ? 700 : 600, fontSize: 12, borderRadius: '11px' }}>
                                                {stepIndex + 1}. {step.title}
                                                {marks?.steps[step.id] && <Box component="span" data-review-mark sx={{ ml: 'auto', pl: 1, flexShrink: 0, fontSize: 11, fontWeight: 700, color: BLUE_TEXT }}>{marks.steps[step.id]}</Box>}
                                            </Button>
                                                </Box>
                                                </React.Fragment>;
                                            })}
                                            {stepGhostIndex === stepOthers.length && stepGhost}
                                            {marks?.removed.filter(row => row.exerciseId === exercise.id).map(row => <Box key={`removed-${row.id}`} data-review-removed-step={row.id}
                                                sx={{ px: 1, py: 0.5, border: `1px dashed ${BORDER}`, borderRadius: '10px' }}>
                                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, textDecoration: 'line-through', overflowWrap: 'anywhere' }}>{row.position}. {row.title}</Typography>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography sx={{ fontFamily: INTER, fontSize: 11, fontWeight: 700, color: SLATE }}>Removed</Typography>
                                                    <Button size="small" onClick={() => marks.onViewOriginal(row.id)} sx={{ minHeight: 28, textTransform: 'none' }}>View original</Button>
                                                </Stack>
                                            </Box>)}
                                        </Stack>;
                                        })()}
                                    </Box>
                                </React.Fragment>;
                            })}
                            {ghostIndex === others.length && ghost}
                            {number !== undefined && groupExercises.length === 0 && ghostIndex === null && <Typography sx={{ px: 1.25, py: 1, fontFamily: INTER, color: SLATE, fontSize: 11.5 }}>No exercises yet. Drag one here.</Typography>}
                        </Collapse>
                    </Box>
                    {number !== undefined && number === sessionNumbers[sessionNumbers.length - 1] && sessionGhostIndex === sessionOthers.length && sessionGhost}
                    </React.Fragment>;
                })}
                {dragging !== null && newSessionNumber <= MAX_SESSION_NUMBER && <Box data-testid="exercise-drop-new-session" data-drop-target={dropAt?.session === newSessionNumber || undefined}
                    onDragEnter={overNewSession} onDragOver={overNewSession}
                    onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null) && dropRef.current?.session === newSessionNumber) setDropAt(null); }}
                    onDrop={event => { event.preventDefault(); dropExercise(newSessionNumber); }}
                    sx={{ px: 1.25, py: 1.5, borderRadius: '16px', border: `2px dashed ${dropAt?.session === newSessionNumber ? BLUE : BORDER}`, backgroundColor: dropAt?.session === newSessionNumber ? SOFT_BLUE : 'transparent', textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 600, color: dropAt?.session === newSessionNumber ? BLUE_TEXT : SLATE, fontSize: 12 }}>Drop here to start Session {newSessionNumber}</Typography>
                </Box>}
            </Stack>
        </Box>
        <Divider />
        <Box component="details" data-exercise-edit-scope="exercise:add" sx={{ p: 1.5, display: readOnly ? 'none' : undefined }}><Box component="summary">Add exercise manually</Box><Stack spacing={1}>
            {!readOnly && <><TextField size="small" label="New exercise" value={newTitle} onChange={event => setNewTitle(event.target.value)} fullWidth />
            <Button fullWidth variant="outlined" startIcon={<AddRoundedIcon />} disabled={saving || newTitle.trim().length === 0}
                onClick={() => { setAddError(null); void onAddExercise({ title: newTitle.trim(), subtitle: null, status: 'draft', duration_minutes: null, purpose: null, icon_key: null, teacher_instructions: null, student_instructions: null, session_number: exercises.find(exercise => exercise.id === selectedExerciseId)?.session_number ?? sessionNumbers[sessionNumbers.length - 1] ?? 1, order_index: Math.max(0, ...exercises.map(row => row.order_index ?? row.id)) + 1 })
                    .then(() => setNewTitle('')).catch(caught => setAddError(lessonAuthoringError(caught, 'Could not add this exercise. Try again.'))); }}
                sx={{ borderColor: BORDER, color: BLUE, fontFamily: INTER, fontSize: 13, py: 1.25, textTransform: 'none' }}>Add exercise</Button>
            {addError && <Alert severity="error">{addError}</Alert>}</>}
        </Stack></Box>
    </Box>;
}

/** A session, exercise or step grip in the outline. It appears on hover or focus, and always on touch screens,
 *  where a row is picked up by touching and holding it, then dragged as with a mouse (see touchDragAndDrop). */
function outlineGripSx(saving: boolean) {
    return { position: 'absolute', zIndex: 1, color: MUTED, opacity: 0, transition: 'opacity 120ms ease', cursor: saving ? 'default' : 'grab',
        '@media (hover: none)': { opacity: 1 } } as const;
}

/** The lesson's content time and target coverage, kept out of the outline so it stays a list. */
function LessonCoverageCard({ targetCoverage, exerciseCount, totalMinutes, savedMinutes, contentGoal, saving, onFillCoverageGaps }: {
    targetCoverage?: IVerbaScholaTargetCoverage;
    exerciseCount: number;
    totalMinutes: number;
    savedMinutes: number;
    contentGoal: number | null;
    saving: boolean;
    onFillCoverageGaps?: () => void;
}) {
    if (exerciseCount === 0 && !targetCoverage) return null;
    const line = (text: React.ReactNode, warning = false) => <Typography sx={{ fontFamily: INTER, color: warning ? WARNING_TEXT : SLATE, fontSize: 13 }}>{text}</Typography>;
    const coverage = targetCoverage && targetCoverage.vocabulary.total + targetCoverage.grammar.total > 0 ? targetCoverage : null;
    const readyDiffers = coverage && (coverage.ready.vocabulary.practised !== coverage.vocabulary.practised || coverage.ready.grammar.practised !== coverage.grammar.practised);
    return <Box data-testid="lesson-target-coverage" sx={{ mb: 2 }}><VerbaScholaCard>
        <VerbaScholaCardHeader title="Lesson coverage" meta={`${contentGoal ? `About ${totalMinutes}/${contentGoal}` : `About ${totalMinutes}`} min of activity content`}
            actions={coverage?.has_gaps && onFillCoverageGaps ? <Button size="small" variant="outlined" disabled={saving} onClick={onFillCoverageGaps}
                sx={{ textTransform: 'none', fontFamily: INTER }}>Fill coverage gaps</Button> : undefined} />
        <Stack spacing={0.75} sx={{ p: { xs: 2, sm: 2.75 } }}>
            {line(`Saved timing labels: ${savedMinutes} min. Students are not timed.`)}
            {contentGoal && totalMinutes < contentGoal && exerciseCount > 0 && line('The current activities may not fill the target.', true)}
            {coverage && <>
                {line(`Words practised: ${coverage.vocabulary.practised}/${coverage.vocabulary.total} · Grammar tagged: ${coverage.grammar.practised}/${coverage.grammar.total}`)}
                {readyDiffers && line(`In Ready exercises (what students get): ${coverage.ready.vocabulary.practised}/${coverage.ready.vocabulary.total} words · ${coverage.ready.grammar.practised}/${coverage.ready.grammar.total} grammar`)}
                {coverage.vocabulary.missing.length > 0 && line(`Words not practised yet: ${coverage.vocabulary.missing.join(', ')}`, true)}
                {coverage.grammar.missing.length > 0 && line(`Grammar not tagged yet: ${coverage.grammar.missing.join(', ')}`, true)}
                {coverage.planned_session_count > coverage.time_session_count && coverage.time_session_count > 0 && line(`${coverage.planned_session_count} sessions are planned so every word and grammar point fits (${coverage.vocabulary_per_session} words and ${coverage.grammar_per_session} grammar points each); the exercise target alone gives ${coverage.time_session_count}.`)}
                {coverage.sessions_without_exercises.length > 0 && <Box>
                    {line(`Planned sessions without exercises yet: ${coverage.sessions_without_exercises.join(', ')}`, true)}
                    {/* These have no outline header, so their exact plan is offered here with the same info icon. */}
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 0.75 }}>
                        {coverage.sessions_without_exercises.map(number => {
                            const plan = coverage.planned_focus?.find(row => row.session_number === number);
                            if (!plan || plan.focus.length + (plan.review?.length ?? 0) === 0) return null;
                            return <Stack key={number} data-planned-session={number} direction="row" alignItems="center"
                                sx={{ pl: 1.25, borderRadius: '999px', border: `1px solid ${BORDER}`, backgroundColor: '#fff' }}>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 600, color: NAVY, fontSize: 12.5 }}>Session {number}</Typography>
                                <SessionTargetsInfo number={number} focus={plan.focus} review={plan.review ?? []} />
                            </Stack>;
                        })}
                    </Stack>
                </Box>}
            </>}
        </Stack>
    </VerbaScholaCard></Box>;
}

function LanguageSummary({ vocabularyTargets, grammarTargets, coverage, location, titleControls }: { vocabularyTargets: IVerbaScholaLessonVocabularyTarget[]; grammarTargets: IVerbaScholaLessonGrammarTarget[]; coverage?: React.ReactNode;
    /** The breadcrumb, shown below the title. */
    location: React.ReactNode;
    titleControls: StageTitleControls }) {
    return (
        <>
            <ExerciseViewHeader title="Vocabulary & grammar" titleControls={titleControls} location={location}
                details={<Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13.5 }}>{vocabularyTargets.length} vocabulary items - {grammarTargets.length} grammar points</Typography>} />
            {coverage}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <VerbaScholaCard>
                    <VerbaScholaCardHeader title="Vocabulary" />
                    <Stack spacing={1}
                        sx={{ p: 2 }}>{vocabularyTargets.length === 0 ? <VerbaScholaEmptyState message="No vocabulary targets yet." /> : vocabularyTargets.map(target => <Box key={target.id}
                        sx={{ p: 1.25, borderRadius: '12px', backgroundColor: SURFACE_TINT }}><Typography
                        sx={{ fontFamily: POPPINS, color: NAVY, fontWeight: 600, fontSize: 13 }}>{target.hanzi}</Typography><Typography
                        sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>{target.meaning ?? 'No meaning'}</Typography></Box>)}</Stack>
                </VerbaScholaCard>
                <VerbaScholaCard>
                    <VerbaScholaCardHeader title="Grammar" />
                    <Stack spacing={1}
                        sx={{ p: 2 }}>{grammarTargets.length === 0 ? <VerbaScholaEmptyState message="No grammar targets yet." /> : grammarTargets.map(target => <Box key={target.id}
                        sx={{ p: 1.25, borderRadius: '12px', backgroundColor: SURFACE_TINT }}><Typography
                        sx={{ fontFamily: POPPINS, color: NAVY, fontWeight: 600, fontSize: 13 }}>{target.pattern}</Typography><Typography
                        sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>{target.meaning ?? 'No meaning'}</Typography></Box>)}</Stack>
                </VerbaScholaCard>
            </Box>
        </>
    );
}

function ExercisePracticeTargetsEditor({ exerciseId, vocabularyTargets, grammarTargets, tags, saving, onSave }: {
    exerciseId: number;
    vocabularyTargets: IVerbaScholaLessonVocabularyTarget[];
    grammarTargets: IVerbaScholaLessonGrammarTarget[];
    tags?: IVerbaScholaExercisePracticeTargets;
    saving: boolean;
    onSave: (vocabularyTargetIds: number[], grammarTargetIds: number[]) => Promise<void>;
}) {
    const [vocabulary, setVocabulary] = React.useState<number[]>(tags?.vocabulary_target_ids ?? []);
    const [grammar, setGrammar] = React.useState<number[]>(tags?.grammar_target_ids ?? []);
    if (vocabularyTargets.length + grammarTargets.length === 0) return null;
    const same = (left: number[], right: number[]) => left.length === right.length && left.every(id => right.includes(id));
    // Saving an unrecorded exercise records the choice, even "none", so it no longer falls back to text matching.
    const changed = !tags?.explicit || !same(vocabulary, tags?.vocabulary_target_ids ?? []) || !same(grammar, tags?.grammar_target_ids ?? []);
    const toggle = (current: number[], id: number) => current.includes(id) ? current.filter(row => row !== id) : [...current, id];
    const chips = (rows: { id: number; label: string }[], selected: number[], set: (ids: number[]) => void) => <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
        {rows.map(row => <Chip key={row.id} label={row.label} size="small" clickable aria-pressed={selected.includes(row.id)}
            color={selected.includes(row.id) ? 'primary' : 'default'} variant={selected.includes(row.id) ? 'filled' : 'outlined'}
            onClick={() => set(toggle(selected, row.id))} sx={{ fontFamily: INTER }} />)}
    </Stack>;
    // Its own edit scope, so saving the tags clears exactly the unsaved state its chips set.
    return <Box role="group" aria-label="Practice targets" data-exercise-edit-scope={`exercise-targets:${exerciseId}`} sx={{ pt: 1 }}>
        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 0.5 }}>PRACTISES</Typography>
        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mb: 1 }}>Select the words and grammar this exercise practises. A selected word counts only when it also appears in an activity.</Typography>
        {!tags?.explicit && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mb: 1 }}>Not recorded yet, so words are found in the activity text. Saving records your choice, even if nothing is selected.</Typography>}
        {vocabularyTargets.length > 0 && <Box sx={{ mb: 1 }}><Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mb: 0.5 }}>Words</Typography>
            {chips(vocabularyTargets.map(row => ({ id: row.id, label: row.hanzi })), vocabulary, setVocabulary)}</Box>}
        {grammarTargets.length > 0 && <Box sx={{ mb: 1 }}><Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mb: 0.5 }}>Grammar</Typography>
            {chips(grammarTargets.map(row => ({ id: row.id, label: row.pattern })), grammar, setGrammar)}</Box>}
        <Button size="small" variant="outlined" disabled={saving || !changed} onClick={() => void onSave(vocabulary, grammar).catch(() => undefined)}
            sx={{ textTransform: 'none', fontFamily: INTER }}>Save practice targets</Button>
    </Box>;
}

function ExercisePictureReviewCard({ review, checkedKeys, loadState, onCheck, onImageLoad, onEdit }: {
    review: IVerbaScholaExercisePictureReview;
    checkedKeys: Set<string>;
    loadState: Record<string, 'loaded' | 'failed'>;
    onCheck: (key: string, checked: boolean) => void;
    onImageLoad: (key: string, state: 'loaded' | 'failed') => void;
    onEdit: (picture: IVerbaScholaExercisePictureReviewItem) => void;
}) {
    const [expanded, setExpanded] = React.useState(!review.reviewed);
    React.useEffect(() => { if (!review.reviewed) setExpanded(true); }, [review.reviewed, review.digest]);
    if (review.pictures.length === 0 && !review.missing_pictures?.length && !review.requires_review) return null;
    const allPictures = [...review.pictures, ...(review.missing_pictures ?? [])];
    const confirmedCount = review.pictures.filter(picture => checkedKeys.has(picture.key) && picture.alt?.trim() && loadState[picture.key] !== 'failed').length;
    const nextPictureIndex = allPictures.findIndex(picture => !checkedKeys.has(picture.key) || picture.missing || !picture.url || !picture.alt?.trim() || loadState[picture.key] === 'failed');
    const moveToNextPicture = () => {
        setExpanded(true);
        window.requestAnimationFrame(() => {
            const next = document.getElementById(`picture-review-${nextPictureIndex}`);
            next?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
            next?.focus({ preventScroll: true });
        });
    };
    return <VerbaScholaCard><Stack data-exercise-picture-review tabIndex={-1} spacing={1.5} sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 17 }}>Images to review</Typography>
            <Chip size="small" color={review.reviewed ? 'success' : 'warning'} label={review.reviewed ? 'Reviewed for current content' : 'Review needed'} />
            <Button size="small" data-picture-review-toggle aria-expanded={expanded} onClick={() => setExpanded(value => !value)} sx={{ ml: 'auto' }}>
                {expanded ? 'Collapse checklist' : 'Show checklist'}
            </Button>
        </Stack>
        <Typography role="status" sx={{ fontFamily: INTER, color: SLATE, fontWeight: 700, fontSize: 12 }}>
            {allPictures.length === 0 ? 'A required picture is missing' : `${confirmedCount} of ${allPictures.length} ${allPictures.length === 1 ? 'picture' : 'pictures'} confirmed`}
        </Typography>
        <Collapse in={expanded}><Stack spacing={1.5}>
        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13 }}>
            Check that each picture loads, depicts the intended meaning, and helps the student complete this activity. AI quality scores are suggestions; your judgment decides whether to use the picture.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {nextPictureIndex >= 0 && <Button size="small" onClick={moveToNextPicture}>Next picture to review</Button>}
        </Stack>
        {allPictures.length === 0 && <Alert severity="warning">A required picture is missing. Open the activity and add a picture before marking this exercise Ready.</Alert>}
        {allPictures.map((picture, index) => {
            const failed = loadState[picture.key] === 'failed';
            const missing = picture.missing || !picture.url;
            const loaded = loadState[picture.key] === 'loaded';
            const noDescription = !picture.alt?.trim();
            const score = picture.quality_review?.score;
            const disabledReason = missing ? 'A picture is missing.' : failed ? 'This picture could not load.' : noDescription ? 'Add a picture description.' : !loaded ? 'Waiting for this picture to load.' : null;
            return <Box id={`picture-review-${index}`} data-picture-review-key={picture.key} key={picture.key} tabIndex={-1} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.5, scrollMarginTop: 90, '&:focus-visible': { outline: `3px solid ${BLUE}` } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                    {picture.url && !picture.missing && <Box component="img" key={picture.url} src={lessonImageUrl(picture.url)} alt={picture.alt || 'Picture to review'} loading="eager"
                        onLoad={() => onImageLoad(picture.key, 'loaded')} onError={() => onImageLoad(picture.key, 'failed')}
                        sx={{ display: failed ? 'none' : 'block', width: { xs: '100%', sm: 140 }, height: 120, objectFit: 'contain', bgcolor: SURFACE_MUTED, borderRadius: 1 }} />}
                    <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 13 }}>{picture.context}</Typography>
                        {picture.alt && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>Description for students: {picture.alt}</Typography>}
                        {picture.source && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>Source: {picture.source}</Typography>}
                        {typeof score === 'number' && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>
                            {isEarlierImagePass(picture.quality_review) ? 'Earlier AI score (unverified)' : 'AI quality score'}: {score}/100
                        </Typography>}
                        {isEarlierImagePass(picture.quality_review) && <Alert severity="warning" sx={{ py: 0 }}>
                            This picture passed an earlier AI check. The current image-only comparison has not reviewed it. Inspect the picture and confirm or replace it.
                        </Alert>}
                        {picture.quality_review?.rationale && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>
                            {isEarlierImagePass(picture.quality_review) ? 'Earlier AI note: ' : ''}{picture.quality_review.rationale}
                        </Typography>}
                        {picture.quality_review?.critical_checks?.filter(check => !check.passed).map((check, index) =>
                            <Typography key={`${check.name}:${index}`} sx={{ fontFamily: INTER, color: WARNING_TEXT, fontSize: 12 }}>
                                AI check to inspect: {check.name.replaceAll('_', ' ')}{check.evidence ? ` — ${check.evidence}` : ''}
                            </Typography>)}
                        {picture.quality_review?.accepted === false && <Alert severity="warning" sx={{ py: 0 }}>The AI review flagged this picture. Inspect it closely or choose another.</Alert>}
                        {(missing || failed) && <Alert severity="error" sx={{ py: 0 }}>{missing ? 'A picture is missing from this activity.' : 'This picture could not load.'} Open the activity to replace it.</Alert>}
                        {noDescription && <Alert severity="warning" sx={{ py: 0 }}>Add a description for students before confirming this picture.</Alert>}
                        <Button size="small" sx={{ alignSelf: 'flex-start', textTransform: 'none' }} onClick={() => onEdit(picture)}>
                            Open activity to change picture
                        </Button>
                    </Stack>
                </Stack>
                <FormControlLabel sx={{ mt: 1, ml: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontFamily: INTER, color: SLATE, fontSize: 12 } }}
                    control={<Checkbox sx={{ minWidth: 44, minHeight: 44, mt: -1 }} checked={checkedKeys.has(picture.key)}
                        inputProps={{ 'aria-label': `Confirm picture ${index + 1} of ${allPictures.length}` }}
                        disabled={Boolean(disabledReason)} onChange={event => onCheck(picture.key, event.target.checked)} />}
                    label="This picture is correct and useful for the activity." />
                {disabledReason && <Typography role="status" sx={{ fontFamily: INTER, color: WARNING_TEXT, fontSize: 12 }}>{disabledReason} Confirmation is unavailable until this is fixed.</Typography>}
            </Box>;
        })}
        </Stack></Collapse>
    </Stack></VerbaScholaCard>;
}

/** An activity editor shown for review: readable, but no field, button, menu or chip responds. */
function ReviewLockedStep({ children }: { children: React.ReactNode }) {
    return <Box component="fieldset" disabled data-review-locked sx={{ border: 0, p: 0, m: 0, minWidth: 0,
        '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: NAVY, color: NAVY },
        '& .MuiInputLabel-root.Mui-disabled, & .MuiFormHelperText-root.Mui-disabled': { color: SLATE },
        '& .MuiSelect-select, & .MuiChip-clickable, & [role="button"], & input[type="checkbox"]': { pointerEvents: 'none' },
        // Editing actions do nothing here; showing them would invite a click.
        '& .MuiButton-root, & .MuiIconButton-root': { display: 'none' } }}>{children}</Box>;
}

function ExerciseDetail({ readinessDisabled, onReviseExercise, renderStep, renderReviewStep, exercise, steps, activeStep, sessionNumber,
    viewMode, onViewModeChange, onSelectStep, choices, sentences, conversations, saving, onSaveExercise,
    onDeleteExercise, onAddStep, onUpdateStep, onDeleteStep, onChildMutation, onMarkDirty, onCancelScope, authoringRevision, courseId, lessonId, practiceTargetEditor, reviewTextFocus, location, titleControls, studentSteps, reviewExerciseField }: {
    /** Student views of proposed steps from a review projection, by step id. */
    studentSteps?: Record<string, IVerbaScholaStudentStep>;
    reviewExerciseField?: { exerciseId: number; field: string } | null;
    /** The breadcrumb, shown below the title. */
    location: React.ReactNode;
    titleControls: StageTitleControls;
    readinessDisabled: boolean;
    practiceTargetEditor?: React.ReactNode;
    onReviseExercise?: (id: number) => void;
    renderStep?: (step: IVerbaScholaLessonExerciseStep) => React.ReactNode;
    renderReviewStep?: (step: IVerbaScholaLessonExerciseStep) => React.ReactNode;
    exercise: IVerbaScholaLessonExercise;
    steps: IVerbaScholaLessonExerciseStep[];
    activeStep: IVerbaScholaLessonExerciseStep | null;
    reviewTextFocus?: { stepId: number; issueId: number; message: string; key: number } | null;
    /** Shown for context; exercises and their steps move by dragging in the outline. */
    sessionNumber?: number;
    viewMode: 'edit' | 'preview';
    onViewModeChange: (mode: 'edit' | 'preview') => void;
    onSelectStep: (stepId: number) => void;
    choices: IVerbaScholaExerciseChoice[];
    sentences: IVerbaScholaSpeakingSentence[];
    conversations: IVerbaScholaExerciseConversation[];
    saving: boolean;
    onSaveExercise: (exerciseId: number, payload: Partial<IVerbaScholaLessonExercise>, localError?: boolean) => Promise<void>;
    onDeleteExercise: (exerciseId: number) => Promise<void>;
    onAddStep: (payload: Partial<IVerbaScholaLessonExerciseStep>) => Promise<void>;
    onUpdateStep: (stepId: number, payload: Partial<IVerbaScholaLessonExerciseStep>, scope?: string) => Promise<void>;
    onDeleteStep: (stepId: number) => Promise<void>
    onChildMutation: (operation: () => Promise<unknown>, scope: string) => Promise<void>;
    onMarkDirty?: (scope: string) => void;
    onCancelScope?: (scope: string) => void;
    authoringRevision: number;
    courseId: number; lessonId: number;
}) {
    const [title, setTitle] = React.useState(exercise.title);
    const [subtitle, setSubtitle] = React.useState(exercise.subtitle ?? '');
    const [durationMinutes, setDurationMinutes] = React.useState(exercise.duration_minutes?.toString() ?? '');
    const [purpose, setPurpose] = React.useState(exercise.purpose ?? '');
    const [teacherInstructions, setTeacherInstructions] = React.useState(exercise.teacher_instructions ?? '');
    const [studentInstructions, setStudentInstructions] = React.useState(exercise.student_instructions ?? '');
    const [newStepTitle, setNewStepTitle] = React.useState('');
    const [addStepError, setAddStepError] = React.useState<string | null>(null);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [pictureReview, setPictureReview] = React.useState<IVerbaScholaExercisePictureReview | null>(null);
    const [pictureReviewLoading, setPictureReviewLoading] = React.useState(true);
    const [pictureReviewError, setPictureReviewError] = React.useState<string | null>(null);
    const [pictureReviewLoadVersion, setPictureReviewLoadVersion] = React.useState(0);
    const [checkedPictureKeys, setCheckedPictureKeys] = React.useState<Set<string>>(new Set());
    const [pictureLoadState, setPictureLoadState] = React.useState<Record<string, 'loaded' | 'failed'>>({});
    const [pictureToEdit, setPictureToEdit] = React.useState<IVerbaScholaExercisePictureReviewItem | null>(null);
    const [pictureEditOpenAttempt, setPictureEditOpenAttempt] = React.useState(0);
    const [reviewTextMissing, setReviewTextMissing] = React.useState(false);
    const lastReviewModeKey = React.useRef<number | null>(null);
    const [newStepType, setNewStepType] = React.useState<string>('introduce_word');
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    // Your version under review: the exercise's own fields can be edited and kept in the review draft.
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const fieldReadOnly = readOnly && !reviewEdit;
    // In Your version each change goes into the review draft as it is typed (untrimmed; the review's save trims it), so switching versions never loses it.
    const keepInReview = (fields: Partial<IVerbaScholaLessonExercise>) => { if (readOnly && reviewEdit) void onSaveExercise(exercise.id, fields).catch(() => undefined); };
    // An emptied title is not kept (an exercise needs one): it shows as required and counts as an open edit, so saving
    // or moving on asks first instead of quietly keeping the title Your version had.
    const titleMissing = readOnly && reviewEdit && !title.trim();
    useReviewUnkept(`exercise:${exercise.id}:title`, titleMissing);
    const [settingsOpen, setSettingsOpen] = React.useState(false);
    // A reviewed change to a field inside Exercise settings opens it, so the highlight shows the field itself.
    const reviewedSetting = readOnly && reviewExerciseField?.exerciseId === exercise.id
        && ['title', 'duration_minutes', 'subtitle', 'settings'].includes(reviewExerciseField.field) ? reviewExerciseField.field : null;
    React.useEffect(() => { if (reviewedSetting) setSettingsOpen(true); }, [reviewedSetting]);
    const activeIndex = steps.findIndex(step => step.id === activeStep?.id);
    const activityKinds = [...new Set(steps.map(step => stepClassNameLabel(step.class_name)))];
    const exerciseType = activityKinds.length === 1 ? activityKinds[0] : 'Mixed practice';

    React.useEffect(() => {
        if (!reviewTextFocus || reviewTextFocus.stepId !== activeStep?.id || lastReviewModeKey.current === reviewTextFocus.key) return;
        lastReviewModeKey.current = reviewTextFocus.key;
        if (viewMode !== 'edit') onViewModeChange('edit');
    }, [reviewTextFocus, activeStep?.id, viewMode, onViewModeChange]);

    React.useEffect(() => {
        setReviewTextMissing(false);
        if (!reviewTextFocus || reviewTextFocus.stepId !== activeStep?.id || viewMode !== 'edit') return;
        let attempts = 0;
        let timer: number | undefined;
        let highlight: HTMLElement | null = null;
        const tryReveal = () => {
            const root = document.querySelector<HTMLElement>(`[data-exercise-step-id="${reviewTextFocus.stepId}"]`);
            const result = root ? revealExerciseReviewText(root, reviewTextFocus.message) : { status: 'waiting' as const };
            if (result.status === 'found') {
                highlight = result.target;
                highlight.setAttribute('data-exercise-review-text-highlight', 'true');
                timer = window.setTimeout(() => highlight?.removeAttribute('data-exercise-review-text-highlight'), 3500);
            } else if (result.status !== 'unquoted' && ++attempts < 30) {
                timer = window.setTimeout(tryReveal, 80);
            } else if (result.status === 'missing') {
                setReviewTextMissing(reviewChineseQuotes(reviewTextFocus.message).length > 0);
            }
        };
        timer = window.setTimeout(tryReveal, 0);
        return () => { window.clearTimeout(timer); highlight?.removeAttribute('data-exercise-review-text-highlight'); };
    }, [reviewTextFocus, activeStep?.id, viewMode]);

    React.useEffect(() => {
        let cancelled = false;
        setPictureReview(null);
        setPictureReviewError(null);
        // A reviewed version can hold rows that exist only in its projection; the checklist reads saved rows.
        setPictureReviewLoading(!readOnly);
        if (readOnly) return;
        setCheckedPictureKeys(new Set());
        setPictureLoadState({});
        void VerbaScholaApi.getExercisePictureReview(exercise.id).then(response => {
            if (cancelled) return;
            if (!Array.isArray(response.data?.pictures) || typeof response.data?.reviewed !== 'boolean') throw new Error('invalid_picture_review');
            setPictureReview(response.data);
            setCheckedPictureKeys(new Set(response.data.reviewed ? response.data.pictures.map(picture => picture.key) : []));
        }).catch(caught => {
            if (!cancelled) setPictureReviewError(lessonAuthoringError(caught, 'Could not load the picture checklist. Retry before marking this exercise Ready.'));
        }).finally(() => { if (!cancelled) setPictureReviewLoading(false); });
        return () => { cancelled = true; };
    }, [exercise.id, authoringRevision, pictureReviewLoadVersion, readOnly]);
    const reviewPictures = pictureReview?.pictures ?? [];
    const picturesCheckable = Boolean(pictureReview) && !pictureReview?.missing_pictures?.length
        && (!pictureReview?.requires_review || reviewPictures.length > 0)
        && reviewPictures.every(picture => Boolean(picture.url) && !picture.missing && Boolean(picture.alt?.trim()) && pictureLoadState[picture.key] === 'loaded');
    const picturesConfirmed = reviewPictures.every(picture => checkedPictureKeys.has(picture.key));
    const picturesNeedConfirmation = Boolean(pictureReview?.pictures.length || pictureReview?.requires_review);
    const readyReviewBlocked = pictureReviewLoading || !pictureReview || Boolean(pictureReviewError)
        || (picturesNeedConfirmation && (!picturesCheckable || !picturesConfirmed));
    const markExerciseReady = () => {
        setSaveError(null);
        void onSaveExercise(exercise.id, {
            status: 'ready',
            ...(picturesNeedConfirmation ? { picture_review_keys: reviewPictures.map(picture => picture.key) } : {}),
        }, true).catch(caught => setSaveError(lessonAuthoringError(caught, 'Could not mark this exercise Ready. Check the picture review and try again.')));
    };

    React.useLayoutEffect(() => {
        if (!pictureToEdit || viewMode !== 'edit' || activeStep?.id !== pictureToEdit.step_id) return;
        const stepElement = document.querySelector<HTMLElement>(`[data-exercise-step-id="${pictureToEdit.step_id}"]`);
        if (!stepElement) return;
        const collapsedActivity = stepElement.querySelector<HTMLElement>('button[aria-label$=" details"][aria-expanded="false"]');
        if (collapsedActivity && pictureEditOpenAttempt < 3) {
            collapsedActivity.click();
            const frame = window.requestAnimationFrame(() => setPictureEditOpenAttempt(value => value + 1));
            return () => window.cancelAnimationFrame(frame);
        }
        const nativeDetails = stepElement.querySelector<HTMLDetailsElement>(`details[data-exercise-edit-scope="native:${pictureToEdit.step_id}"]`);
        if (pictureToEdit.key.endsWith(':question') && nativeDetails) nativeDetails.open = true;
        const choiceId = /^choice:(\d+):/.exec(pictureToEdit.key)?.[1];
        const scope = choiceId ? stepElement.querySelector<HTMLElement>(`[data-exercise-edit-scope="choice:${choiceId}"]`) : stepElement;
        const fieldName = pictureToEdit.key.endsWith(':question') ? 'Question picture'
            : pictureToEdit.key.endsWith(':left') ? 'Left card picture'
                : pictureToEdit.key.endsWith(':right') ? 'Right card picture'
                    : choiceId ? 'Answer picture' : 'Activity picture';
        const field = [...(scope?.querySelectorAll<HTMLElement>('[data-exercise-picture-field]') ?? [])]
            .find(element => element.dataset.exercisePictureField === fieldName);
        const target = field ?? scope ?? stepElement;
        target.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        target.focus({ preventScroll: true });
        setPictureToEdit(null);
    }, [pictureToEdit, activeStep?.id, viewMode, pictureEditOpenAttempt]);

    React.useEffect(() => {
        setTitle(exercise.title);
        setSubtitle(exercise.subtitle ?? '');
        setDurationMinutes(exercise.duration_minutes?.toString() ?? '');
        setPurpose(exercise.purpose ?? '');
        setTeacherInstructions(exercise.teacher_instructions ?? '');
        setStudentInstructions(exercise.student_instructions ?? '');
    }, [exercise]);
    const cancelExerciseChanges = () => {
        setTitle(exercise.title); setSubtitle(exercise.subtitle ?? '');
        setDurationMinutes(exercise.duration_minutes?.toString() ?? '');
        setPurpose(exercise.purpose ?? ''); setTeacherInstructions(exercise.teacher_instructions ?? '');
        setStudentInstructions(exercise.student_instructions ?? ''); setSaveError(null);
        onCancelScope?.(`exercise:${exercise.id}`);
    };

    return (
        <>
            {/* The breadcrumb names this an exercise, so the details start with its length. */}
            <ExerciseViewHeader title={exercise.title} titleControls={titleControls} location={location}
                controls={<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ToggleButtonGroup exclusive size="small" value={viewMode} onChange={(_event, value: 'edit' | 'preview' | null) => value && onViewModeChange(value)} aria-label="Exercise view">
                        <ToggleButton value="edit" disabled={saving || readinessDisabled} sx={{ fontFamily: INTER, px: 2 }}>Edit</ToggleButton>
                        <ToggleButton value="preview" disabled={saving || readinessDisabled} sx={{ fontFamily: INTER, px: 2 }}>Student preview</ToggleButton>
                    </ToggleButtonGroup>
                    {viewMode === 'preview' && !readOnly && (exercise.status !== 'ready' || (picturesNeedConfirmation && pictureReview?.reviewed === false)) &&
                        <Button startIcon={<CheckCircleRoundedIcon />} disabled={saving || readinessDisabled || steps.length === 0 || readyReviewBlocked}
                            onClick={markExerciseReady} sx={{ minHeight: 44 }}>
                            {exercise.status === 'ready' ? 'Confirm pictures' : 'Mark ready'}
                        </Button>}
                </Stack>}
                details={<Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13.5 }}>
                    {exercise.subtitle || exerciseType} · {steps.length} student {steps.length === 1 ? 'turn' : 'turns'}
                    {sessionNumber && <Box component="span" sx={{ color: MUTED }}> · Session {sessionNumber}</Box>}
                    {/* Written like the session overview's length. */}
                    {` · about ${exercise.duration_minutes ?? exercise.content_estimated_minutes ?? 0} min`}
                </Typography>} />
            {activeStep && <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '16px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1.25, backgroundColor: '#fff', border: `1px solid ${BORDER}` }}>
                <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: '12px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 700, color: BLUE_TEXT, fontSize: 12 }}>{activeIndex + 1}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE, fontSize: 11, letterSpacing: '0.06em' }}>STEP {activeIndex + 1} OF {steps.length}</Typography>
                        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11 }}>· Student turn</Typography>
                    </Stack>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 14, mt: 0.25 }}>{activeStep.title}</Typography>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, mt: 0.15 }}>{stepClassNameLabel(activeStep.class_name)}</Typography>
                </Box>
                {/* Steps are reordered by dragging them in the outline. */}
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Button disabled={activeIndex <= 0} onClick={() => onSelectStep(steps[activeIndex - 1].id)} startIcon={<ChevronLeftRoundedIcon />} sx={{ border: `1px solid ${BORDER}`, color: BLUE, fontFamily: INTER, fontSize: 11.5, px: 1.25, py: 0.8 }}>Previous</Button>
                    <Button disabled={activeIndex >= steps.length - 1} onClick={() => onSelectStep(steps[activeIndex + 1].id)} endIcon={<ChevronRightRoundedIcon />} sx={{ border: `1px solid ${BORDER}`, color: BLUE, fontFamily: INTER, fontSize: 11.5, px: 1.25, py: 0.8 }}>Next</Button>
                </Stack>
            </Box>}
            {pictureReviewLoading && <Typography role="status" sx={{ mb: 1.5, fontFamily: INTER, color: SLATE, fontSize: 12 }}>Checking saved pictures…</Typography>}
            {pictureReviewError && <Alert severity="warning" sx={{ mb: 2 }} action={<Button size="small" onClick={() => setPictureReviewLoadVersion(value => value + 1)}>Retry</Button>}>{pictureReviewError}</Alert>}
            {pictureReview && <Box data-exercise-picture-review-for={exercise.id} sx={{ mb: 2.5 }}><ExercisePictureReviewCard review={pictureReview} checkedKeys={checkedPictureKeys} loadState={pictureLoadState}
                onCheck={(key, checked) => setCheckedPictureKeys(current => { const next = new Set(current); if (checked) next.add(key); else next.delete(key); return next; })}
                onImageLoad={(key, state) => setPictureLoadState(current => current[key] === state ? current : { ...current, [key]: state })}
                onEdit={picture => {
                    if (readinessDisabled && activeStep?.id !== picture.step_id) {
                        setSaveError('Save or cancel your current edits before opening another activity.');
                        return;
                    }
                    setSaveError(null);
                    setPictureEditOpenAttempt(0);
                    setPictureToEdit(picture);
                    onViewModeChange('edit');
                    onSelectStep(picture.step_id);
                }} /></Box>}
            {viewMode === 'edit' && <Box data-exercise-edit-scope={`exercise:${exercise.id}`}><VerbaScholaCard>
                <Stack spacing={3} sx={{ p: { xs: 2.5, md: 3.5 } }}>
                    <Box data-review-field={`exercise:${exercise.id}:purpose`}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>PEDAGOGICAL PURPOSE</Typography>
                        <TextField aria-label="Pedagogical purpose" InputProps={{ readOnly: fieldReadOnly }} value={purpose} onChange={event => { const value = event.target.value; setPurpose(value); keepInReview({ purpose: value }); }} fullWidth multiline minRows={2} sx={{ '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.5 } }} />
                    </Box>
                    <Box data-review-field={`exercise:${exercise.id}:teacher_instructions`}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>TEACHER INSTRUCTIONS</Typography>
                        <TextField aria-label="Teacher instructions" InputProps={{ readOnly: fieldReadOnly }} value={teacherInstructions} onChange={event => { const value = event.target.value; setTeacherInstructions(value); keepInReview({ teacher_instructions: value }); }} fullWidth multiline minRows={3} sx={{ '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.5 } }} />
                    </Box>
                    <Box data-review-field={`exercise:${exercise.id}:student_instructions`}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>STUDENT INSTRUCTIONS</Typography>
                        <TextField aria-label="Student instructions" InputProps={{ readOnly: fieldReadOnly }} value={studentInstructions} onChange={event => { const value = event.target.value; setStudentInstructions(value); keepInReview({ student_instructions: value }); }} fullWidth multiline minRows={2} sx={{ '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.5 } }} />
                    </Box>
                    <Box component="details" data-review-field={`exercise:${exercise.id}:settings`} open={settingsOpen}
                        onToggle={event => setSettingsOpen((event.currentTarget as HTMLDetailsElement).open)}
                        sx={{ '& summary': { cursor: 'pointer', color: SLATE, fontFamily: INTER, fontWeight: 600, fontSize: 12 } }}>
                        <Box component="summary">Exercise settings</Box>
                        <Stack spacing={1.5} sx={{ pt: 2 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <TextField data-review-field={`exercise:${exercise.id}:title`} label="Exercise title" InputProps={{ readOnly: fieldReadOnly }} value={title}
                                    error={titleMissing} helperText={titleMissing ? 'Exercise title is required' : undefined} onChange={event => { const value = event.target.value; setTitle(value); if (value.trim()) keepInReview({ title: value }); }} fullWidth />
                                <TextField data-review-field={`exercise:${exercise.id}:duration_minutes`} label="Minutes" InputProps={{ readOnly: fieldReadOnly }} value={durationMinutes} onChange={event => { const value = event.target.value.replace(/[^\d]/g, '').slice(0, 3); setDurationMinutes(value); keepInReview({ duration_minutes: optionalNumber(value) }); }} sx={{ width: { xs: '100%', sm: 130 } }} />
                            </Stack>
                            <TextField data-review-field={`exercise:${exercise.id}:subtitle`} label="Subtitle" InputProps={{ readOnly: fieldReadOnly }} value={subtitle} onChange={event => { const value = event.target.value; setSubtitle(value); keepInReview({ subtitle: value }); }} fullWidth />
                            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>Saved minutes are a planning estimate; students are not timed. Activity content suggests about {exercise.content_estimated_minutes ?? 0} minutes.</Typography>
                            {hasTimingGap(optionalNumber(durationMinutes), exercise.content_estimated_minutes) && <Typography sx={{ fontFamily: INTER, color: WARNING_TEXT, fontSize: 12 }}>
                                Timing check: {durationMinutes} minutes is far from the activity content estimate.
                                {' '}Update it if the content will take about {exercise.content_estimated_minutes} minutes.</Typography>}
                            {!readOnly && practiceTargetEditor}
                        </Stack>
                    </Box>
                    <Divider />
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip label={titleCase(exercise.status)} size="small" sx={exerciseStatusChipSx(exercise.status)} />
                        {readOnly && reviewEdit && <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>Changes to these fields go into Your version as you type.</Typography>}
                        {!readOnly && <>{exercise.status === 'draft' && <Button disabled={saving || readinessDisabled} onClick={() => void onSaveExercise(exercise.id, { status: 'review' }).catch(() => undefined)}>Mark for review</Button>}
                        {exercise.status !== 'draft' && <Button disabled={saving || readinessDisabled} onClick={() => void onSaveExercise(exercise.id, { status: 'draft' }).catch(() => undefined)}>Return to draft</Button>}
                        {(exercise.status !== 'ready' || (picturesNeedConfirmation && pictureReview?.reviewed === false)) && <Tooltip title={readyReviewBlocked && picturesNeedConfirmation ? 'Inspect each picture, confirm it is correct and useful, and replace any picture that is missing or cannot load.' : ''}>
                            <span><Button startIcon={<CheckCircleRoundedIcon />} disabled={saving || readinessDisabled || steps.length === 0 || readyReviewBlocked} onClick={markExerciseReady}>
                                {exercise.status === 'ready' ? 'Confirm pictures' : 'Mark ready'}
                            </Button></span>
                        </Tooltip>}
                        {onReviseExercise && <Button disabled={saving || readinessDisabled} onClick={() => onReviseExercise(exercise.id)}>Revise exercise with AI</Button>}
                        <Button disabled={saving || title.trim().length === 0}
                            onClick={() => { setSaveError(null); void onSaveExercise(exercise.id, {
                                title: title.trim(), subtitle: optionalText(subtitle), duration_minutes: optionalNumber(durationMinutes),
                                purpose: optionalText(purpose), teacher_instructions: optionalText(teacherInstructions),
                                student_instructions: optionalText(studentInstructions),
                            }, true).catch(caught => setSaveError(lessonAuthoringError(caught, 'Could not save this exercise. Check its fields and try again.'))); }}
                            sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Save exercise</Button>
                        <Button disabled={saving} onClick={cancelExerciseChanges}>Cancel changes</Button>
                        <VerbaScholaConfirmDeleteButton label="Delete exercise" disabled={saving} onConfirm={() => onDeleteExercise(exercise.id)} sx={{ ml: { sm: 'auto !important' } }} /></>}
                    </Stack>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                </Stack>
            </VerbaScholaCard></Box>}
            <Stack spacing={1.5} sx={{ mt: 2 }}>
                {steps.length === 0 && <VerbaScholaEmptyState message="This exercise has no steps yet." />}
                {viewMode === 'preview' && !readOnly && <Alert severity="info">Preview uses the student player and saved activity content, including drafts. Your practice stays in this preview: no student attempt, score, recording, or lesson destination is saved. Pronunciation scoring and open-ended writing feedback are unavailable; typed answers are checked against saved examples. Next-lesson choices show every authored option, including drafts; students see only destinations available to them.</Alert>}
                {activeStep && <Box key={activeStep.id} data-exercise-step-id={activeStep.id} tabIndex={-1}
                    sx={{ scrollMarginTop: 90, outline: 'none', '&[data-exercise-highlight="true"]': { outline: `3px solid ${BLUE}`, outlineOffset: 4, borderRadius: '16px', bgcolor: SOFT_BLUE },
                        '& [data-exercise-review-text-highlight="true"]': { outline: `3px solid ${BLUE}`, outlineOffset: 3, borderRadius: '6px', backgroundColor: SOFT_BLUE } }}>
                    {reviewTextMissing && reviewTextFocus?.stepId === activeStep.id && <Alert severity="info" sx={{ mb: 1 }}>The quoted text could not be located in this activity editor. Check the saved content here; the finding may refer to an earlier edit.</Alert>}
                    {viewMode === 'preview'
                        ? <VerbaScholaTeacherStudentPreview key={activeStep.id} stepId={activeStep.id} exerciseTitle={exercise.title} studentInstructions={exercise.student_instructions ?? null}
                            projectedStep={studentSteps?.[String(activeStep.id)]}
                            onPrevious={activeIndex > 0 ? () => onSelectStep(steps[activeIndex - 1].id) : undefined}
                            onNext={activeIndex < steps.length - 1 ? () => onSelectStep(steps[activeIndex + 1].id) : undefined} />
                        : readOnly ? (reviewEdit && renderReviewStep?.(activeStep)) || <ReviewLockedStep>{renderStep?.(activeStep) ?? <StepEditorRow step={activeStep} index={activeIndex} saving onSave={onUpdateStep} onDelete={onDeleteStep}
                            choices={choices.filter(row => row.verbaschola_lesson_exercise_step_id === activeStep.id)}
                            sentences={sentences.filter(row => row.verbaschola_lesson_exercise_step_id === activeStep.id || conversations.some(group => group.verbaschola_lesson_exercise_step_id === activeStep.id && group.id === row.verbaschola_lesson_exercise_step_conversation_id))}
                            conversations={conversations.filter(row => row.verbaschola_lesson_exercise_step_id === activeStep.id)}
                            onChildMutation={onChildMutation} authoringRevision={authoringRevision} courseId={courseId} lessonId={lessonId} />}</ReviewLockedStep>
                        : renderStep?.(activeStep) ?? <StepEditorRow step={activeStep} index={activeIndex} saving={saving} onSave={onUpdateStep} onDelete={onDeleteStep}
                            choices={choices.filter(row => row.verbaschola_lesson_exercise_step_id === activeStep.id)}
                            sentences={sentences.filter(row => row.verbaschola_lesson_exercise_step_id === activeStep.id || conversations.some(group => group.verbaschola_lesson_exercise_step_id === activeStep.id && group.id === row.verbaschola_lesson_exercise_step_conversation_id))}
                            conversations={conversations.filter(row => row.verbaschola_lesson_exercise_step_id === activeStep.id)}
                            onChildMutation={onChildMutation} onMarkDirty={onMarkDirty} onCancelScope={onCancelScope} authoringRevision={authoringRevision} courseId={courseId} lessonId={lessonId} />}
                </Box>}
                {viewMode === 'edit' && !readOnly && <Box component="details" data-exercise-edit-scope="step:add" sx={{ p: 2, border: `1px solid ${BORDER}`, borderRadius: '16px', backgroundColor: '#fff', '& summary': { cursor: 'pointer', color: BLUE, fontFamily: INTER, fontWeight: 600, fontSize: 13 } }}>
                    <Box component="summary">Add step manually</Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 2 }}>
                        <TextField label="New step title" value={newStepTitle} onChange={event => setNewStepTitle(event.target.value)} fullWidth />
                        <Select inputProps={{ 'aria-label': 'Activity type' }} value={newStepType} onChange={event => { onMarkDirty?.('step:add'); setNewStepType(event.target.value); }} sx={{ minWidth: { sm: 220 } }}>
                            {stepClassNameOptions.map(option => <MenuItem key={option} value={option}>{stepClassNameLabel(option)}</MenuItem>)}
                        </Select>
                        <Button variant="outlined" startIcon={<AddRoundedIcon />}
                            disabled={saving || newStepTitle.trim().length === 0}
                            onClick={() => { setAddStepError(null); void onAddStep({ title: newStepTitle.trim(), class_name: newStepType, is_note_to_student: false, is_hidden: false, order_index: Math.max(0, ...steps.map(row => row.order_index ?? row.id)) + 1 })
                                .then(() => setNewStepTitle('')).catch(caught => setAddStepError(lessonAuthoringError(caught, 'Could not add this activity. Try again.'))); }}
                            sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap' }}>
                            Add step
                        </Button>
                    </Stack>
                    {addStepError && <Alert severity="error" sx={{ mt: 1 }}>{addStepError}</Alert>}
                </Box>}
            </Stack>
        </>
    );
}

export function VerbaScholaStepSettings({ step, saving, onSave, onDelete, onCancel }: {
    step: IVerbaScholaLessonExerciseStep; saving: boolean;
    onSave: (stepId: number, payload: Partial<IVerbaScholaLessonExerciseStep>, scope?: string) => Promise<void>;
    onDelete: (stepId: number) => Promise<void>;
    onCancel?: () => void;
}) {
    const [newWordInfo, setNewWordInfo] = useSyncedState(step.new_word_info_text ?? '');
    const [displayPinyin, setDisplayPinyin] = useSyncedState(Boolean(step.display_pinyin));
    const [isHidden, setIsHidden] = useSyncedState(Boolean(step.is_hidden));
    const [correctReply, setCorrectReply] = useSyncedState(step.spoken_yes_response ?? '');
    const [tryAgainReply, setTryAgainReply] = useSyncedState(step.spoken_no_response ?? '');
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const saveSettingsLabel = useReviewSaveLabel('Save settings');
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const speakingFeedback = step.class_name === 'speak_with_grade' || step.class_name === 'type_speak';
    const showMoreInfo = speakingFeedback || step.class_name === 'conversation_listen';
    return <Box component="details" data-exercise-edit-scope={`step:${step.id}`}
        sx={{ p: 2, border: `1px solid ${BORDER}`, borderRadius: '16px', backgroundColor: '#fff', '& summary': { cursor: 'pointer', fontFamily: INTER, fontWeight: 700, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', color: SLATE } }}>
        <Box component="summary">Activity settings</Box>
        <Stack spacing={1.5} sx={{ pt: 2 }}>
            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>{reviewEdit
                ? 'In Your version, change this activity’s answers or lines in its editor above. Adding, removing or reordering them is available once the review is saved or closed.'
                : 'Use “Add, remove or reorder” to edit saved answers or lines.'}</Typography>
            {showMoreInfo && <TextField label="More info" value={newWordInfo} onChange={event => setNewWordInfo(event.target.value)} fullWidth />}
            {speakingFeedback && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField label="After a correct answer" value={correctReply} onChange={event => setCorrectReply(event.target.value)} fullWidth />
                <TextField label="After an incorrect answer" value={tryAgainReply} onChange={event => setTryAgainReply(event.target.value)} fullWidth />
            </Stack>}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={displayPinyin ? 'Pinyin visible' : 'Pinyin hidden'} onClick={() => setDisplayPinyin(value => !value)}
                    sx={{ fontFamily: INTER, color: displayPinyin ? BLUE_TEXT : SLATE, backgroundColor: displayPinyin ? SOFT_BLUE : SURFACE_MUTED }} />
                <Chip label={isHidden ? 'Hidden from students' : 'Visible to students'} onClick={() => setIsHidden(value => !value)}
                    sx={{ fontFamily: INTER, color: isHidden ? WARNING_TEXT : SUCCESS_TEXT, backgroundColor: isHidden ? WARNING_SOFT : SUCCESS_SOFT }} />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button disabled={saving} onClick={() => { setSaveError(null); void onSave(step.id, {
                    ...(showMoreInfo ? { new_word_info_text: optionalText(newWordInfo) } : {}),
                    display_pinyin: displayPinyin, is_hidden: isHidden,
                    ...(speakingFeedback ? { spoken_yes_response: optionalText(correctReply), spoken_no_response: optionalText(tryAgainReply) } : {}),
                }, `step:${step.id}`).catch(caught => setSaveError(lessonAuthoringError(caught, 'Could not save these settings. Check the fields and try again.'))); }}>{saveSettingsLabel}</Button>
                <Button disabled={saving} onClick={() => {
                    setNewWordInfo(step.new_word_info_text ?? ''); setDisplayPinyin(Boolean(step.display_pinyin));
                    setIsHidden(Boolean(step.is_hidden)); setCorrectReply(step.spoken_yes_response ?? '');
                    setTryAgainReply(step.spoken_no_response ?? ''); setSaveError(null); onCancel?.();
                }}>Cancel changes</Button>
                {!reviewEdit && <VerbaScholaConfirmDeleteButton label="Delete activity" disabled={saving} onConfirm={() => onDelete(step.id)} />}
            </Stack>
            {saveError && <Alert severity="error">{saveError}</Alert>}
        </Stack>
    </Box>;
}

function criteriaLines(content: IVerbaScholaLessonExerciseStep['open_response_content_json'], role: 'required' | 'suggested' | 'example'): string {
    return (content?.criteria ?? []).filter(item => item.role === role).map(item => item.text).join('\n');
}

// Its own edit scope (open:<step id>), so saving these settings and saving the step's other
// settings never discard each other's unsaved edits.
function OpenResponseAuthoringFields({ step, saving, onSave, onMarkDirty }: { step: IVerbaScholaLessonExerciseStep; saving: boolean;
    onSave: (stepId: number, payload: Partial<IVerbaScholaLessonExerciseStep>, scope?: string) => Promise<void>;
    onMarkDirty?: (scope: string) => void }) {
    const saveOpenLabel = useReviewSaveLabel('Save open answer settings');
    const content = step.open_response_content_json;
    const scope = `open:${step.id}`;
    // Each field follows the saved settings only where they changed: saving another part of the step
    // refreshes the step with a new settings object, which must not discard these unsaved edits.
    const [mode, setMode] = useSyncedState<'live' | 'deferred'>(content?.feedback_mode ?? 'deferred');
    const [guidance, setGuidance] = useSyncedState(content?.guidance ?? '');
    const [hint, setHint] = useSyncedState(content?.speaking_hint ?? '');
    const [required, setRequired] = useSyncedState(criteriaLines(content, 'required'));
    const [suggested, setSuggested] = useSyncedState(criteriaLines(content, 'suggested'));
    const [examples, setExamples] = useSyncedState(criteriaLines(content, 'example'));
    const [error, setError] = React.useState<string | null>(null);
    const criteria = ([['required', required], ['suggested', suggested], ['example', examples]] as const)
        .flatMap(([role, value]) => value.split(/\r?\n/).map(text => text.trim()).filter(Boolean).map(text => ({ role, text })));
    return <Stack spacing={1.5} data-exercise-edit-scope={scope} sx={{ p: 2, bgcolor: SURFACE_MUTED, borderRadius: 2 }}>
        <Typography sx={{ fontFamily: POPPINS, fontWeight: 700, color: NAVY }}>Open answer settings</Typography>
        {/* The menu renders outside this scope, so a choice marks the settings unsaved itself. */}
        <Select value={mode} onChange={event => { setMode(event.target.value as 'live' | 'deferred'); onMarkDirty?.(scope); }} inputProps={{ 'aria-label': 'Feedback timing' }}>
            <MenuItem value="deferred">Submit and continue; teacher feedback later</MenuItem>
            <MenuItem value="live">Get AI feedback and revise until finished</MenuItem>
        </Select>
        <TextField label="Student guidance" value={guidance} onChange={event => setGuidance(event.target.value)} fullWidth multiline minRows={2} />
        {step.class_name !== 'write_open' && <TextField label="Speaking hint" value={hint} onChange={event => setHint(event.target.value)} fullWidth />}
        <TextField label="Required points (one per line)" value={required} onChange={event => setRequired(event.target.value)} fullWidth multiline minRows={2} />
        <TextField label="Suggestions (one per line)" value={suggested} onChange={event => setSuggested(event.target.value)} fullWidth multiline minRows={2} />
        <TextField label="Examples only (one per line)" value={examples} onChange={event => setExamples(event.target.value)} fullWidth multiline minRows={2} />
        <Button disabled={saving} onClick={() => {
            setError(null);
            void onSave(step.id, { open_response_content_json: { feedback_mode: mode, guidance, speaking_hint: hint, criteria } }, scope)
                .catch(caught => {
                    // Validation messages ("Use at most 30 open-response criteria") are written for teachers.
                    const message = axios.isAxiosError(caught) && caught.response?.status === 400 ? caught.response.data?.message : null;
                    setError(typeof message === 'string' && message.includes(' ') ? message
                        : lessonAuthoringError(caught, 'Could not save the open answer settings.'));
                });
        }}>{saveOpenLabel}</Button>
        {error && <Alert severity="error">{error}</Alert>}
    </Stack>;
}

export function StepEditorRow({ step, index, saving, onSave, onDelete, choices, sentences, conversations, onChildMutation, onMarkDirty, onCancelScope,
    authoringRevision, courseId, lessonId }: { step: IVerbaScholaLessonExerciseStep; index: number; saving: boolean;
    onSave: (stepId: number, payload: Partial<IVerbaScholaLessonExerciseStep>, scope?: string) => Promise<void>;
    onDelete: (stepId: number) => Promise<void>;
    choices: IVerbaScholaExerciseChoice[]; sentences: IVerbaScholaSpeakingSentence[]; conversations: IVerbaScholaExerciseConversation[];
    onChildMutation: (operation: () => Promise<unknown>, scope: string) => Promise<void>; onMarkDirty?: (scope: string) => void; onCancelScope?: (scope: string) => void;
    authoringRevision: number; courseId: number; lessonId: number }) {
    const [expanded, setExpanded] = React.useState(true);
    // Each field follows the saved row only where it changed, so a refresh keeps text typed in the others.
    const [title, setTitle] = useSyncedState(step.title);
    const [className, setClassName] = useSyncedState(step.class_name);
    const [promptText, setPromptText] = useSyncedState(step.prompt_text ?? '');
    const [promptPinyin, setPromptPinyin] = useSyncedState(editablePinyin(step.prompt_pinyin));
    const [imageSrcUrl, setImageSrcUrl] = useSyncedState(step.image_src_url ?? '');
    const [imageAlt, setImageAlt] = useSyncedState(step.instructional_content_json?.image_alt ?? '');
    const [newWord, setNewWord] = useSyncedState(step.new_word ?? '');
    const [newWordInfoText, setNewWordInfoText] = useSyncedState(step.new_word_info_text ?? '');
    const [displayPinyin, setDisplayPinyin] = useSyncedState(Boolean(step.display_pinyin));
    const [isHidden, setIsHidden] = useSyncedState(Boolean(step.is_hidden));
    const [correctReply, setCorrectReply] = useSyncedState(step.spoken_yes_response ?? '');
    const [tryAgainReply, setTryAgainReply] = useSyncedState(step.spoken_no_response ?? '');
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [pictureReset, setPictureReset] = React.useState(0);
    // Your version under review: the activity's content is kept in the review draft; its type and existence stay.
    const reviewEdit = React.useContext(LessonReviewEditContext);
    const saveStepLabel = useReviewSaveLabel('Save step');
    const typeHasChildren = className !== step.class_name && (choices.length > 0 || sentences.length > 0 || conversations.length > 0);
    const instructional = className === 'image' || className === 'introduce_word';
    const speaking = ['conversation_listen', 'conversation_listen_without_character', 'conversation_listen_speak',
        'conversation_read_speak', 'conversation_read_with_blank_speak', 'conversation_speak_only', 'listen_speak',
        'listen_speak_no_grade', 'speak_with_grade', 'speak_no_grade', 'type_speak', 'type_speak_v2',
        'image_select_speak', 'quiz_question_speak'].includes(className);

    const cancelStepChanges = () => {
        setTitle(step.title); setClassName(step.class_name); setPromptText(step.prompt_text ?? '');
        setPromptPinyin(editablePinyin(step.prompt_pinyin)); setImageSrcUrl(step.image_src_url ?? '');
        setImageAlt(step.instructional_content_json?.image_alt ?? '');
        setNewWord(step.new_word ?? ''); setNewWordInfoText(step.new_word_info_text ?? '');
        setDisplayPinyin(Boolean(step.display_pinyin)); setIsHidden(Boolean(step.is_hidden));
        setCorrectReply(step.spoken_yes_response ?? ''); setTryAgainReply(step.spoken_no_response ?? '');
        setPictureReset(value => value + 1);
        setSaveError(null); onCancelScope?.(`step:${step.id}`);
    };

    return (
        <Box data-exercise-edit-scope={`step:${step.id}`} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', backgroundColor: '#fff', overflow: 'hidden' }}>
            <Box component="button" type="button" aria-expanded={expanded} aria-label={`${step.title} details`} onClick={() => setExpanded(open => !open)} sx={{ width: '100%', border: 0, textAlign: 'left', backgroundColor: 'transparent', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', '&:focus-visible': { outline: `3px solid ${BLUE}`, outlineOffset: -3 } }}>
                <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: '12px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}><Typography sx={{ fontFamily: POPPINS, fontWeight: 700, color: BLUE_TEXT, fontSize: 12 }}>{index + 1}</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE, fontSize: 11, letterSpacing: '0.06em' }}>STEP {index + 1}</Typography>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 14, mt: 0.25 }}>{step.title}</Typography>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5 }}>{stepClassNameLabel(step.class_name)}</Typography>
                </Box>
                {step.is_hidden && <Chip label="Hidden" size="small" sx={{ fontFamily: INTER, color: SLATE, backgroundColor: SURFACE_MUTED }} />}
                <ExpandMoreRoundedIcon sx={{ color: SLATE, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
            </Box>
            <Collapse in={expanded}>
                <Stack spacing={1.5} sx={{ p: 2, pt: 0 }}>
                    <Divider />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField label="Step title" value={title} onChange={event => setTitle(event.target.value)} fullWidth />
                        <Select inputProps={{ 'aria-label': 'Activity type' }} disabled={reviewEdit} value={className} onChange={(event: SelectChangeEvent<string>) => { onMarkDirty?.(`step:${step.id}`); setClassName(event.target.value); }} sx={{ width: { xs: '100%', sm: 230 } }}>
                            {!studentAuthorableStepTypeSet.has(className) && <MenuItem value={className} disabled>{stepClassNameLabel(className)} (legacy only)</MenuItem>}
                            {stepClassNameOptions.map(option => <MenuItem key={option} value={option}>{stepClassNameLabel(option)}</MenuItem>)}
                        </Select>
                    </Stack>
                    <TextField label="Prompt text" value={promptText} onChange={event => setPromptText(event.target.value)} fullWidth multiline minRows={3} />
                    <TextField label="Prompt pinyin (space-separated)" value={promptPinyin} onChange={event => setPromptPinyin(event.target.value)} fullWidth />
                    {instructional && <VerbaScholaExerciseImageField key={`${step.id}:${pictureReset}`} lessonId={lessonId} label="Activity picture"
                        value={imageSrcUrl} altText={imageAlt}
                        concept={newWord || promptText || title}
                        meaning={step.instructional_content_json?.english || newWordInfoText || ''}
                        lessonContext={[title, promptText].filter(Boolean).join('. ')}
                        studentTask={promptText || title}
                        onChange={(url, alt) => { onMarkDirty?.(`step:${step.id}`); setImageSrcUrl(url); setImageAlt(alt); }} />}
                    {instructional && !isSafePictureUrl(imageSrcUrl) && <Alert severity="warning">Use an https:// or http:// picture URL, or a path beginning with /.</Alert>}
                    {instructional && !hasPictureDescription(imageSrcUrl, imageAlt) && <Alert severity="warning">{reviewEdit ? 'Describe the picture before keeping it in your version.' : 'Describe the picture before saving.'}</Alert>}
                    {className === 'introduce_word' && <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE }}>New word: {newWord || 'Add a primary recording below'}. {reviewEdit
                        ? 'Edit its Chinese sentence and pinyin below to change the word; saving your version creates fresh audio.'
                        : 'Edit its Chinese sentence and pinyin below to change the word and create fresh audio.'}</Typography>}
                    {(instructional || speaking) && <TextField label="More info" value={newWordInfoText} onChange={event => setNewWordInfoText(event.target.value)} fullWidth />}
                    {speaking && <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontFamily: INTER, fontWeight: 600, color: SLATE, fontSize: 12 } }}>
                        <Box component="summary">Spoken feedback</Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1.5 }}>
                            <TextField label="After a correct answer" value={correctReply} onChange={event => setCorrectReply(event.target.value)} fullWidth />
                            <TextField label="After an incorrect answer" value={tryAgainReply} onChange={event => setTryAgainReply(event.target.value)} fullWidth />
                        </Stack>
                    </Box>}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={displayPinyin ? 'Pinyin visible' : 'Pinyin hidden'} onClick={() => setDisplayPinyin(value => !value)} sx={{ fontFamily: INTER, color: displayPinyin ? BLUE_TEXT : SLATE, backgroundColor: displayPinyin ? SOFT_BLUE : SURFACE_MUTED }} />
                        <Chip label={isHidden ? 'Hidden from students' : 'Visible to students'} onClick={() => setIsHidden(value => !value)} sx={{ fontFamily: INTER, color: isHidden ? WARNING_TEXT : SUCCESS_TEXT, backgroundColor: isHidden ? WARNING_SOFT : SUCCESS_SOFT }} />
                    </Stack>
                    {typeHasChildren && <Alert severity="warning">Remove this activity’s answers or script lines before changing its type.</Alert>}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            disabled={saving || title.trim().length === 0 || typeHasChildren || instructional && (!isSafePictureUrl(imageSrcUrl) || !hasPictureDescription(imageSrcUrl, imageAlt))}
                            onClick={() => { setSaveError(null); void onSave(step.id,
                                {
                                title: title.trim(),
                                class_name: className,
                                prompt_text: optionalText(promptText),
                                prompt_pinyin: promptPinyin.trim().split(/\s+/).filter(Boolean),
                                ...(instructional ? { image_src_url: optionalText(imageSrcUrl),
                                    instructional_content_json: { ...step.instructional_content_json, image_alt: imageAlt.trim() } } : {}),
                                ...(instructional || speaking ? { new_word_info_text: optionalText(newWordInfoText) } : {}),
                                display_pinyin: displayPinyin,
                                ...(speaking ? { spoken_yes_response: optionalText(correctReply), spoken_no_response: optionalText(tryAgainReply) } : {}),
                                is_hidden: isHidden
                            }, `step:${step.id}`).catch(caught => setSaveError(lessonAuthoringError(caught, 'Could not save this activity. Check its fields and try again.'))); }}
                            sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>{saveStepLabel}</Button>
                        <Button disabled={saving} onClick={cancelStepChanges}>Cancel changes</Button>
                        {!reviewEdit && <VerbaScholaConfirmDeleteButton label="Delete activity" disabled={saving} onConfirm={() => onDelete(step.id)} sx={{ ml: 'auto !important' }} />}
                    </Stack>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    {['write_open', 'speak_open', 'type_speak_open'].includes(step.class_name) && <OpenResponseAuthoringFields key={step.id} step={step} saving={saving} onSave={onSave} onMarkDirty={onMarkDirty} />}
                    <Box data-exercise-edit-scope={`native:${step.id}`}><VerbaScholaNativeStepFields step={step} choices={choices} sentences={sentences} conversations={conversations}
                        saving={saving} authoringRevision={authoringRevision}
                        onSaveStep={(id, payload) => onSave(id, payload, `native:${id}`)} onChildMutation={onChildMutation}
                        courseId={courseId} lessonId={lessonId} onDirty={() => onMarkDirty?.(`native:${step.id}`)} onChildDirty={onMarkDirty}
                        onCancel={() => onCancelScope?.(`native:${step.id}`)} /></Box>
                </Stack>
            </Collapse>
        </Box>
    );
}
